/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Product.css";
import ReusableModal from "../../ManageSettings/ReusableModal";
import ProductController from "./ProductController";
import ManageStockController from "../ManageStock/ManageStockController";
import productIcon from "../../../../../assets/Image/add-product (black).png";
import segmentIcon from "../../../../../assets/Image/segment.png";
import scanner from "../../../../../assets/Image/scan.png";
import stockIcon from "../../../../../assets/Image/add-stock (black).png";
import HistoryIcon from "../../../../../assets/Image/stock-history (light).png";
import { ToastContainer, toast } from "react-toastify";
import AddVendorModal from "../../ReusableForm/AddVendorModal";
import UomController from "../Uom/UomController";
import UomView from "../Uom/UomView";
import AddUomIcon from "../../../../../assets/Image/Adduom.png";
import ImportIcon from "../../../../../assets/Image/importicon (2).png";
import {
  TextField,
  FormControl,
  Autocomplete,
  MenuItem,
} from "../../../../common/ReusableComponent/MUIComponents";
import {
  CustomButton,
  RegularButton,
} from "../../../../common/ReusableComponent/CustomButton";
import { ReusableInput } from "../../../../common/ReusableComponent/ReusableInput";
import PaginatedSearchSelect from "../../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import useApi from "../../../../../utils/api-manager/Helper/useApi";
import useFormattingUtils from "../../../../../context/useFormattingUtils";
import AuthContext from "../../../../../utils/secure-route/AuthContext";
import PaginatedMultiSelect from "../../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedMultiSelect";
import { Popover } from "bootstrap";
import {
  Search,
  Package,
  Boxes,
  ChevronDown,
  Eye,
  Pencil,
  PlusCircle,
  Layers,
  Wallet,
  TrendingUp,
  AlertTriangle,
  Image as ImageIcon,
  History,
  Check,
  X,
} from "lucide-react";

const invMergeSafeNumber = (value) => {
  if (value === "-" || value === null || value === undefined || value === "") return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const invMergeParseFirstNumber = (value) => {
  if (value === "-" || value === null || value === undefined || value === "") return 0;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const direct = Number(value);
  if (Number.isFinite(direct)) return direct;

  const matched = String(value).match(/-?\d+(?:\.\d+)?/);
  return matched ? invMergeSafeNumber(matched[0]) : 0;
};

const invMergeFormatCount = (value) =>
  invMergeSafeNumber(value).toLocaleString("en-IN");

const invMergeFormatMoney = (value) =>
  invMergeSafeNumber(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const invMergeFormatDateTime = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date
    .toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\bam\b/i, "AM")
    .replace(/\bpm\b/i, "PM");
};

const invMergePickValue = (item, keys = []) => {
  if (!item || typeof item !== "object") return "";

  for (const key of keys) {
    const value = key.split(".").reduce((current, part) => {
      if (current === undefined || current === null) return undefined;
      return current[part];
    }, item);

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
};

const invMergeHasDisplayValue = (value) => {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

const invMergeDisplayValue = (value) => {
  if (!invMergeHasDisplayValue(value)) return "";

  if (Array.isArray(value)) {
    return value.filter(Boolean).join(", ");
  }

  if (typeof value === "object") {
    return value.name || value.title || value.label || value.value || "";
  }

  return value;
};

const invMergeGetHistorySegments = (item) =>
  Array.isArray(item?.segment_inventory) ? item.segment_inventory : [];

const invMergeGetFirstHistorySegment = (item) =>
  invMergeGetHistorySegments(item)[0] || {};

const invMergeGetHistorySegmentTotalPieces = (segment) => {
  const totalUomArrived = invMergeSafeNumber(segment?.total_uom_arrived);
  const pcInUom = invMergeSafeNumber(segment?.segment_total_pc_in_uom || 1);
  return totalUomArrived * (pcInUom || 1);
};

const invMergeGetHistoryTitle = (item) =>
  item?.product?.title ||
  item?.product?.product_name ||
  item?.product_title ||
  item?.product_name ||
  item?.title ||
  item?.combinedField ||
  item?.product_name_title ||
  (typeof item?.product === "string" ? item.product : "") ||
  "Untitled Product";

const invMergeGetHistoryVendor = (item) =>
  invMergeDisplayValue(
    invMergePickValue(item, [
      "vendor_name",
      "vendor.name",
      "vendorName",
      "vendor",
      "supplier_name",
      "supplier.name",
    ]),
  ) || "No Vendor";

const invMergeGetHistoryCategory = (item) => {
  const categoryList = invMergeGetCategories(item);
  return categoryList.length ? categoryList.join(", ") : "-";
};

const invMergeGetHistoryUom = (item) => {
  const firstSegment = invMergeGetFirstHistorySegment(item);

  return (
    invMergeDisplayValue(
      invMergePickValue(item, [
        "segment_inventory.0.segment_uom",
        "uom_title",
        "uom.name",
        "uom.title",
        "uom",
        "UOM",
        "unit",
        "unit_name",
        "segment_uom",
        "segment.uom_title",
      ]),
    ) || firstSegment?.segment_uom || "-"
  );
};

const invMergeGetHistoryPiecesInUom = (item) =>
  invMergeDisplayValue(
    invMergePickValue(item, [
      "segment_inventory.0.segment_total_pc_in_uom",
      "total_pc_in_uom",
      "total_pieces_in_uom",
      "pieces_in_uom",
      "pcs_in_uom",
      "piece_in_uom",
      "pc_in_uom",
      "segment.total_pc_in_uom",
    ]),
  );

const invMergeGetHistoryStockAdded = (item) =>
  invMergeDisplayValue(
    invMergePickValue(item, [
      "segment_inventory.0.total_uom_arrived",
      "total_uom_arrived",
      "arrayField",
      "added_stock",
      "stock_added",
      "addition_in_stock",
      "addition_stock",
      "quantity_added",
      "qty_added",
      "new_stock",
      "quantity",
      "qty",
    ]),
  );

const invMergeGetHistoryTotalPieces = (item) => {
  const segmentTotal = invMergeGetHistorySegments(item).reduce(
    (sum, segment) => sum + invMergeGetHistorySegmentTotalPieces(segment),
    0,
  );

  if (segmentTotal > 0) {
    return segmentTotal;
  }

  return invMergeDisplayValue(
    invMergePickValue(item, [
      "total_stock_arrived",
      "total_piece_added",
      "total_pieces_added",
      "total_pc_added",
      "total_piece_arrived",
      "stock_arrived",
      "pieces_arrived",
    ]),
  );
};

const invMergeGetHistoryCurrentStock = (item) =>
  invMergeDisplayValue(
    invMergePickValue(item, [
      "current_stock",
      "current_inventory",
      "remaining_inventory",
      "total_current_stock",
      "available_stock",
      "balance_stock",
    ]),
  );

const invMergeGetHistoryPurchase = (item) =>
  invMergePickValue(item, [
    "segment_inventory.0.purchase_price_per_pc",
    "segment_inventory.0.purchase_price",
    "purchaseprice",
    "purchase_price",
    "purchasePrice",
    "cost_price",
    "buying_price",
    "segment.purchase_price",
  ]);

const invMergeGetHistorySelling = (item) =>
  invMergePickValue(item, [
    "segment_inventory.0.selling_price_per_pc",
    "segment_inventory.0.selling_price",
    "sellingprice",
    "selling_price",
    "sellingPrice",
    "sale_price",
    "retail_price",
    "segment.selling_price",
  ]);

const invMergeGetHistoryQty = (item) =>
  invMergeSafeNumber(
    invMergeGetHistoryTotalPieces(item) ||
      invMergeGetHistoryStockAdded(item) ||
      item?.current_stock ||
      0,
  );

const invMergeFormatPriceRange = (values = [], money = true) => {
  const filteredValues = values
    .map((value) => invMergeSafeNumber(value))
    .filter((value) => value > 0);

  if (!filteredValues.length) return money ? "₹0.00" : "0";

  const min = Math.min(...filteredValues);
  const max = Math.max(...filteredValues);
  const formatValue = (value) => (money ? `₹${invMergeFormatMoney(value)}` : invMergeFormatCount(value));

  return min === max ? formatValue(min) : `${formatValue(min)} - ${formatValue(max)}`;
};

const invMergeFormatStockAdded = (lot) => {
  const uomAdded = invMergeSafeNumber(lot?.uomAdded);
  const uom = lot?.uom || "UOM";

  if (uomAdded <= 0) return "-";
  return `${invMergeFormatCount(uomAdded)} ${uom}`;
};

const invMergeFormatStockFormula = (lot) => {
  const uomAdded = invMergeSafeNumber(lot?.uomAdded);
  const pcInUom = invMergeSafeNumber(lot?.pcInUom || 1) || 1;
  const totalPieces = invMergeSafeNumber(lot?.totalPieces);
  const uom = lot?.uom || "UOM";

  if (uomAdded <= 0) return `${invMergeFormatCount(totalPieces)} pcs`;
  return `${invMergeFormatCount(uomAdded)} ${uom} × ${invMergeFormatCount(pcInUom)} pc = ${invMergeFormatCount(totalPieces)} pcs`;
};

const invMergeGetLotSource = (lot) => lot?.rawEntry || lot || {};

const invMergeGetLotProductDisplay = (lot, fallbackTitle = "") => {
  const source = invMergeGetLotSource(lot);
  return (
    source?.combinedField ||
    source?.product_display ||
    source?.productTitleDisplay ||
    lot?.combinedField ||
    `${lot?.productTitle || fallbackTitle || "Product"}${lot?.uom ? ` (${lot.uom}, ${invMergeFormatCount(lot.pcInUom || 1)} pcs)` : ""}`
  );
};

const invMergeGetLotAddedStockDisplay = (lot) => {
  const source = invMergeGetLotSource(lot);

  if (source?.arrayField) return source.arrayField;
  if (source?.added_stock_display) return source.added_stock_display;

  return invMergeFormatStockAdded(lot);
};

const invMergeGetLotTotalPieces = (lot) => {
  const source = invMergeGetLotSource(lot);
  return invMergeParseFirstNumber(
    source?.total_stock_arrived ??
      source?.total_piece_added ??
      source?.total_pieces_arrived ??
      lot?.totalPieces,
  );
};

const invMergeGetLotCurrentStock = (lot) => {
  const source = invMergeGetLotSource(lot);
  return invMergeParseFirstNumber(
    source?.current_stock ??
      source?.current_inventory ??
      source?.remaining_inventory ??
      lot?.currentStock,
  );
};

const invMergeGetLotPurchasePrice = (lot) => {
  const source = invMergeGetLotSource(lot);
  const segment = lot?.rawSegment || {};

  return invMergeParseFirstNumber(
    source?.purchaseprice ??
      source?.purchase_price ??
      source?.purchasePrice ??
      segment?.purchase_price_per_pc ??
      lot?.purchasePerPc,
  );
};

const invMergeGetLotSellingPrice = (lot) => {
  const source = invMergeGetLotSource(lot);
  const segment = lot?.rawSegment || {};

  return invMergeParseFirstNumber(
    source?.sellingprice ??
      source?.selling_price ??
      source?.sellingPrice ??
      segment?.selling_price_per_pc ??
      lot?.sellingPerPc,
  );
};

const invMergeGetLotVendor = (lot) => {
  const source = invMergeGetLotSource(lot);
  return source?.vendor_name || source?.vendorName || lot?.vendor || "No Vendor";
};

const invMergeGetLotEntryDate = (lot) => {
  const source = invMergeGetLotSource(lot);
  return source?.date || lot?.date || source?.created_at || source?.updated_at || "";
};

const invMergeGetLotStatus = (lot) => {
  const source = invMergeGetLotSource(lot);
  if (source?.is_segment_deleted || lot?.isDeleted) return "Deleted";
  return source?.segment_status || source?.status || lot?.status || "Active";
};

// expiry_date/expiry_status come straight from ProductInventorySerializer
// on the raw lot record (rawEntry) - null/undefined for any lot that isn't
// perishable, same as everywhere else expiry shows up.
const invMergeGetLotExpiryDate = (lot) => {
  const source = invMergeGetLotSource(lot);
  return source?.expiry_date || lot?.expiry_date || null;
};

const invMergeGetLotExpiryStatus = (lot) => {
  const source = invMergeGetLotSource(lot);
  return source?.expiry_status || lot?.expiry_status || null;
};

const invMergeExpiryChipClass = (expiryStatus) => {
  if (expiryStatus === "expired") return "danger";
  if (expiryStatus === "expiring_soon") return "warning";
  return "success";
};

const invMergeExpiryLabel = (expiryDate, expiryStatus) => {
  if (!expiryDate) return null;
  if (expiryStatus === "expired") return `${expiryDate} (Expired)`;
  if (expiryStatus === "expiring_soon") return `${expiryDate} (Soon)`;
  return expiryDate;
};

// Earliest expiring lot within a UOM group, so the summary row can flag
// "something in this group needs attention" without listing every lot.
const invMergeGetNearestExpiry = (lots = []) => {
  const withExpiry = (lots || [])
    .map((lot) => ({ date: invMergeGetLotExpiryDate(lot), status: invMergeGetLotExpiryStatus(lot) }))
    .filter((entry) => Boolean(entry.date));
  if (!withExpiry.length) return null;
  return withExpiry.sort((a, b) => new Date(a.date) - new Date(b.date))[0];
};

// Corner alert badge on the product thumbnail in the collapsed catalog row -
// a separate, coarser tiering than the per-lot "expired/expiring soon" chips
// above (which use a tight 7-day window): red inside 30 days (or already
// expired), yellow inside 60, green inside 90, nothing beyond that so
// long-dated/non-perishable products stay unbadged.
const invMergeGetDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (Number.isNaN(target.getTime())) return null;
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const invMergeGetGroupExpiryAlert = (group) => {
  const lots = group?.stockLots || [];
  let nearest = null;
  lots.forEach((lot) => {
    const expiryDate = invMergeGetLotExpiryDate(lot);
    if (!expiryDate) return;
    const daysLeft = invMergeGetDaysUntil(expiryDate);
    if (daysLeft === null) return;
    if (nearest === null || daysLeft < nearest.daysLeft) {
      nearest = { date: expiryDate, daysLeft };
    }
  });
  if (!nearest) return null;

  let tier = null;
  if (nearest.daysLeft <= 30) tier = "red";
  else if (nearest.daysLeft <= 60) tier = "yellow";
  else if (nearest.daysLeft <= 90) tier = "green";
  if (!tier) return null;

  return { ...nearest, tier };
};

const invMergeExpiryAlertLabel = (alert) => {
  if (!alert) return "";
  if (alert.daysLeft < 0) return `Expired ${Math.abs(alert.daysLeft)}d ago (${alert.date})`;
  if (alert.daysLeft === 0) return `Expires today (${alert.date})`;
  return `Expires in ${alert.daysLeft}d (${alert.date})`;
};

// Short text for the on-thumbnail chip itself (the tooltip above carries
// the full date) - kept tight since it overlays a 48px product image.
const invMergeExpiryAlertChipText = (alert) => {
  if (!alert) return "";
  if (alert.daysLeft < 0) return "Expired";
  if (alert.daysLeft === 0) return "Today";
  return `${alert.daysLeft}d left`;
};

const invMergeGetLotCategory = (lot) => {
  const source = invMergeGetLotSource(lot);
  return lot?.category || invMergeGetHistoryCategory(source) || "-";
};

const invMergeGetLotUom = (lot) => {
  const source = invMergeGetLotSource(lot);
  const segment = lot?.rawSegment || {};
  return lot?.uom || segment?.segment_uom || invMergeGetHistoryUom(source) || "-";
};

const invMergeGetLotBarcode = (lot) => {
  const source = invMergeGetLotSource(lot);
  const segment = lot?.rawSegment || {};
  return lot?.barcode || segment?.segment_barcode || source?.product_barcode || source?.barcode || "";
};

const invMergeGetLotMarginPerPc = (lot) => {
  return invMergeGetLotSellingPrice(lot) - invMergeGetLotPurchasePrice(lot);
};

const invMergeGetLotCostValue = (lot) => {
  return invMergeGetLotCurrentStock(lot) * invMergeGetLotPurchasePrice(lot);
};

const invMergeGetLotSalesValue = (lot) => {
  return invMergeGetLotCurrentStock(lot) * invMergeGetLotSellingPrice(lot);
};

const invMergeGetLotProfitValue = (lot) => {
  return invMergeGetLotCurrentStock(lot) * invMergeGetLotMarginPerPc(lot);
};

const invMergeGetProfitPercent = (profitValue, costValue) => {
  const cost = invMergeSafeNumber(costValue);
  if (cost <= 0) return 0;
  return (invMergeSafeNumber(profitValue) / cost) * 100;
};

const invMergeFormatPercent = (value) => {
  const number = invMergeSafeNumber(value);
  return `${number.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
};

const invMergeProfitClass = (value) => {
  const number = invMergeSafeNumber(value);
  if (number < 0) return "loss";
  if (number > 0) return "profit";
  return "neutral";
};

const invMergeFormatProfitRange = (values = []) => {
  const filteredValues = values
    .map((value) => invMergeSafeNumber(value))
    .filter((value) => Number.isFinite(value));

  if (!filteredValues.length) return "₹0.00";

  const min = Math.min(...filteredValues);
  const max = Math.max(...filteredValues);

  return min === max
    ? `₹${invMergeFormatMoney(min)}`
    : `₹${invMergeFormatMoney(min)} - ₹${invMergeFormatMoney(max)}`;
};

const invMergeUniqueRowsByKey = (rows = []) => {
  const seen = new Set();

  return (Array.isArray(rows) ? rows : []).filter((row, index) => {
    if (!row) return false;
    const segmentKey = Array.isArray(row?.segment_inventory)
      ? row.segment_inventory
          .map((segment) => segment?.id || segment?.segment || segment?.segment_barcode)
          .filter(Boolean)
          .join("|")
      : "";
    const key =
      row?.id ||
      `${row?.product || row?.product_id || row?.product_title || "row"}-${row?.batch || ""}-${row?.date || row?.created_at || ""}-${segmentKey || index}`;

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const invMergeGetSegmentArray = (row) =>
  Array.isArray(row?.segment_inventory)
    ? row.segment_inventory
    : Array.isArray(row?.segments)
      ? row.segments
      : [];

const invMergeStatusClass = (status) => {
  const normalized = invMergeNormalizeText(status);

  if (normalized.includes("delete") || normalized.includes("inactive")) return "danger";
  if (normalized.includes("pending") || normalized.includes("hold")) return "warning";
  return "success";
};

const invMergeFindMatchingProductKey = (productMap, entry, historyKey) => {
  const entryProductId = typeof entry?.product === "object" ? entry.product?.id : entry?.product;

  if (entryProductId) {
    const matchedById = Array.from(productMap.entries()).find(([, group]) =>
      group?.productIds?.has?.(entryProductId),
    );

    if (matchedById) return matchedById[0];
  }

  if (productMap.has(historyKey)) return historyKey;

  return (
    Array.from(productMap.keys()).find((productKey) => {
      if (!productKey || !historyKey) return false;
      return productKey.includes(historyKey) || historyKey.includes(productKey);
    }) || historyKey
  );
};

const invMergeGetStockLotsFromHistory = (entry) => {
  const segments = invMergeGetSegmentArray(entry);
  const category = invMergeGetHistoryCategory(entry);
  const vendor = invMergeGetHistoryVendor(entry);
  const date = entry?.date || entry?.created_at || entry?.updated_at || entry?.timestamp;
  const batch = entry?.batch || "-";
  const status = entry?.is_segment_deleted ? "Deleted" : entry?.segment_status || entry?.status || "Active";
  const entryCurrentStock = invMergeSafeNumber(entry?.current_stock ?? entry?.remaining_inventory ?? entry?.total_current_stock);
  const entryTotalPieces = invMergeSafeNumber(entry?.total_stock_arrived ?? entry?.stock_arrived ?? entry?.total_piece_added);
  const productTitle = invMergeGetHistoryTitle(entry);
  const productId = typeof entry?.product === "object" ? entry.product?.id : entry?.product;
  const entryBarcode = entry?.product_barcode || entry?.barcode || invMergeGetBarcode(entry);

  if (segments.length > 0) {
    return segments.map((segment, index) => {
      const pcInUom = invMergeSafeNumber(segment?.segment_total_pc_in_uom ?? segment?.total_pc_in_uom ?? segment?.totalPcInUom ?? 1) || 1;
      const totalUomArrivedFromPayload = invMergeParseFirstNumber(segment?.total_uom_arrived ?? segment?.added_stock ?? segment?.stock_added ?? entry?.arrayField);
      const uomAdded =
        totalUomArrivedFromPayload > 0
          ? totalUomArrivedFromPayload
          : (entryTotalPieces > 0 && pcInUom > 0 ? entryTotalPieces / pcInUom : 0);
      const totalPiecesFromSegment = uomAdded * pcInUom;
      const totalPieces = totalPiecesFromSegment || entryTotalPieces;
      const currentStock = segments.length === 1 ? entryCurrentStock : totalPieces;
      const purchasePerUom = invMergeSafeNumber(segment?.purchase_price ?? entry?.purchase_price ?? entry?.purchaseprice);
      const sellingPerUom = invMergeSafeNumber(segment?.selling_price ?? entry?.selling_price ?? entry?.sellingprice);
      const purchasePerPc =
        invMergeSafeNumber(segment?.purchase_price_per_pc) ||
        (purchasePerUom > 0 && pcInUom > 0 ? purchasePerUom / pcInUom : invMergeSafeNumber(entry?.purchase_price));
      const sellingPerPc =
        invMergeSafeNumber(segment?.selling_price_per_pc) ||
        (sellingPerUom > 0 && pcInUom > 0 ? sellingPerUom / pcInUom : invMergeSafeNumber(entry?.selling_price));
      const segmentUom = segment?.segment_uom || segment?.uom_title || segment?.uom || invMergeGetHistoryUom(entry);
      const barcode = segment?.segment_barcode || entryBarcode || "";

      return {
        id: segment?.id || `${entry?.id || productTitle || "lot"}-${index}`,
        entryId: entry?.id || "",
        productId,
        productTitle,
        batch,
        category,
        vendor,
        date,
        status,
        uom: segmentUom || "-",
        pcInUom,
        uomAdded,
        totalPieces,
        currentStock,
        purchasePerPc,
        sellingPerPc,
        purchasePerUom,
        sellingPerUom,
        barcode,
        rawEntry: entry,
        rawSegment: segment,
      };
    });
  }

  const pcInUom = invMergeSafeNumber(invMergeGetHistoryPiecesInUom(entry) || 1) || 1;
  const uomAdded = invMergeParseFirstNumber(invMergeGetHistoryStockAdded(entry)) || (entryTotalPieces > 0 ? entryTotalPieces / pcInUom : 0);

  return [
    {
      id: entry?.id || `${productTitle}-${date}`,
      entryId: entry?.id || "",
      productId,
      productTitle,
      batch,
      category,
      vendor,
      date,
      status,
      uom: invMergeGetHistoryUom(entry),
      pcInUom,
      uomAdded,
      totalPieces: entryTotalPieces || invMergeGetHistoryQty(entry),
      currentStock: entryCurrentStock,
      purchasePerPc: invMergeSafeNumber(invMergeGetHistoryPurchase(entry)),
      sellingPerPc: invMergeSafeNumber(invMergeGetHistorySelling(entry)),
      purchasePerUom: invMergeSafeNumber(invMergeGetHistoryPurchase(entry)),
      sellingPerUom: invMergeSafeNumber(invMergeGetHistorySelling(entry)),
      barcode: entryBarcode,
      rawEntry: entry,
    },
  ];
};

const invMergeGetStockLotsFromVariants = (variants = []) =>
  variants.map((variant, index) => ({
    id: variant?.id || `variant-${index}`,
    entryId: variant?.id || "",
    batch: variant?.batch || "-",
    category: invMergeGetCategories(variant).join(", "),
    vendor: variant?.vendor_name || variant?.vendor?.name || variant?.vendor || "-",
    date: variant?.date || variant?.created_at || variant?.updated_at || "",
    status: variant?.is_segment_deleted ? "Deleted" : variant?.segment_status || "Active",
    uom: invMergeGetUom(variant),
    pcInUom: invMergeSafeNumber(variant?.total_pc_in_uom || variant?.segment_total_pc_in_uom || 1) || 1,
    uomAdded: invMergeSafeNumber(variant?.total_uom_arrived || variant?.remaining_inventory || variant?.current_stock),
    totalPieces: invMergeGetStock(variant),
    currentStock: invMergeGetStock(variant),
    purchasePerPc:
      invMergeSafeNumber(variant?.purchase_price_per_pc) ||
      (invMergeGetPurchase(variant) > 0 && (invMergeSafeNumber(variant?.total_pc_in_uom || variant?.segment_total_pc_in_uom || 1) || 1) > 0
        ? invMergeGetPurchase(variant) / (invMergeSafeNumber(variant?.total_pc_in_uom || variant?.segment_total_pc_in_uom || 1) || 1)
        : invMergeGetPurchase(variant)),
    sellingPerPc:
      invMergeSafeNumber(variant?.selling_price_per_pc) ||
      (invMergeGetSelling(variant) > 0 && (invMergeSafeNumber(variant?.total_pc_in_uom || variant?.segment_total_pc_in_uom || 1) || 1) > 0
        ? invMergeGetSelling(variant) / (invMergeSafeNumber(variant?.total_pc_in_uom || variant?.segment_total_pc_in_uom || 1) || 1)
        : invMergeGetSelling(variant)),
    purchasePerUom: invMergeGetPurchase(variant),
    sellingPerUom: invMergeGetSelling(variant),
    barcode: invMergeGetBarcode(variant),
  }));

const invMergeBuildUomSummary = (stockLots = []) => {
  const summaryMap = new Map();

  stockLots.forEach((lot) => {
    const uom = lot.uom || "-";
    const pcInUom = invMergeSafeNumber(lot.pcInUom || 1) || 1;
    const key = `${uom}__${pcInUom}`;

    if (!summaryMap.has(key)) {
      summaryMap.set(key, {
        key,
        uom,
        pcInUom,
        categories: new Set(),
        vendors: new Set(),
        barcodes: new Set(),
        lotCount: 0,
        totalUomAdded: 0,
        totalPiecesArrived: 0,
        currentStock: 0,
        stockValue: 0,
        costValue: 0,
        profitValue: 0,
        marginPerPcValues: [],
        profitPercentValues: [],
        purchasePrices: [],
        sellingPrices: [],
        lots: [],
      });
    }

    const summary = summaryMap.get(key);
    if (lot.category) summary.categories.add(lot.category);
    if (lot.vendor && lot.vendor !== "-") summary.vendors.add(lot.vendor);
    if (lot.barcode) summary.barcodes.add(lot.barcode);
    summary.lotCount += 1;
    summary.totalUomAdded += invMergeSafeNumber(lot.uomAdded);
    summary.totalPiecesArrived += invMergeSafeNumber(lot.totalPieces);
    summary.currentStock += invMergeGetLotCurrentStock(lot);
    summary.stockValue += invMergeGetLotSalesValue(lot);
    summary.costValue += invMergeGetLotCostValue(lot);
    summary.profitValue += invMergeGetLotProfitValue(lot);

    const marginPerPc = invMergeGetLotMarginPerPc(lot);
    const lotCostValue = invMergeGetLotCostValue(lot);
    const lotProfitValue = invMergeGetLotProfitValue(lot);

    if (invMergeGetLotPurchasePrice(lot) > 0) summary.purchasePrices.push(invMergeGetLotPurchasePrice(lot));
    if (invMergeGetLotSellingPrice(lot) > 0) summary.sellingPrices.push(invMergeGetLotSellingPrice(lot));
    if (invMergeGetLotPurchasePrice(lot) > 0 || invMergeGetLotSellingPrice(lot) > 0) {
      summary.marginPerPcValues.push(marginPerPc);
    }
    if (lotCostValue > 0) {
      summary.profitPercentValues.push(invMergeGetProfitPercent(lotProfitValue, lotCostValue));
    }

    summary.lots.push(lot);
  });

  return Array.from(summaryMap.values()).sort((a, b) => a.uom.localeCompare(b.uom));
};

const InvMergeHistoryMeta = ({ label, value, money = false }) => {
  if (!invMergeHasDisplayValue(value)) return null;

  const display = money ? `₹${invMergeFormatMoney(value)}` : invMergeDisplayValue(value);
  if (!display) return null;

  return (
    <span className="invmerge-history-meta">
      <small>{label}</small>
      <strong>{display}</strong>
    </span>
  );
};

const invMergeNormalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const invMergeGetTitle = (item) =>
  item?.title || item?.product_title || item?.product_name || item?.combinedField || "Untitled Product";

const invMergeGetBarcode = (item) => {
  const source = item?.__sourceProduct || item || {};

  return (
    source.product_barcode ||
    source.barcode ||
    source.segment_barcode ||
    source.code_sku ||
    item?.product_barcode ||
    item?.barcode ||
    item?.segment_barcode ||
    ""
  );
};

const invMergeGetCategories = (item) => {
  const categories = [];

  const add = (value) => {
    if (!value) return;
    if (typeof value === "string") {
      const label = value.trim();
      if (label && !categories.includes(label)) categories.push(label);
      return;
    }
    if (typeof value === "object") {
      const label = value.name || value.title || value.category_name || value.label;
      if (label && !categories.includes(label)) categories.push(label);
    }
  };

  add(item?.category);
  add(item?.category_name);
  if (Array.isArray(item?.product_category)) {
    item.product_category.forEach(add);
  } else {
    add(item?.product_category);
  }

  if (Array.isArray(item?.categories?.assigned)) item.categories.assigned.forEach(add);
  if (Array.isArray(item?.categories?.breadcrumb)) item.categories.breadcrumb.flat(Infinity).forEach(add);
  if (Array.isArray(item?.assigned_categories)) item.assigned_categories.forEach(add);

  return categories.length ? categories : ["Uncategorized"];
};

const invMergeGetSegments = (product) => {
  if (Array.isArray(product?.segments) && product.segments.length > 0) {
    return product.segments.map((segment) => ({ ...product, ...segment, __sourceProduct: product }));
  }

  const fallbackHasPrice =
    product?.UOM ||
    product?.uom ||
    product?.uom_title ||
    product?.purchasePrice ||
    product?.purchase_price ||
    product?.sellingPrice ||
    product?.selling_price ||
    product?.current_stock ||
    product?.total_current_stock;

  if (!fallbackHasPrice) {
    return [{ ...product, __sourceProduct: product }];
  }

  return [
    {
      ...product,
      __sourceProduct: product,
      uom_title: product?.UOM || product?.uom_title || product?.uom || product?.unit || "-",
      purchase_price: product?.purchasePrice || product?.purchase_price || product?.cost_price || "",
      selling_price: product?.sellingPrice || product?.selling_price || product?.sale_price || "",
      remaining_inventory: product?.current_stock || product?.total_current_stock || product?.remaining_inventory || "",
    },
  ];
};

const invMergeGetUom = (item) =>
  item?.uom_title || item?.UOM || item?.uom || item?.unit || item?.unit_name || "-";

const invMergeGetStock = (item) =>
  invMergeSafeNumber(
    item?.remaining_inventory ??
      item?.current_stock ??
      item?.total_current_stock ??
      item?.stock ??
      item?.opening_inventory ??
      0,
  );

const invMergeGetPurchase = (item) =>
  invMergeSafeNumber(
    item?.purchase_price ??
      item?.purchasePrice ??
      item?.purchase_price_per_pc ??
      item?.cost_price ??
      0,
  );

const invMergeGetSelling = (item) =>
  invMergeSafeNumber(
    item?.selling_price ??
      item?.sellingPrice ??
      item?.selling_price_per_pc ??
      item?.sale_price ??
      0,
  );

const invMergeGetStatus = (stock) => {
  const current = invMergeSafeNumber(stock);
  if (current <= 0) return { label: "No Stock", className: "danger" };
  if (current <= 20) return { label: "Low", className: "warning" };
  if (current <= 100) return { label: "Watch", className: "info" };
  return { label: "Healthy", className: "success" };
};

const InvMergeMiniMetric = ({ icon: Icon, label, value, tone = "blue" }) => (
  <div className={`invmerge-mini-metric ${tone}`}>
    <span className="invmerge-mini-icon">
      <Icon size={16} />
    </span>
    <div>
      <strong>{value}</strong>
      <p>{label}</p>
    </div>
  </div>
);

const InvMergeStockBadge = ({ value }) => {
  const status = invMergeGetStatus(value);
  return <span className={`invmerge-stock-badge ${status.className}`}>{status.label}</span>;
};

const InvMergeProductRow = ({ group, expanded, onToggle, onView, onEdit, onAddStock, onAddSegment, onEditExpiry }) => {
  const expiryAlert = invMergeGetGroupExpiryAlert(group);

  // Inline "edit expiry" state for a single stock lot row. The Quick Edit
  // Product modal intentionally only covers product-level fields (see its
  // own note), so this is the only place an owner can set/change expiry on
  // an existing lot without re-adding stock.
  const [editingExpiryLotId, setEditingExpiryLotId] = useState(null);
  const [editingExpiryValue, setEditingExpiryValue] = useState("");
  const [savingExpiryLotId, setSavingExpiryLotId] = useState(null);

  const startEditExpiry = (lot, currentValue) => {
    setEditingExpiryLotId(lot.id);
    setEditingExpiryValue(currentValue || "");
  };

  const cancelEditExpiry = () => {
    setEditingExpiryLotId(null);
    setEditingExpiryValue("");
  };

  const saveEditExpiry = async (lot) => {
    if (!onEditExpiry) return;
    setSavingExpiryLotId(lot.id);
    try {
      await onEditExpiry(lot, editingExpiryValue || null);
      setEditingExpiryLotId(null);
      setEditingExpiryValue("");
    } finally {
      setSavingExpiryLotId(null);
    }
  };

  return (
  <div className={`invmerge-row ${expanded ? "expanded" : ""}`}>
    <div className="invmerge-row-main">
      <button type="button" className="invmerge-row-trigger" onClick={onToggle}>
        <span className="invmerge-product-img-wrap">
          <span className={`invmerge-product-img ${group.imageUrl ? "has-image" : ""}`}>
            {group.imageUrl && (
              <img
                src={group.imageUrl}
                alt={group.title}
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                  event.currentTarget.parentElement?.classList.remove("has-image");
                }}
              />
            )}
            <Package className="invmerge-placeholder-icon" size={21} />
          </span>
          {expiryAlert && (
            <span
              className={`invmerge-expiry-alert-chip ${expiryAlert.tier}`}
              title={invMergeExpiryAlertLabel(expiryAlert)}
            >
              {invMergeExpiryAlertChipText(expiryAlert)}
            </span>
          )}
        </span>

        <span className="invmerge-product-info">
          <strong>{group.title}</strong>
          <span>
            {group.categories.join(", ")} • {invMergeGetBarcode(group.primaryProduct || group.variants?.[0]) ? `Barcode: ${invMergeGetBarcode(group.primaryProduct || group.variants?.[0])} • ` : ""}{group.uomSummary?.length || group.variants.length} UOM group{(group.uomSummary?.length || group.variants.length) > 1 ? "s" : ""} • {group.stockLots?.length || 0} stock lot{(group.stockLots?.length || 0) !== 1 ? "s" : ""}
          </span>
        </span>
      </button>

      <div className="invmerge-row-stats">
        <span>
          <small>Total Stock</small>
          <b>{invMergeFormatCount(group.totalStock)}</b>
        </span>
        <span>
          <small>Stock Value</small>
          <b>₹{invMergeFormatMoney(group.stockValue)}</b>
        </span>
        <span className={`invmerge-profit-stat ${invMergeProfitClass(group.profitValue)}`}>
          <small>Gross Profit</small>
          <b>₹{invMergeFormatMoney(group.profitValue)}</b>
        </span>
        <InvMergeStockBadge value={group.totalStock} />
      </div>

      <div className="invmerge-row-actions">
        <button type="button" className="invmerge-action-btn view" onClick={onView} title="View product details">
          <Eye size={15} /> View
        </button>
        <button type="button" className="invmerge-action-btn edit" onClick={onEdit} title="Edit product basics">
          <Pencil size={15} /> Edit
        </button>
        <button type="button" className="invmerge-action-btn stock" onClick={onAddStock} title="Add stock for this product">
          <Boxes size={15} /> Stock
        </button>
        <button type="button" className="invmerge-action-btn segment" onClick={onAddSegment} title="Add product segment for this product">
          <PlusCircle size={15} /> Segment
        </button>
        <button type="button" className="invmerge-icon-toggle" onClick={onToggle} aria-label="Expand product">
          <ChevronDown size={20} />
        </button>
      </div>
    </div>

    {expanded && (
      <div className="invmerge-expanded">
        <div className="invmerge-expanded-summary-strip">
          <span>
            <small>Combined Available Stock</small>
            <strong>{invMergeFormatCount(group.totalStock)} pcs</strong>
          </span>
          <span>
            <small>Stock Value</small>
            <strong>₹{invMergeFormatMoney(group.stockValue)}</strong>
          </span>
          <span>
            <small>Stock Cost</small>
            <strong>₹{invMergeFormatMoney(group.costValue)}</strong>
          </span>
          <span className={`invmerge-profit-summary ${invMergeProfitClass(group.profitValue)}`}>
            <small>Gross Profit</small>
            <strong>₹{invMergeFormatMoney(group.profitValue)}</strong>
          </span>
          <span>
            <small>Profit Margin</small>
            <strong>{invMergeFormatPercent(group.profitPercent)}</strong>
          </span>
          <span>
            <small>UOM Groups</small>
            <strong>{invMergeFormatCount(group.uomSummary?.length || 0)}</strong>
          </span>
          <span>
            <small>Stock Lots</small>
            <strong>{invMergeFormatCount(group.stockLots?.length || 0)}</strong>
          </span>
        </div>

        <div className="invmerge-expanded-stack stock-logic">
          <div className="invmerge-expanded-card invmerge-uom-card">
            <div className="invmerge-card-title invmerge-card-title-between">
              <span><Layers size={16} /> UOM-wise Available Stock</span>
              <em>{invMergeFormatCount(group.uomSummary?.length || 0)} UOM group{(group.uomSummary?.length || 0) !== 1 ? "s" : ""}</em>
            </div>
            <div className="invmerge-table-wrap">
              <table className="invmerge-table invmerge-uom-summary-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>UOM</th>
                    <th>Barcode</th>
                    <th>Lots</th>
                    <th>Added Stock</th>
                    <th>Pc / UOM</th>
                    <th>Pieces Arrived</th>
                    <th>Current Stock</th>
                    <th>Stock Value</th>
                    <th>Purchase / Pc</th>
                    <th>Selling / Pc</th>
                    <th>Margin / Pc</th>
                    <th>Gross Profit</th>
                    <th>Margin %</th>
                    <th>Vendors</th>
                    <th>Nearest Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {(group.uomSummary || []).map((summary) => {
                    const nearestExpiry = invMergeGetNearestExpiry(summary.lots);
                    return (
                    <tr key={summary.key}>
                      <td>{Array.from(summary.categories).join(", ") || "-"}</td>
                      <td><strong>{summary.uom}</strong></td>
                      <td>{Array.from(summary.barcodes).join(", ") || "-"}</td>
                      <td>{invMergeFormatCount(summary.lotCount)}</td>
                      <td>{invMergeFormatCount(summary.totalUomAdded)} {summary.uom}</td>
                      <td>{invMergeFormatCount(summary.pcInUom)}</td>
                      <td>{invMergeFormatCount(summary.totalPiecesArrived)}</td>
                      <td className="invmerge-strong-green">{invMergeFormatCount(summary.currentStock)}</td>
                      <td>₹{invMergeFormatMoney(summary.stockValue)}</td>
                      <td>{invMergeFormatPriceRange(summary.purchasePrices)}</td>
                      <td>{invMergeFormatPriceRange(summary.sellingPrices)}</td>
                      <td className={invMergeProfitClass(summary.profitValue)}>
                        {invMergeFormatProfitRange(summary.marginPerPcValues)}
                      </td>
                      <td className={invMergeProfitClass(summary.profitValue)}>
                        ₹{invMergeFormatMoney(summary.profitValue)}
                      </td>
                      <td className={invMergeProfitClass(summary.profitValue)}>
                        {invMergeFormatPercent(invMergeGetProfitPercent(summary.profitValue, summary.costValue))}
                      </td>
                      <td>{Array.from(summary.vendors).join(", ") || "-"}</td>
                      <td>
                        {nearestExpiry ? (
                          <span className={`invmerge-status-chip ${invMergeExpiryChipClass(nearestExpiry.status)}`}>
                            {invMergeExpiryLabel(nearestExpiry.date, nearestExpiry.status)}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="invmerge-expanded-card invmerge-stock-history-card">
            <div className="invmerge-card-title invmerge-card-title-between">
              <span><History size={16} /> All Stock Lots / Movement</span>
              <em>{invMergeFormatCount(group.stockLots?.length || 0)} stock lot{(group.stockLots?.length || 0) !== 1 ? "s" : ""}</em>
            </div>

            {group.stockLots && group.stockLots.length > 0 ? (
              <div className="invmerge-table-wrap invmerge-stock-lot-table-wrap">
                <table className="invmerge-table invmerge-stock-history-table">
                  <thead>
                    <tr>
                      <th>Product Title</th>
                      <th>Added Stock</th>
                      <th>Total Pieces Arrived</th>
                      <th>Current Stock</th>
                      <th>Purchase Price</th>
                      <th>Selling Price</th>
                      <th>Margin / Pc</th>
                      <th>Gross Profit</th>
                      <th>Vendor</th>
                      <th>Entry Date</th>
                      <th>Expiry Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.stockLots.map((lot, index) => (
                      <tr key={`${group.key}-lot-table-${lot.id || index}`}>
                        <td>
                          <strong>{invMergeGetLotProductDisplay(lot, group.title)}</strong>
                          <small className="invmerge-table-subtext">
                            {invMergeGetLotCategory(lot)} • {invMergeGetLotUom(lot)}
                            {invMergeGetLotBarcode(lot) ? ` • Barcode: ${invMergeGetLotBarcode(lot)}` : ""}
                          </small>
                          <small className="invmerge-table-subtext">Batch: {lot.batch || "-"}</small>
                        </td>
                        <td>
                          <strong>{invMergeGetLotAddedStockDisplay(lot)}</strong>
                          <small className="invmerge-table-subtext">{invMergeFormatStockFormula(lot)}</small>
                        </td>
                        <td>{invMergeFormatCount(invMergeGetLotTotalPieces(lot))}</td>
                        <td className="invmerge-strong-green">{invMergeFormatCount(invMergeGetLotCurrentStock(lot))}</td>
                        <td>₹{invMergeFormatMoney(invMergeGetLotPurchasePrice(lot))}</td>
                        <td>₹{invMergeFormatMoney(invMergeGetLotSellingPrice(lot))}</td>
                        <td className={invMergeProfitClass(invMergeGetLotProfitValue(lot))}>
                          ₹{invMergeFormatMoney(invMergeGetLotMarginPerPc(lot))}
                        </td>
                        <td className={invMergeProfitClass(invMergeGetLotProfitValue(lot))}>
                          <strong>₹{invMergeFormatMoney(invMergeGetLotProfitValue(lot))}</strong>
                          <small className="invmerge-table-subtext">
                            {invMergeFormatPercent(invMergeGetProfitPercent(invMergeGetLotProfitValue(lot), invMergeGetLotCostValue(lot)))}
                          </small>
                        </td>
                        <td>{invMergeGetLotVendor(lot)}</td>
                        <td>{invMergeFormatDateTime(invMergeGetLotEntryDate(lot))}</td>
                        <td>
                          {editingExpiryLotId === lot.id ? (
                            <span className="invmerge-expiry-edit">
                              <input
                                type="date"
                                className="invmerge-expiry-edit-input"
                                value={editingExpiryValue || ""}
                                onChange={(event) => setEditingExpiryValue(event.target.value)}
                                disabled={savingExpiryLotId === lot.id}
                              />
                              <button
                                type="button"
                                className="invmerge-expiry-edit-btn save"
                                title="Save expiry date"
                                onClick={() => saveEditExpiry(lot)}
                                disabled={savingExpiryLotId === lot.id}
                              >
                                <Check size={14} />
                              </button>
                              <button
                                type="button"
                                className="invmerge-expiry-edit-btn cancel"
                                title="Cancel"
                                onClick={cancelEditExpiry}
                                disabled={savingExpiryLotId === lot.id}
                              >
                                <X size={14} />
                              </button>
                            </span>
                          ) : (
                            <span className="invmerge-expiry-display">
                              {invMergeGetLotExpiryDate(lot) ? (
                                <span className={`invmerge-status-chip ${invMergeExpiryChipClass(invMergeGetLotExpiryStatus(lot))}`}>
                                  {invMergeExpiryLabel(invMergeGetLotExpiryDate(lot), invMergeGetLotExpiryStatus(lot))}
                                </span>
                              ) : (
                                "-"
                              )}
                              {onEditExpiry && (
                                <button
                                  type="button"
                                  className="invmerge-expiry-edit-btn edit"
                                  title="Set / change expiry date"
                                  onClick={() => startEditExpiry(lot, invMergeGetLotExpiryDate(lot))}
                                >
                                  <Pencil size={12} />
                                </button>
                              )}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`invmerge-status-chip ${invMergeStatusClass(invMergeGetLotStatus(lot))}`}>
                            {invMergeGetLotStatus(lot)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="invmerge-history-empty">
                No stock movement found for this product.
              </div>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
  );
};

const Product = () => {
  const {
    productTitle,
    purchasePrice,
    sellingPrice,
    openingInventory,
    expiryDate,
    setExpiryDate,
    barcode,
    productList,
    isEditMode,
    handleProductTitleChange,
    handlePurchasePriceChange,
    handleSellingPriceChange,
    handleCategoryChange,
    handleOpeningInventoryChange,
    handleBarcodeChange,
    handleAddProduct,
    handleDeleteProduct,
    handleEditProduct,
    showModal,
    handleOpenModal,
    handleCloseModal,
    assignBarcode,
    setAssignBarcode,
    sellingPriceError,
    handleSellingPriceBlur,
    vendors,
    selectedVendorId,
    handleVendorChange,
    selectedVendorName,
    showVendorModal,
    handleCloseVendorModal,
    updateVendors,
    handleVendorAdded,
    totalPcInUom,
    handleTotalPcInUomChange,
    resetForm,
    currentPage,
    totalCount,
    handlePageChange,
    loading,
    handleDataUpdate,
    selectedUom,
    setSelectedUom,
    handleBlurOpeningInventory,
    handleFocusOpeningInventory,
    searchTerm,
    handleSearch,
    setSelectedVendorId,
    setShowVendorModal,
    setSelectedVendorName,
    errors,
    handleTitleBlur,
    handleCategoryBlur,
    handlePurchasePriceBlur,
    handleTotalPcBlur,
    productImage,
    handleProductImageChange,
    imagePreview,
    autoGenerateBarcode,
    setAutoGenerateBarcode,
    fileInputRef,
    galleryPreview,
    handleRemovePrimaryImage,
    handleGalleryUpload,
    removeGalleryImage,
    shortDescription,
    longDescription,
    setLongDescription,
    setShortDescription,
    hideFromOtherStores,
    setHideFromOtherStores,
    selectedCategory,
    setSelectedCategory,
    selectedWeightUnit,
    setSelectedWeightUnit,
    weightValue,
    setWeightValue,
  } = ProductController();
  const { showUomModal, setShowUomModal, handleCloseUomModal } =
    UomController();
  const {
    showModal: showStockModal,
    handleOpenModal: handleOpenStockModal,
    handleCloseModal: handleCloseStockModal,
    productList: stockProductList,
    selectedProduct: stockSelectedProduct,
    barcode: stockBarcode,
    category: stockCategory,
    uom: stockUom,
    purchasePrice: stockPurchasePrice,
    sellingPrice: stockSellingPrice,
    currentStock: stockCurrentStock,
    totalStockArrived: stockTotalStockArrived,
    isNewProduct: stockIsNewProduct,
    isSubmitting: stockIsSubmitting,
    showConfirmation: stockShowConfirmation,
    productTitle: stockProductTitle,
    openingInventory: stockOpeningInventory,
    expiryDate: stockExpiryDate,
    setExpiryDate: stockSetExpiryDate,
    setSelectedProduct: stockSetSelectedProduct,
    setTotalStockArrived: stockSetTotalStockArrived,
    handleSubmit: stockHandleSubmit,
    handleCancelNewProduct: stockHandleCancelNewProduct,
    scanBarcode: stockScanBarcode,
    handleBarcodeChange: stockHandleBarcodeChange,
    handleProductTitleChange: stockHandleProductTitleChange,
    handleOpeningInventoryChange: stockHandleOpeningInventoryChange,
    handleUomChange: stockHandleUomChange,
    purchasePriceError: stockPurchasePriceError,
    sellingPriceError: stockSellingPriceError,
    handlePurchasePriceBlur: stockHandlePurchasePriceBlur,
    handleSellingPriceBlur: stockHandleSellingPriceBlur,
    handlePurchasePriceChange: stockHandlePurchasePriceChange,
    handleSellingPriceChange: stockHandleSellingPriceChange,
    vendors: stockVendors,
    selectedVendorId: stockSelectedVendorId,
    showVendorModal: stockShowVendorModal,
    handleVendorChange: stockHandleVendorChange,
    handleCloseVendorModal: stockHandleCloseVendorModal,
    updateVendors: stockUpdateVendors,
    handleVendorAdded: stockHandleVendorAdded,
    totalPcInUom: stockTotalPcInUom,
    handleTotalPcInUomChange: stockHandleTotalPcInUomChange,
    segments: stockSegments,
    selectedSegment: stockSelectedSegment,
    resetForm: stockResetForm,
    handleDataUpdate: stockHandleDataUpdate,
    setSearchQuery: stockSetSearchQuery,
    fetchProductList: stockFetchProductList,
    setProductTitle: stockSetProductTitle,
    selectedUom: stockSelectedUom,
    setSelectedUom: stockSetSelectedUom,
    handleFocusOpeningInventory: stockHandleFocusOpeningInventory,
    handleBlurOpeningInventory: stockHandleBlurOpeningInventory,
    selectedCategory: stockSelectedCategory,
    setSelectedCategory: stockSetSelectedCategory,
    autoGenerateBarcode: stockAutoGenerateBarcode,
    setAutoGenerateBarcode: stockSetAutoGenerateBarcode,
    selectedVendorName: stockSelectedVendorName,
    setSelectedVendorName: stockSetSelectedVendorName,
    inventoryData: stockInventoryHistoryData,
  } = ManageStockController();

  const stockHistoryData = Array.isArray(stockInventoryHistoryData)
    ? stockInventoryHistoryData
    : [];
  const stockHistoryLoading = false;

  const mediaInputRef = useRef(null);
  // const handleAssignBarcodeChange = (e) => {
  //   setAssignBarcode(e.target.checked);
  //   if (!e.target.checked) setBarcode("");
  // };
  const { Get, GetPaginatedData, Post, Patch, _Get, getAPI, PostPaginatedData } = useApi();
  const { user } = useContext(AuthContext);
  const storeId = user?.store || "";
  const location = useLocation();
  const navigate = useNavigate();
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [showProductViewModal, setShowProductViewModal] = useState(false);
  const [selectedViewProduct, setSelectedViewProduct] = useState(null);
  const [showDeletedProducts, setShowDeletedProducts] = useState(false);
  const [viewProductLoading, setViewProductLoading] = useState(false);
  const { formatCurrency } = useFormattingUtils();
  const infoRef = useRef(null);

  const [showImportResultModal, setShowImportResultModal] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [editingProductId, setEditingProductId] = useState("");
  const [editingProductData, setEditingProductData] = useState(null);
  const [primaryImageFileForUpdate, setPrimaryImageFileForUpdate] =
    useState(null);
  const [primaryImagePreviewForUpdate, setPrimaryImagePreviewForUpdate] =
    useState("");
  const [galleryFilesForUpdate, setGalleryFilesForUpdate] = useState([]);
  const [removedGalleryItemsForUpdate, setRemovedGalleryItemsForUpdate] =
    useState([]);

  const [expandedProductKey, setExpandedProductKey] = useState(null);
  const [inventorySearch, setInventorySearch] = useState("");
  const [productInventoryLots, setProductInventoryLots] = useState([]);
  const [productInventoryLotsLoading, setProductInventoryLotsLoading] = useState(false);
  const [productInventoryLotsRefreshKey, setProductInventoryLotsRefreshKey] = useState(0);

  // Lets the owner set/change the expiry date on an EXISTING stock lot
  // directly from the "All Stock Lots / Movement" table - the Quick Edit
  // Product modal is intentionally scoped to non-stock fields only, so
  // without this there was no way to add expiry after the lot was created.
  const handleEditLotExpiry = async (lot, expiryDateValue) => {
    // `lot.id` is a display-row id - for lots with a segment it's actually
    // the SegmentInventory id (see invMergeGetStockLotsFromHistoryEntry),
    // not the ProductInventory id. `lot.entryId` (or rawEntry.id) always
    // carries the real ProductInventory id, which is what this PATCH must
    // target - using lot.id there caused a 404 ("No ProductInventory
    // matches the given query") whenever a lot had a linked segment.
    const inventoryId = lot?.entryId || lot?.rawEntry?.id || lot?.id;
    if (!inventoryId) {
      toast.error("Could not determine which stock lot to update.");
      throw new Error("Missing ProductInventory id for lot expiry update.");
    }
    try {
      await Patch("productInventoryData", inventoryId, { expiry_date: expiryDateValue || null });
      toast.success("Expiry date updated.");
      setProductInventoryLotsRefreshKey((current) => current + 1);
    } catch (error) {
      console.error("Failed to update lot expiry date:", error);
      toast.error("Failed to update expiry date. Please try again.");
      throw error;
    }
  };

  const [segmentProduct, setSegmentProduct] = useState(null);
  const [segmentSelectedProduct, setSegmentSelectedProduct] = useState(null);
  const [segmentSelectedUom, setSegmentSelectedUom] = useState(null);
  const [segmentSelectedVendor, setSegmentSelectedVendor] = useState(null);
  const [segmentPurchasePrice, setSegmentPurchasePrice] = useState("");
  const [segmentSellingPrice, setSegmentSellingPrice] = useState("");
  const [segmentTotalPcInUom, setSegmentTotalPcInUom] = useState("");
  const [segmentBarcodeValue, setSegmentBarcodeValue] = useState("");
  const [segmentSaving, setSegmentSaving] = useState(false);
  const [segmentUomOptions, setSegmentUomOptions] = useState([]);
  const [segmentVendorOptions, setSegmentVendorOptions] = useState([]);
  const [segmentLookupLoading, setSegmentLookupLoading] = useState(false);
  const [stockLockedProduct, setStockLockedProduct] = useState(null);
  const [inlineStockSaving, setInlineStockSaving] = useState(false);
  const [stockPurchaseInvoiceNo, setStockPurchaseInvoiceNo] = useState("");
  const [stockPurchasePaymentMethod, setStockPurchasePaymentMethod] = useState("cash");
  const [stockPurchaseInvoiceRemark, setStockPurchaseInvoiceRemark] = useState("");
  const [quickEditProduct, setQuickEditProduct] = useState(null);
  const [quickEditTitle, setQuickEditTitle] = useState("");
  const [quickEditCategories, setQuickEditCategories] = useState([]);
  const [quickEditShortDescription, setQuickEditShortDescription] = useState("");
  const [quickEditLongDescription, setQuickEditLongDescription] = useState("");
  const [quickEditHideFromOtherStores, setQuickEditHideFromOtherStores] = useState(false);
  const [quickEditPrimaryFile, setQuickEditPrimaryFile] = useState(null);
  const [quickEditPrimaryPreview, setQuickEditPrimaryPreview] = useState("");
  const [quickEditGalleryFiles, setQuickEditGalleryFiles] = useState([]);
  const [quickEditRemovedGalleryIds, setQuickEditRemovedGalleryIds] = useState([]);
  const [quickEditSaving, setQuickEditSaving] = useState(false);
  const [resolvedPageSize, setResolvedPageSize] = useState(10);


  const PRODUCT_UPDATE_API = "productData";

  useEffect(() => {
    if (currentPage === 1 && Array.isArray(productList) && productList.length > 0) {
      setResolvedPageSize(productList.length);
    }
  }, [currentPage, productList]);


  const openImportResultModal = (result) => {
    setImportResult(result);
    setShowImportResultModal(true);
  };

  const closeImportResultModal = () => {
    setShowImportResultModal(false);
  };

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (
      stringValue.includes(",") ||
      stringValue.includes('"') ||
      stringValue.includes("\n")
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const isBlankValue = (value) => {
    return value === null || value === undefined || String(value).trim() === "";
  };

  const isInvalidNumberValue = (value) => {
    if (isBlankValue(value)) return true;
    const cleaned = String(value).trim();
    const number = Number(cleaned);
    return Number.isNaN(number) || number < 0;
  };

  const getFallbackSelectedCategories = () => {
    const assignedCategories = Array.isArray(
      editingProductData?.categories?.assigned,
    )
      ? editingProductData.categories.assigned
      : Array.isArray(editingProductData?.category)
        ? editingProductData.category
        : [];

    return assignedCategories.map((cat) => ({
      ...cat,
      id: cat?.id || cat?.category_id || cat?.value,
      name: cat?.name || cat?.category_name || cat?.title || "Category",
    }));
  };

  const getEffectiveSelectedCategory = () => {
    if (Array.isArray(selectedCategory) && selectedCategory.length > 0) {
      return selectedCategory;
    }

    if (selectedCategory && !Array.isArray(selectedCategory)) {
      return [selectedCategory];
    }

    return getFallbackSelectedCategories();
  };

  const getFallbackSelectedUom = () => {
    const firstSegment = getFirstSegment(editingProductData);

    if (firstSegment?.uom || firstSegment?.uom_title) {
      return {
        id: firstSegment.uom || firstSegment.uom_title,
        uom_title: firstSegment.uom_title || firstSegment.uom,
      };
    }

    if (editingProductData?.uom || editingProductData?.uom_title) {
      return {
        id: editingProductData.uom || editingProductData.uom_title,
        uom_title: editingProductData.uom_title || editingProductData.uom,
      };
    }

    return null;
  };

  const getEffectiveSelectedUom = () => {
    const hasSelectedUomValue = Boolean(
      selectedUom?.id ||
        selectedUom?.uom_title ||
        selectedUom?.name ||
        typeof selectedUom === "string",
    );

    if (selectedUom) {
      if (hasSelectedUomValue) {
        return selectedUom;
      }
    }

    return getFallbackSelectedUom();
  };

  const getSelectedUomValue = () => {
    const effectiveUom = getEffectiveSelectedUom();
    if (!effectiveUom) return "";
    if (typeof effectiveUom === "string") return effectiveUom;
    return (
      effectiveUom?.id || effectiveUom?.uom_title || effectiveUom?.name || ""
    );
  };

  const getFallbackSelectedVendor = () => {
    if (editingProductData?.vendor || editingProductData?.vendor_name) {
      return {
        id: editingProductData.vendor || editingProductData.vendor_name,
        name: editingProductData.vendor_name || editingProductData.vendor,
      };
    }

    return null;
  };

  const getEffectiveSelectedVendor = () => {
    if (selectedVendorId || selectedVendorName) {
      return {
        id: selectedVendorId || selectedVendorName,
        name: selectedVendorName || selectedVendorId,
      };
    }

    return getFallbackSelectedVendor();
  };

  const getFallbackSelectedWeightUnit = () => {
    if (editingProductData?.weight_unit) {
      return {
        id: editingProductData.weight_unit,
        weight_title: editingProductData.weight_unit,
      };
    }

    return null;
  };

  const getEffectiveSelectedWeightUnit = () => {
    const hasSelectedWeightUnitValue = Boolean(
      selectedWeightUnit?.id ||
        selectedWeightUnit?.weight_title ||
        selectedWeightUnit?.name ||
        typeof selectedWeightUnit === "string",
    );

    if (selectedWeightUnit) {
      if (hasSelectedWeightUnitValue) {
        return selectedWeightUnit;
      }
    }

    return getFallbackSelectedWeightUnit();
  };

  const getOptionId = (value) => {
    if (!value) return "";
    if (typeof value === "string" || typeof value === "number") return value;
    return value.id || value.value || "";
  };

  const getProductId = (productOrId) => {
    if (!productOrId) return "";

    const source = productOrId?.primaryProduct || productOrId;

    if (typeof source === "string" || typeof source === "number") {
      return String(source);
    }

    /**
     * If source is productInventory payload, `id` is inventory-row id,
     * while `product` is the actual product id. For productData payload,
     * `id` is the actual product id. This prevents inline Stock/Segment
     * actions from sending inventory id by mistake.
     */
    const looksLikeInventoryPayload = Boolean(
      source?.product &&
        (source?.product_title ||
          source?.product_category ||
          source?.segment_inventory ||
          source?.total_stock_arrived !== undefined ||
          source?.current_stock !== undefined),
    );

    return String(
      (looksLikeInventoryPayload ? source.product : "") ||
        source.product_id ||
        source.productId ||
        source.id ||
        source.uuid ||
        source.product ||
        "",
    );
  };

  const getActionProductTitle = (productOrGroup) => {
    const source = productOrGroup?.primaryProduct || productOrGroup;
    return invMergeGetTitle(source);
  };

  const getActionProductBarcode = (productOrGroup) => {
    const source = productOrGroup?.primaryProduct || productOrGroup;
    return invMergeGetBarcode(source) || invMergeGetBarcode(productOrGroup?.variants?.[0]);
  };

  const normalizeProductForAction = (productOrGroup) => {
    const source = productOrGroup?.primaryProduct || productOrGroup;
    const productId = getProductId(source);
    const title = getActionProductTitle(productOrGroup);
    const barcodeValue = getActionProductBarcode(productOrGroup);

    if (!productId) return null;

    return {
      ...(source && typeof source === "object" ? source : {}),
      id: productId,
      product_id: productId,
      title,
      product_title: title,
      barcode: barcodeValue,
      product_barcode: barcodeValue,
    };
  };

  const getStockProductOptionLabel = (option) => {
    if (!option) return "";
    const title = invMergeGetTitle(option);
    const barcodeValue = invMergeGetBarcode(option);
    return barcodeValue ? `${title} • ${barcodeValue}` : title;
  };

  const getProductObject = (productOrId) => {
    if (!productOrId) return null;

    if (typeof productOrId === "object") {
      return productOrId;
    }

    const productId = String(productOrId);

    return (
      productList?.find(
        (item) =>
          String(item?.id) === productId ||
          String(item?.product_id) === productId ||
          String(item?.uuid) === productId,
      ) || null
    );
  };

  const getFirstSegment = (product) => {
    return Array.isArray(product?.segments) && product.segments.length > 0
      ? product.segments[0]
      : null;
  };

  const triggerInputHandler = (handler, value) => {
    if (typeof handler === "function") {
      handler({
        target: {
          value: value === null || value === undefined ? "" : value,
        },
      });
    }
  };

  const buildMediaUrl = (url) => {
    if (!url) return "";

    const value = String(url);

    if (
      value.startsWith("http://") ||
      value.startsWith("https://") ||
      value.startsWith("blob:") ||
      value.startsWith("data:")
    ) {
      return value;
    }

    if (value.startsWith("/media/")) {
      return `https://api.paisavara.com${value}`;
    }

    return value;
  };

  const extractProductInventoryPayload = (response) => {
    const payload = response?.data || response || {};
    const results = Array.isArray(payload?.results)
      ? payload.results
      : Array.isArray(payload)
        ? payload
        : [];

    return {
      results,
      next: payload?.next || null,
      count: payload?.count || results.length,
    };
  };

  const fetchAllProductInventoryLots = async () => {
    setProductInventoryLotsLoading(true);

    try {
      const collectedRows = [];
      let page = 1;
      let total = 0;
      let detectedPageSize = 0;
      let safetyCounter = 0;

      while (safetyCounter < 40) {
        const response = await GetPaginatedData("productInventoryData", {
          page,
          search: undefined,
        });

        const results = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.results)
            ? response.results
            : Array.isArray(response)
              ? response
              : [];

        total = Number(response?.totalCount || response?.count || total || results.length || 0);
        detectedPageSize = detectedPageSize || results.length || 10;

        collectedRows.push(...results);

        const reachedTotal = total > 0 && collectedRows.length >= total;
        const noMoreRows = results.length === 0;
        const lastShortPage = results.length > 0 && detectedPageSize > 0 && results.length < detectedPageSize;

        if (reachedTotal || noMoreRows || lastShortPage) {
          break;
        }

        page += 1;
        safetyCounter += 1;
      }

      setProductInventoryLots(invMergeUniqueRowsByKey(collectedRows));
    } catch (error) {
      console.error("Failed to fetch complete product inventory lots:", error);
      setProductInventoryLots([]);
    } finally {
      setProductInventoryLotsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllProductInventoryLots();
  }, [productInventoryLotsRefreshKey]);

  const normalizeExistingGalleryItems = (product) => {
    const gallery = Array.isArray(product?.media_gallery)
      ? product.media_gallery
      : [];

    return gallery
      .map((item, index) => {
        const image = item?.image || "";
        const video = item?.video || "";
        const url = buildMediaUrl(image || video);

        if (!url) return null;

        return {
          ...item,
          id: item?.id || item?.gallery_id || `existing-${index}`,
          url,
          type: video ? "video" : "image",
          source: "existing",
        };
      })
      .filter(Boolean)
      .filter(
        (item) =>
          !removedGalleryItemsForUpdate.includes(
            item.id || item.gallery_id || item.image_id,
          ),
      );
  };

  const normalizeControllerGalleryItems = () => {
    return Array.isArray(galleryPreview)
      ? galleryPreview
          .map((item, index) => {
            const url = buildMediaUrl(item?.url || item?.image || item?.video);
            return {
              ...item,
              url,
              type:
                item?.type ||
                (item?.video || String(url).match(/\.(mp4|mov|webm)$/i)
                  ? "video"
                  : "image"),
              source: "controller",
              controllerIndex: index,
            };
          })
          .filter((item) => item.url)
      : [];
  };

  const getDisplayImagePreview = () => {
    return (
      primaryImagePreviewForUpdate ||
      imagePreview ||
      buildMediaUrl(editingProductData?.product_image)
    );
  };

  const getDisplayGalleryPreview = () => {
    const existingItems = normalizeExistingGalleryItems(editingProductData);
    const controllerItems = normalizeControllerGalleryItems();

    const controllerHasExistingServerItems = controllerItems.some(
      (item) => item.id || String(item.url || "").includes("/media/"),
    );

    if (controllerHasExistingServerItems) {
      return controllerItems.filter(
        (item) =>
          !removedGalleryItemsForUpdate.includes(
            item.id || item.gallery_id || item.image_id,
          ),
      );
    }

    return [...existingItems, ...controllerItems];
  };

  const applyEditFallbackValues = (product) => {
    if (!product) return;

    const firstSegment = getFirstSegment(product);

    triggerInputHandler(
      handleProductTitleChange,
      product.title || product.product_name || "",
    );
    triggerInputHandler(
      handlePurchasePriceChange,
      firstSegment?.purchase_price ||
        product.purchase_price ||
        product.purchasePrice ||
        "",
    );
    triggerInputHandler(
      handleSellingPriceChange,
      firstSegment?.selling_price ||
        product.selling_price ||
        product.sellingPrice ||
        "",
    );
    triggerInputHandler(
      handleOpeningInventoryChange,
      product.opening_inventory || product.openingInventory || "",
    );
    triggerInputHandler(handleBarcodeChange, product.barcode || "");

    setShortDescription(product.short_description || "");
    setLongDescription(product.long_description || "");
    setWeightValue(
      product.weight_value !== null && product.weight_value !== undefined
        ? String(product.weight_value)
        : "",
    );

    if (product.barcode) {
      setAutoGenerateBarcode(false);
    }

    const assignedCategories = Array.isArray(product?.categories?.assigned)
      ? product.categories.assigned
      : Array.isArray(product?.category)
        ? product.category
        : [];

    if (assignedCategories.length > 0) {
      setSelectedCategory(assignedCategories);
    }

    if (firstSegment?.uom || firstSegment?.uom_title) {
      setSelectedUom({
        id: firstSegment.uom,
        uom_title: firstSegment.uom_title || firstSegment.uom,
      });
    } else if (product?.uom || product?.uom_title) {
      setSelectedUom({
        id: product.uom,
        uom_title: product.uom_title || product.uom,
      });
    }

    if (product?.vendor || product?.vendor_name) {
      setSelectedVendorId(product.vendor || "");
      setSelectedVendorName(product.vendor_name || "");
    } else {
      setSelectedVendorId("");
      setSelectedVendorName("");
    }

    if (product?.weight_unit) {
      setSelectedWeightUnit({
        id: product.weight_unit,
        weight_title: product.weight_unit,
      });
    } else {
      setSelectedWeightUnit(null);
    }
  };

  const fetchProductDetailForEdit = async (productId) => {
    if (!productId || !_Get || !getAPI) return null;

    try {
      const apiPath = `${getAPI(PRODUCT_UPDATE_API)}${productId}`;
      const detail = await _Get(apiPath);
      return detail?.data || detail || null;
    } catch (error) {
      console.warn("Could not fetch product detail for edit fallback:", error);
      return null;
    }
  };

  const handleTableEditProduct = async (productOrId) => {
    const productId = getProductId(productOrId);
    const product = getProductObject(productOrId);

    setEditingProductId(productId);
    setEditingProductData(product);
    setPrimaryImageFileForUpdate(null);
    setPrimaryImagePreviewForUpdate("");
    setGalleryFilesForUpdate([]);
    setRemovedGalleryItemsForUpdate([]);

    /**
     * Keep original controller edit flow exactly as before so title/image and
     * other values that already worked continue to work.
     */
    if (typeof handleEditProduct === "function") {
      handleEditProduct(productOrId);
    }

    /**
     * Fill from table row immediately.
     */
    if (product) {
      applyEditFallbackValues(product);
      setTimeout(() => applyEditFallbackValues(product), 150);
      setTimeout(() => applyEditFallbackValues(product), 500);
      setTimeout(() => applyEditFallbackValues(product), 1000);
    }

    /**
     * If ProductTable only sent id OR row object has incomplete data,
     * fetch product detail and apply category/uom/vendor/weight/gallery fallback.
     */
    if (productId) {
      const productDetail = await fetchProductDetailForEdit(productId);

      if (productDetail) {
        setEditingProductData(productDetail);
        applyEditFallbackValues(productDetail);
        setTimeout(() => applyEditFallbackValues(productDetail), 150);
        setTimeout(() => applyEditFallbackValues(productDetail), 500);
      }
    }
  };


  const getQuickEditExistingGallery = (product) => {
    const gallery = Array.isArray(product?.media_gallery) ? product.media_gallery : [];
    return gallery
      .map((item, index) => {
        const url = buildMediaUrl(item?.image || item?.video || item?.url);
        if (!url) return null;
        return {
          ...item,
          id: item?.id || item?.gallery_id || item?.image_id || `existing-${index}`,
          url,
          type: item?.video ? "video" : "image",
          source: "existing",
        };
      })
      .filter(Boolean)
      .filter((item) => !quickEditRemovedGalleryIds.includes(item.id));
  };

  const openQuickEditProduct = (product) => {
    if (!product) return;

    const normalizedProduct = product?.primaryProduct || product;
    setQuickEditProduct(normalizedProduct);
    setEditingProductId(getProductId(normalizedProduct));
    setEditingProductData(normalizedProduct);
    setQuickEditTitle(normalizedProduct?.title || normalizedProduct?.product_name || "");
    setQuickEditCategories(getFallbackSelectedCategoriesFromProduct(normalizedProduct));
    setQuickEditShortDescription(normalizedProduct?.short_description || "");
    setQuickEditLongDescription(normalizedProduct?.long_description || "");
    setQuickEditHideFromOtherStores(Boolean(normalizedProduct?.hide_from_other_stores));
    setQuickEditPrimaryFile(null);
    setQuickEditPrimaryPreview(buildMediaUrl(normalizedProduct?.product_image));
    setQuickEditGalleryFiles([]);
    setQuickEditRemovedGalleryIds([]);
  };

  const closeQuickEditProduct = () => {
    setQuickEditProduct(null);
    setQuickEditPrimaryFile(null);
    setQuickEditPrimaryPreview("");
    setQuickEditGalleryFiles([]);
    setQuickEditRemovedGalleryIds([]);
    setQuickEditSaving(false);
  };

  const getFallbackSelectedCategoriesFromProduct = (product) => {
    const assignedCategories = Array.isArray(product?.categories?.assigned)
      ? product.categories.assigned
      : Array.isArray(product?.category)
        ? product.category
        : [];

    return assignedCategories.map((cat) => ({
      ...cat,
      id: cat?.id || cat?.category_id || cat?.value,
      name: cat?.name || cat?.category_name || cat?.title || "Category",
    }));
  };

  const handleQuickEditPrimaryImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setQuickEditPrimaryFile(file);
    setQuickEditPrimaryPreview(file ? URL.createObjectURL(file) : buildMediaUrl(quickEditProduct?.product_image));
  };

  const handleQuickEditGalleryUpload = (event) => {
    const files = Array.from(event.target.files || []).filter((file) => file instanceof File);
    setQuickEditGalleryFiles((prev) => [...prev, ...files]);
    event.target.value = "";
  };

  const removeQuickEditGalleryFile = (index) => {
    setQuickEditGalleryFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const removeQuickEditExistingGallery = (item) => {
    if (!item?.id) return;
    setQuickEditRemovedGalleryIds((prev) => [...prev, item.id]);
  };

  const handleQuickEditSubmit = async () => {
    const productId = getProductId(quickEditProduct);

    if (!productId) {
      toast.error("Product id not found. Please close and try again.");
      return;
    }

    if (isBlankValue(quickEditTitle)) {
      toast.error("Product title is required.");
      return;
    }

    const formPayload = new FormData();
    appendIfNotBlank(formPayload, "title", quickEditTitle);
    appendIfNotBlank(formPayload, "product_title", quickEditTitle);
    appendIfNotBlank(formPayload, "short_description", quickEditShortDescription);
    appendIfNotBlank(formPayload, "long_description", quickEditLongDescription);
    formPayload.append("hide_from_other_stores", quickEditHideFromOtherStores ? "true" : "false");

    (Array.isArray(quickEditCategories) ? quickEditCategories : []).forEach((category) => {
      const categoryId = getOptionId(category);
      if (categoryId) {
        formPayload.append("categories", categoryId);
        formPayload.append("category", categoryId);
      }
    });

    if (quickEditPrimaryFile instanceof File) {
      formPayload.append("product_image", quickEditPrimaryFile);
    }

    quickEditGalleryFiles.forEach((file) => {
      if (file instanceof File) {
        formPayload.append("gallery_images", file);
        formPayload.append("media_gallery", file);
        formPayload.append("media_gallery_images", file);
      }
    });

    if (quickEditRemovedGalleryIds.length > 0) {
      formPayload.append("removed_gallery_ids", JSON.stringify(quickEditRemovedGalleryIds));
      formPayload.append("delete_gallery_ids", JSON.stringify(quickEditRemovedGalleryIds));
    }

    try {
      setQuickEditSaving(true);
      const response = await Patch(PRODUCT_UPDATE_API, productId, formPayload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response?.status === 200 || response?.status === 201 || response?.data) {
        toast.success("Product updated successfully.");
        closeQuickEditProduct();
        if (typeof handleDataUpdate === "function") {
          await handleDataUpdate();
        }
        setProductInventoryLotsRefreshKey((current) => current + 1);
      } else {
        toast.error("Product update failed.");
      }
    } catch (error) {
      console.error("Product quick update failed:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Product update failed.",
      );
    } finally {
      setQuickEditSaving(false);
    }
  };

  const resetInlineSegmentForm = () => {
    setSegmentSelectedProduct(null);
    setSegmentSelectedUom(null);
    setSegmentSelectedVendor(null);
    setSegmentPurchasePrice("");
    setSegmentSellingPrice("");
    setSegmentTotalPcInUom("");
    setSegmentBarcodeValue("");
    setSegmentSaving(false);
  };

  const openGlobalSegmentModal = () => {
    setSegmentProduct(null);
    resetInlineSegmentForm();
    setShowSegmentModal(true);
  };

  const openSegmentForProduct = (product) => {
    const normalizedProduct = product?.primaryProduct || product;
    setSegmentProduct(normalizedProduct || null);
    setSegmentSelectedProduct(normalizedProduct || null);
    setSegmentSelectedUom(null);
    setSegmentSelectedVendor(null);
    setSegmentPurchasePrice("");
    setSegmentSellingPrice("");
    setSegmentTotalPcInUom("");
    setSegmentBarcodeValue(normalizedProduct?.barcode || "");
    setSegmentSaving(false);
    setShowSegmentModal(true);
  };

  const closeInlineSegmentModal = () => {
    setShowSegmentModal(false);
    setSegmentProduct(null);
    resetInlineSegmentForm();
  };

  const loadSegmentLookupOptions = async () => {
    try {
      setSegmentLookupLoading(true);
      const [uomResponse, vendorResponse] = await Promise.all([
        Get("uomData"),
        Get("vendorDepartmentData"),
      ]);

      setSegmentUomOptions(normalizeApiListResponse(uomResponse));
      setSegmentVendorOptions(
        normalizeApiListResponse(vendorResponse).filter(
          (vendor) => vendor?.type === "purchase" && !vendor?.is_deleted,
        ),
      );
    } catch (error) {
      console.error("Failed to load segment lookup options:", error);
      setSegmentVendorOptions(
        Array.isArray(stockVendors)
          ? stockVendors.filter((vendor) => vendor?.type === "purchase" && !vendor?.is_deleted)
          : [],
      );
    } finally {
      setSegmentLookupLoading(false);
    }
  };

  useEffect(() => {
    if (showSegmentModal) {
      loadSegmentLookupOptions();
    }
  }, [showSegmentModal]);

  const handleStockHistoryClick = () => {
    navigate("/stock-history");
  };

  const openGlobalStockModal = () => {
    setStockLockedProduct(null);

    if (typeof stockResetForm === "function") {
      stockResetForm();
    }

    if (typeof handleOpenStockModal === "function") {
      handleOpenStockModal();
    }
  };

  const closeStockModal = () => {
    setStockLockedProduct(null);
    setStockPurchaseInvoiceNo("");
    setStockPurchasePaymentMethod("cash");
    setStockPurchaseInvoiceRemark("");

    if (typeof handleCloseStockModal === "function") {
      handleCloseStockModal();
    }
  };

  const openStockForProduct = (product) => {
    const normalizedProduct = normalizeProductForAction(product);

    if (typeof stockResetForm === "function") {
      stockResetForm();
    }

    if (normalizedProduct) {
      setStockLockedProduct(normalizedProduct);

      if (typeof stockSetSelectedProduct === "function") {
        stockSetSelectedProduct(normalizedProduct);
      }

      if (typeof stockSetProductTitle === "function") {
        stockSetProductTitle(normalizedProduct.title || normalizedProduct.product_title || "");
      }

      if (typeof stockSetSearchQuery === "function") {
        stockSetSearchQuery(normalizedProduct.barcode || normalizedProduct.title || "");
      }

      if (typeof stockFetchProductList === "function") {
        stockFetchProductList(normalizedProduct.barcode || normalizedProduct.title || "");
      }

      if (typeof stockHandleBarcodeChange === "function" && normalizedProduct.barcode) {
        stockHandleBarcodeChange({ target: { value: normalizedProduct.barcode } });
      }

      setTimeout(() => {
        if (typeof stockSetSelectedProduct === "function") {
          stockSetSelectedProduct(normalizedProduct);
        }
        if (typeof stockSetProductTitle === "function") {
          stockSetProductTitle(normalizedProduct.title || normalizedProduct.product_title || "");
        }
      }, 0);
    }

    if (typeof handleOpenStockModal === "function") {
      handleOpenStockModal();
    }
  };

  const getInlineSegmentProduct = () => {
    return segmentProduct || segmentSelectedProduct;
  };

  const handleInlineSegmentSubmit = async () => {
    const selectedProductForSegment = getInlineSegmentProduct();
    const productId = getProductId(selectedProductForSegment);
    const uomId = getOptionId(segmentSelectedUom);
    const vendorId = getOptionId(segmentSelectedVendor);

    const missingFields = [];

    if (!productId) missingFields.push("Product");
    if (!uomId) missingFields.push("UOM");
    if (isInvalidNumberValue(segmentPurchasePrice)) missingFields.push("Purchase Price");
    if (isInvalidNumberValue(segmentSellingPrice)) missingFields.push("Selling Price");
    if (isInvalidNumberValue(segmentTotalPcInUom)) missingFields.push("Total PC in UOM");

    if (missingFields.length > 0) {
      toast.error(`Please fill mandatory segment fields: ${missingFields.join(", ")}`);
      return;
    }

    const payload = {
      store: storeId,
      product: productId,
      purchase_price: parseFloat(segmentPurchasePrice).toFixed(2),
      selling_price: parseFloat(segmentSellingPrice).toFixed(2),
      uom: uomId,
      total_pc_in_uom: segmentTotalPcInUom,
      segment_barcode: segmentBarcodeValue || undefined,
    };

    if (vendorId) {
      payload.vendor = vendorId;
      payload.vendor_name =
        segmentSelectedVendor?.name ||
        segmentSelectedVendor?.vendor_name ||
        segmentSelectedVendor?.label ||
        "";
    }

    try {
      setSegmentSaving(true);
      const response = await Post("segmentData", payload);

      if (response?.status === 200 || response?.status === 201 || response?.data || response) {
        toast.success("Product segment added successfully.");
        closeInlineSegmentModal();

        if (typeof handleDataUpdate === "function") {
          await handleDataUpdate();
        }
      } else {
        toast.error("Unable to add product segment.");
      }
    } catch (error) {
      console.error("Add product segment failed:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to add product segment.",
      );
    } finally {
      setSegmentSaving(false);
    }
  };


  const handlePrimaryImageChangeWithTracking = (e) => {
    const file = e.target.files?.[0] || null;
    setPrimaryImageFileForUpdate(file);

    if (primaryImagePreviewForUpdate) {
      URL.revokeObjectURL(primaryImagePreviewForUpdate);
    }

    setPrimaryImagePreviewForUpdate(file ? URL.createObjectURL(file) : "");

    if (typeof handleProductImageChange === "function") {
      handleProductImageChange(e);
    }
  };

  const handleRemovePrimaryImageWithTracking = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setPrimaryImageFileForUpdate(null);

    if (primaryImagePreviewForUpdate) {
      URL.revokeObjectURL(primaryImagePreviewForUpdate);
      setPrimaryImagePreviewForUpdate("");
    }

    if (typeof handleRemovePrimaryImage === "function") {
      handleRemovePrimaryImage();
    }
  };

  const handleGalleryUploadWithTracking = (e) => {
    const files = Array.from(e.target.files || []).filter(
      (file) => file instanceof File,
    );

    if (files.length > 0) {
      setGalleryFilesForUpdate((prev) => [...prev, ...files]);
    }

    if (typeof handleGalleryUpload === "function") {
      handleGalleryUpload(e);
    }

    if (mediaInputRef?.current) {
      mediaInputRef.current.value = "";
    }
  };

  const handleRemoveDisplayGalleryImage = (item) => {
    if (!item) return;

    const itemId = item.id || item.gallery_id || item.image_id;

    if (item.source === "existing") {
      if (itemId) {
        setRemovedGalleryItemsForUpdate((prev) => [...prev, itemId]);
        return;
      }
    }

    if (item.source === "controller") {
      if (typeof removeGalleryImage === "function") {
        removeGalleryImage(item.controllerIndex);
      }
    }

    if (item?.file instanceof File) {
      setGalleryFilesForUpdate((prev) =>
        prev.filter((file) => file !== item.file),
      );
    }
  };

  const appendIfNotBlank = (formData, key, value) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      formData.append(key, value);
    }
  };

  const getSelectedCategoryIds = () => {
    const effectiveCategory = getEffectiveSelectedCategory();

    if (!effectiveCategory) return [];

    if (Array.isArray(effectiveCategory)) {
      return effectiveCategory.map((item) => getOptionId(item)).filter(Boolean);
    }

    const singleId = getOptionId(effectiveCategory);
    return singleId ? [singleId] : [];
  };

  const buildProductUpdateFormData = () => {
    const formData = new FormData();

    const productId = editingProductId || getProductId(editingProductData);
    const selectedCategoryIds = getSelectedCategoryIds();
    const uomId = getSelectedUomValue();
    const weightUnitId = getOptionId(getEffectiveSelectedWeightUnit());
    const effectiveVendor = getEffectiveSelectedVendor();
    const firstSegment = getFirstSegment(editingProductData);

    appendIfNotBlank(formData, "title", productTitle);
    appendIfNotBlank(formData, "product_title", productTitle);

    appendIfNotBlank(formData, "purchase_price", purchasePrice);
    appendIfNotBlank(formData, "purchasePrice", purchasePrice);

    appendIfNotBlank(formData, "selling_price", sellingPrice);
    appendIfNotBlank(formData, "sellingPrice", sellingPrice);

    appendIfNotBlank(formData, "opening_inventory", openingInventory);
    appendIfNotBlank(formData, "openingInventory", openingInventory);

    appendIfNotBlank(formData, "total_pc_in_uom", totalPcInUom);
    appendIfNotBlank(formData, "totalPcInUom", totalPcInUom);

    appendIfNotBlank(formData, "short_description", shortDescription);
    appendIfNotBlank(formData, "long_description", longDescription);
    appendIfNotBlank(formData, "weight_value", weightValue);

    appendIfNotBlank(formData, "barcode", barcode);
    appendIfNotBlank(formData, "uom", uomId);
    appendIfNotBlank(formData, "vendor", effectiveVendor?.id || "");
    appendIfNotBlank(formData, "weight_unit", weightUnitId);

    if (firstSegment?.id) {
      formData.append("segment_id", firstSegment.id);
    }

    /**
     * Important:
     * Backend expects categories as UUID values, not JSON-string array.
     *
     * Wrong:
     * categories = '["id1","id2"]'
     *
     * Correct multipart:
     * categories = id1
     * categories = id2
     */
    selectedCategoryIds.forEach((categoryId) => {
      formData.append("categories", categoryId);
      formData.append("category", categoryId);
    });

    if (primaryImageFileForUpdate instanceof File) {
      formData.append("product_image", primaryImageFileForUpdate);
    }

    galleryFilesForUpdate.forEach((file) => {
      if (file instanceof File) {
        formData.append("gallery_images", file);
        formData.append("media_gallery", file);
        formData.append("media_gallery_images", file);
      }
    });

    if (removedGalleryItemsForUpdate.length > 0) {
      formData.append(
        "removed_gallery_ids",
        JSON.stringify(removedGalleryItemsForUpdate),
      );
      formData.append(
        "delete_gallery_ids",
        JSON.stringify(removedGalleryItemsForUpdate),
      );
    }

    return {
      productId,
      formData,
    };
  };

  const handleUpdateProductWithMedia = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!validateAddProductMandatoryFields()) {
      return;
    }

    const { productId, formData } = buildProductUpdateFormData();

    if (!productId || productId === "[object Object]") {
      toast.error(
        "Product id not found. Please close edit popup and open again.",
      );
      console.error("Invalid product id for update:", {
        editingProductId,
        editingProductData,
      });
      return;
    }

    try {
      const response = await Patch(PRODUCT_UPDATE_API, productId, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (
        response?.status === 200 ||
        response?.status === 201 ||
        response?.data
      ) {
        toast.success("Product updated successfully.");

        setPrimaryImageFileForUpdate(null);
        setPrimaryImagePreviewForUpdate("");
        setGalleryFilesForUpdate([]);
        setRemovedGalleryItemsForUpdate([]);

        if (typeof handleDataUpdate === "function") {
          await handleDataUpdate();
        }

        if (typeof handleCloseModal === "function") {
          handleCloseModal();
        }
      } else {
        toast.error("Product update failed.");
      }
    } catch (error) {
      console.error("Product update failed:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Product update failed.",
      );
    }
  };

  const validateAddProductMandatoryFields = () => {
    const missingFields = [];

    if (isBlankValue(productTitle)) {
      missingFields.push("Product Title");
    }

    if (isBlankValue(getSelectedUomValue())) {
      missingFields.push("UOM");
    }

    if (isInvalidNumberValue(purchasePrice)) {
      missingFields.push("Purchase Price");
    }

    if (isInvalidNumberValue(sellingPrice)) {
      missingFields.push("Selling Price");
    }

    if (missingFields.length > 0) {
      toast.error(`Please fill mandatory fields: ${missingFields.join(", ")}`);
      return false;
    }

    return true;
  };

  const handleValidatedAddProduct = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!validateAddProductMandatoryFields()) {
      return;
    }

    if (isEditMode) {
      handleUpdateProductWithMedia(e);
      return;
    }

    handleAddProduct(e);
  };

  const parseCsvLine = (line) => {
    const result = [];
    let current = "";
    let insideQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"' && insideQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === "," && !insideQuotes) {
        result.push(current);
        current = "";
      } else {
        current += char;
      }
    }

    result.push(current);
    return result.map((item) => item.trim());
  };

  const normalizeCsvHeader = (value) => {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/_/g, " ");
  };

  const findCsvColumnIndex = (headers, possibleNames) => {
    const normalizedHeaders = headers.map(normalizeCsvHeader);
    const normalizedPossibleNames = possibleNames.map(normalizeCsvHeader);

    return normalizedHeaders.findIndex((header) =>
      normalizedPossibleNames.includes(header),
    );
  };

  const validateCsvMandatoryFields = async (file) => {
    const text = await file.text();
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line !== "");

    if (lines.length <= 1) {
      return {
        isValid: false,
        totalRows: 0,
        errors: [
          {
            row: "-",
            title: file.name,
            error: "CSV file is empty or does not contain product rows.",
          },
        ],
      };
    }

    const headers = parseCsvLine(lines[0]);

    const columnMap = {
      productTitle: findCsvColumnIndex(headers, [
        "Product Title",
        "Product Name",
        "Title",
        "product_title",
        "product_name",
        "name",
      ]),
      uom: findCsvColumnIndex(headers, [
        "UOM",
        "Uom",
        "Unit",
        "Unit Of Measurement",
        "uom_title",
        "uom",
      ]),
      purchasePrice: findCsvColumnIndex(headers, [
        "Purchase Price",
        "PurchasePrice",
        "Cost Price",
        "purchase_price",
        "purchasePrice",
      ]),
      sellingPrice: findCsvColumnIndex(headers, [
        "Selling Price",
        "SellingPrice",
        "Sale Price",
        "selling_price",
        "sellingPrice",
      ]),
    };

    const missingColumns = [];

    if (columnMap.productTitle === -1) missingColumns.push("Product Title");
    if (columnMap.uom === -1) missingColumns.push("UOM");
    if (columnMap.purchasePrice === -1) missingColumns.push("Purchase Price");
    if (columnMap.sellingPrice === -1) missingColumns.push("Selling Price");

    if (missingColumns.length > 0) {
      return {
        isValid: false,
        totalRows: lines.length - 1,
        errors: [
          {
            row: "Header",
            title: file.name,
            error: `Missing mandatory CSV column(s): ${missingColumns.join(", ")}`,
          },
        ],
      };
    }

    const validationErrors = [];

    for (let i = 1; i < lines.length; i += 1) {
      const values = parseCsvLine(lines[i]);
      const rowNumber = i;
      const title = values[columnMap.productTitle] || "-";

      const rowErrors = [];

      if (isBlankValue(values[columnMap.productTitle])) {
        rowErrors.push("Product Title is required");
      }

      if (isBlankValue(values[columnMap.uom])) {
        rowErrors.push("UOM is required");
      }

      if (isInvalidNumberValue(values[columnMap.purchasePrice])) {
        rowErrors.push("Purchase Price is required and must be a valid number");
      }

      if (isInvalidNumberValue(values[columnMap.sellingPrice])) {
        rowErrors.push("Selling Price is required and must be a valid number");
      }

      if (rowErrors.length > 0) {
        validationErrors.push({
          row: rowNumber,
          title,
          error: rowErrors.join(", "),
        });
      }
    }

    return {
      isValid: validationErrors.length === 0,
      totalRows: lines.length - 1,
      errors: validationErrors,
    };
  };

  const downloadImportErrorReport = () => {
    if (!importResult?.errors?.length) return;

    const headers = ["Row", "Title", "Error"];
    const rows = importResult.errors.map((item) => [
      item.row || "",
      item.title || "",
      item.error || "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "product_import_error_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * CSV Import Fix
   *
   * Backend returns 200 OK even when import has row-level errors.
   * So do not show success based only on HTTP status.
   * Check created_count and error_count from response body.
   *
   * Large file support:
   * If user uploads 1000 records and 300 fail, show a modal with:
   * - Total processed rows
   * - Created count
   * - Failed count
   * - Full failed row list
   * - Error report CSV download
   */
  const handleImportProducts = async (file) => {
    if (!file) {
      toast.error("Please select a CSV file.");
      return;
    }

    const isCsv =
      file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");

    if (!isCsv) {
      toast.error("Only CSV files are allowed.");

      if (fileInputRef?.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    let csvValidationResult;

    try {
      csvValidationResult = await validateCsvMandatoryFields(file);
    } catch (csvReadError) {
      console.error("CSV validation error:", csvReadError);
      toast.error("Unable to read CSV file for validation.");
      return;
    }

    if (!csvValidationResult.isValid) {
      openImportResultModal({
        status: "failed",
        message:
          "CSV validation failed. Please fix mandatory field errors and upload again.",
        fileName: file.name,
        totalProcessedRows: csvValidationResult.totalRows || 0,
        createdCount: 0,
        errorCount: csvValidationResult.errors.length,
        skippedOrUnknownCount: 0,
        created: [],
        errors: csvValidationResult.errors,
        rawResponse: csvValidationResult,
      });

      toast.error(
        `CSV validation failed. ${csvValidationResult.errors.length} row(s) have missing mandatory fields.`,
      );

      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await Post("importBulkProduct", formData);
      const data = response?.data || response || {};

      const createdCount = Number(data.created_count || 0);
      const errorCount = Number(data.error_count || 0);
      const createdList = Array.isArray(data.created) ? data.created : [];
      const errorsList = Array.isArray(data.errors) ? data.errors : [];

      const totalProcessedRows =
        Number(data.total_count || data.total_rows || 0) ||
        createdCount + errorCount;

      const skippedOrUnknownCount = Math.max(
        totalProcessedRows - createdCount - errorCount,
        0,
      );

      const importStatus =
        (createdCount > 0 && errorCount === 0)
          ? "success"
          : (createdCount > 0 && errorCount > 0)
            ? "partial"
            : "failed";

      const resultPayload = {
        status: importStatus,
        message: data.message || "Bulk import completed",
        fileName: file.name,
        totalProcessedRows,
        createdCount,
        errorCount,
        skippedOrUnknownCount,
        created: createdList,
        errors: errorsList,
        rawResponse: data,
      };

      openImportResultModal(resultPayload);

      /**
       * Toast should be only short summary.
       * Full details are shown in modal.
       */
      if (importStatus === "success") {
        toast.success(`Imported ${createdCount} product(s) successfully.`);
      } else if (importStatus === "partial") {
        toast.warning(
          `Imported ${createdCount} product(s), ${errorCount} row(s) failed.`,
        );
      } else {
        toast.error(
          `Import failed. ${errorCount || "No"} row(s) failed and no products were created.`,
        );
      }

      if (createdCount > 0 && typeof handleDataUpdate === "function") {
        await handleDataUpdate();
      }
    } catch (error) {
      console.error("CSV import error:", error);

      const apiErrors = error?.response?.data?.errors;
      const errorsList = Array.isArray(apiErrors) ? apiErrors : [];
      const firstApiError = errorsList[0];

      openImportResultModal({
        status: "failed",
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to import products.",
        fileName: file.name,
        totalProcessedRows: errorsList.length,
        createdCount: 0,
        errorCount: errorsList.length || 1,
        skippedOrUnknownCount: 0,
        created: [],
        errors:
          errorsList.length > 0
            ? errorsList
            : [
                {
                  row: "-",
                  title: file.name,
                  error:
                    firstApiError?.error ||
                    error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    error?.message ||
                    "Failed to import products.",
                },
              ],
        rawResponse: error?.response?.data || error,
      });

      toast.error(
        firstApiError?.error ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to import products.",
      );
    } finally {
      /**
       * Reset file input so the same CSV can be selected again after fixing it.
       */
      if (fileInputRef?.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  useEffect(() => {
    if (location.state?.openAddModal) {
      if (location.state.barcode) {
        handleBarcodeChange({ target: { value: location.state.barcode } });
        setAutoGenerateBarcode(false);
      }
      handleOpenModal();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  useEffect(() => {
    if (!infoRef.current) return;

    const pop = new Popover(infoRef.current, {
      html: true,
      trigger: "hover focus click",
      placement: "bottom",
      container: "body",
      sanitize: false,
    });

    return () => pop.dispose();
  }, []);

  const columns = [
    { header: "Image", field: "product_image" },
    { header: "Product Title", field: "combinedField" },
    { header: "Category", field: "category" },
    { header: "UOM", field: "UOM" },
    { header: "Purchase Price", field: "purchasePrice" },
    { header: "Selling Price", field: "sellingPrice" },
    { header: "Barcode", field: "barcode" },
    { header: "View Details", field: "customView" },
  ];

  const getProductTableUomValue = (product) => {
    const firstSegment = getFirstSegment(product);

    return (
      product?.UOM ||
      product?.uom ||
      product?.uom_title ||
      product?.unit ||
      product?.unit_name ||
      firstSegment?.uom_title ||
      firstSegment?.uom ||
      ""
    );
  };

  const getProductTablePurchasePriceValue = (product) => {
    const firstSegment = getFirstSegment(product);

    return (
      product?.purchasePrice ||
      product?.purchase_price ||
      product?.cost_price ||
      firstSegment?.purchase_price ||
      firstSegment?.purchase_price_per_pc ||
      ""
    );
  };

  const getProductTableSellingPriceValue = (product) => {
    const firstSegment = getFirstSegment(product);

    return (
      product?.sellingPrice ||
      product?.selling_price ||
      product?.sale_price ||
      firstSegment?.selling_price ||
      firstSegment?.selling_price_per_pc ||
      ""
    );
  };

  /**
   * Soft deleted rule requested:
   * If UOM, Purchase Price and Selling Price columns have no data,
   * then product should be treated as soft deleted.
   */
  const isProductSoftDeletedByMissingPriceInfo = (product) => {
    return (
      isBlankValue(getProductTableUomValue(product)) &&
      isBlankValue(getProductTablePurchasePriceValue(product)) &&
      isBlankValue(getProductTableSellingPriceValue(product))
    );
  };

  const activeProductList = Array.isArray(productList)
    ? productList.filter((product) => !isProductSoftDeletedByMissingPriceInfo(product))
    : [];

  const softDeletedProductList = Array.isArray(productList)
    ? productList.filter((product) => isProductSoftDeletedByMissingPriceInfo(product))
    : [];

  const visibleProductList = showDeletedProducts
    ? [...activeProductList, ...softDeletedProductList]
    : activeProductList;

  const getEnhancedTableData = (list) => {
    return (Array.isArray(list) ? list : []).map((product) => {
      const isSoftDeleted = isProductSoftDeletedByMissingPriceInfo(product);

      return {
        ...product,
        __softDeleted: isSoftDeleted,

        /**
         * Use customView instead of view.
         *
         * Reason:
         * ProductTable may have built-in behavior for field="view".
         * That built-in view opens a different layout for active products.
         * customView is a normal field, so it renders our same View Details button
         * for both active and soft-deleted products.
         */
        customView: (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleViewProduct(product);
            }}
            style={{
              border: "none",
              background: isSoftDeleted ? "#fef2f2" : "#eff6ff",
              color: isSoftDeleted ? "#b91c1c" : "#1d4ed8",
              borderRadius: "999px",
              padding: "7px 12px",
              fontWeight: 800,
              fontSize: "12px",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            View Details
          </button>
        ),

        /**
         * Keep view null to avoid ProductTable default view modal/layout.
         */
        view: null,
      };
    });
  };

  const getValue = (value, fallback = "-") => {
    if (value === null || value === undefined || value === "") return fallback;
    return value;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const number = Number(value);
    if (Number.isNaN(number)) return value;
    return number.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAssignedCategories = (product) => {
    return Array.isArray(product?.categories?.assigned)
      ? product.categories.assigned
      : [];
  };

  const getCategoryNames = (product) => {
    const assigned = getAssignedCategories(product);
    return assigned.length > 0
      ? assigned.map((category) => category?.name).filter(Boolean).join(", ")
      : "-";
  };

  const getCategoryBreadcrumbs = (product) => {
    const breadcrumbs = Array.isArray(product?.categories?.breadcrumb)
      ? product.categories.breadcrumb
      : [];

    if (breadcrumbs.length === 0) return [];

    return breadcrumbs.map((path) =>
      Array.isArray(path)
        ? path.map((item) => item?.name).filter(Boolean).join(" / ")
        : "",
    ).filter(Boolean);
  };

  const getViewSegments = (product) => {
    return Array.isArray(product?.segments) ? product.segments : [];
  };

  const getPrimarySegmentForView = (product) => {
    const segments = getViewSegments(product);
    return segments.length > 0 ? segments[0] : null;
  };

  const getProductUomTitle = (product) => {
    const segment = getPrimarySegmentForView(product);
    return segment?.uom_title || product?.uom_title || product?.weight_unit || "-";
  };

  const getProductPurchasePrice = (product) => {
    const segment = getPrimarySegmentForView(product);
    return segment?.purchase_price || product?.purchase_price || product?.purchasePrice || "-";
  };

  const getProductSellingPrice = (product) => {
    const segment = getPrimarySegmentForView(product);
    return segment?.selling_price || product?.selling_price || product?.sellingPrice || "-";
  };

  const getProductMediaGallery = (product) => {
    return Array.isArray(product?.media_gallery) ? product.media_gallery : [];
  };

  const getProductStatusLabel = (product) => {
    return isProductSoftDeletedByMissingPriceInfo(product) ? "Deleted / Inactive" : "Active";
  };

  const downloadCsvFile = (filename, rows) => {
    const csvContent = rows.map((row) => row.map(escapeCsvValue).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const fetchAllProductsForExport = async () => {
    const firstResponse = await PostPaginatedData("showProductAndInventory", {
      page: 1,
      search: undefined,
      queryParams: {},
      body: {},
    });
    const firstRows = Array.isArray(firstResponse?.data) ? firstResponse.data : [];
    const pageSize = firstRows.length || 10;
    const totalPages = Math.max(1, Math.ceil((firstResponse?.totalCount || firstRows.length) / pageSize));
    const rows = [...firstRows];

    for (let page = 2; page <= totalPages; page += 1) {
      const response = await PostPaginatedData("showProductAndInventory", {
        page,
        search: undefined,
        queryParams: {},
        body: {},
      });
      rows.push(...(Array.isArray(response?.data) ? response.data : []));
    }

    return rows;
  };

  const handleExportProductsCsv = async () => {
    try {
      const allProducts = await fetchAllProductsForExport();

      if (!allProducts.length) {
        toast.info("No products found to export.");
        return;
      }

      const headers = [
        "Product ID",
        "Product Title",
        "Status",
        "Category",
        "Category Breadcrumb",
        "Barcode",
        "UOM",
        "Purchase Price",
        "Selling Price",
        "Vendor",
        "Short Description",
        "Long Description",
        "Weight",
        "Segment Count",
      ];

      const rows = allProducts.flatMap((product) => {
        const segments = getViewSegments(product);
        const exportRows = segments.length > 0 ? segments : [null];

        return exportRows.map((segment) => {
          const productForValue = segment ? { ...product, segments: [segment] } : product;
          const weight = [product?.weight_value, product?.weight_unit].filter(Boolean).join(" ");

          return [
            product?.id || product?.product || "",
            invMergeGetTitle(product),
            getProductStatusLabel(product),
            getCategoryNames(product),
            getCategoryBreadcrumbs(product).join(" | "),
            segment?.barcode || product?.barcode || invMergeGetBarcode(product) || "",
            segment?.uom_title || segment?.uom || getProductUomTitle(productForValue),
            segment?.purchase_price || segment?.purchase_price_per_pc || getProductPurchasePrice(productForValue),
            segment?.selling_price || segment?.selling_price_per_pc || getProductSellingPrice(productForValue),
            segment?.vendor_name || product?.vendor_name || product?.vendor || "",
            product?.short_description || "",
            product?.long_description || "",
            weight,
            segments.length,
          ];
        });
      });

      downloadCsvFile(`all-products-${new Date().toISOString().slice(0, 10)}.csv`, [headers, ...rows]);
      toast.success(`Exported ${rows.length} product row${rows.length === 1 ? "" : "s"}.`);
    } catch (error) {
      console.error("Product export failed:", error);
      toast.error("Failed to export products CSV.");
    }
  };

  const normalizeApiListResponse = (response) => {
    const payload = response?.data || response || {};

    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.results)) return payload.results;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.items)) return payload.items;

    return [];
  };

  const normalizeApiObjectResponse = (response) => {
    const payload = response?.data || response || {};

    if (Array.isArray(payload)) return payload[0] || null;
    if (Array.isArray(payload.results)) return payload.results[0] || null;
    if (Array.isArray(payload.data)) return payload.data[0] || null;

    return payload && typeof payload === "object" ? payload : null;
  };

  const uniqueByIdOrValue = (items) => {
    const seen = new Set();

    return (Array.isArray(items) ? items : []).filter((item, index) => {
      const key =
        item?.id ||
        item?.segment_id ||
        item?.segment_barcode ||
        item?.uom ||
        item?.uom_title ||
        `row-${index}`;

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const normalizeSegmentFromAnySource = (item, productFallback = {}) => {
    if (!item) return null;

    const productId = getProductId(productFallback);
    const itemProductId =
      item.product_id ||
      item.product ||
      item.productId ||
      item.product_uuid ||
      item.product_details?.id ||
      "";

    if (productId && itemProductId && String(itemProductId) !== String(productId)) {
      return null;
    }

    return {
      id: item.id || item.segment_id || item.segmentId || "",
      segment_barcode:
        item.segment_barcode ||
        item.barcode ||
        productFallback?.barcode ||
        "",
      product_id: itemProductId || productId,
      uom: item.uom || item.uom_id || item.uomId || "",
      uom_title:
        item.uom_title ||
        item.uom_name ||
        item.UOM ||
        item.uom ||
        item.unit ||
        item.unit_name ||
        productFallback?.UOM ||
        productFallback?.uom_title ||
        "",
      total_pc_in_uom:
        item.total_pc_in_uom ||
        item.totalPcInUom ||
        item.total_pc ||
        productFallback?.total_pc_in_uom ||
        productFallback?.totalPcInUom ||
        "",
      purchase_price:
        item.purchase_price ||
        item.purchasePrice ||
        item.cost_price ||
        productFallback?.purchase_price ||
        productFallback?.purchasePrice ||
        "",
      selling_price:
        item.selling_price ||
        item.sellingPrice ||
        item.sale_price ||
        productFallback?.selling_price ||
        productFallback?.sellingPrice ||
        "",
      purchase_price_per_pc:
        item.purchase_price_per_pc ||
        item.purchasePricePerPc ||
        item.purchase_price ||
        item.purchasePrice ||
        productFallback?.purchasePrice ||
        productFallback?.purchase_price ||
        "",
      selling_price_per_pc:
        item.selling_price_per_pc ||
        item.sellingPricePerPc ||
        item.selling_price ||
        item.sellingPrice ||
        productFallback?.sellingPrice ||
        productFallback?.selling_price ||
        "",
      inventory_available:
        item.inventory_available !== undefined
          ? item.inventory_available
          : productFallback?.current_stock > 0 ||
            productFallback?.total_current_stock > 0 ||
            false,
      remaining_inventory:
        item.remaining_inventory ||
        item.current_stock ||
        item.total_current_stock ||
        productFallback?.current_stock ||
        productFallback?.total_current_stock ||
        "",
      source: item.source || "api-or-row",
    };
  };

  const buildFallbackSegmentFromProduct = (product) => {
    if (!product) return null;

    const uomValue = getProductTableUomValue(product);
    const purchaseValue = getProductTablePurchasePriceValue(product);
    const sellingValue = getProductTableSellingPriceValue(product);

    if (
      isBlankValue(uomValue) &&
      isBlankValue(purchaseValue) &&
      isBlankValue(sellingValue)
    ) {
      return null;
    }

    return normalizeSegmentFromAnySource(
      {
        id: product?.inventory_id || product?.id || "fallback-segment",
        segment_barcode: product?.barcode || "",
        product_id: product?.id || "",
        uom_title: uomValue,
        purchase_price: purchaseValue,
        selling_price: sellingValue,
        purchase_price_per_pc: purchaseValue,
        selling_price_per_pc: sellingValue,
        remaining_inventory:
          product?.current_stock || product?.total_current_stock || "",
        inventory_available:
          product?.current_stock > 0 || product?.total_current_stock > 0,
        source: "table-row-fallback",
      },
      product,
    );
  };

  const mergeProductDetailWithPricing = ({
    baseProduct,
    detailProduct,
    apiSegments,
  }) => {
    const mergedProduct = {
      ...(baseProduct || {}),
      ...(detailProduct || {}),
    };

    const detailSegments = Array.isArray(detailProduct?.segments)
      ? detailProduct.segments
      : [];

    const baseSegments = Array.isArray(baseProduct?.segments)
      ? baseProduct.segments
      : [];

    const normalizedApiSegments = uniqueByIdOrValue(
      (apiSegments || [])
        .map((item) => normalizeSegmentFromAnySource(item, mergedProduct))
        .filter(Boolean),
    );

    const fallbackSegment = buildFallbackSegmentFromProduct(mergedProduct);

    const finalSegments = uniqueByIdOrValue([
      ...detailSegments,
      ...baseSegments,
      ...normalizedApiSegments,
      ...(fallbackSegment ? [fallbackSegment] : []),
    ]).filter((item) => {
      return !(
        isBlankValue(item?.uom_title || item?.uom) &&
        isBlankValue(item?.purchase_price || item?.purchasePrice) &&
        isBlankValue(item?.selling_price || item?.sellingPrice)
      );
    });

    return {
      ...mergedProduct,
      segments: finalSegments,
      __detail_loaded: Boolean(detailProduct),
      __pricing_loaded_from_api: normalizedApiSegments.length > 0,
      __pricing_loaded_from_fallback:
        normalizedApiSegments.length === 0 && Boolean(fallbackSegment),
    };
  };

  const fetchProductDetailByCandidateApis = async (productId) => {
    if (!productId) return null;

    const detailAttempts = [];

    if (_Get && getAPI) {
      detailAttempts.push(async () => {
        const apiPath = `${getAPI(PRODUCT_UPDATE_API)}${productId}`;
        return _Get(apiPath);
      });

      detailAttempts.push(async () => {
        const apiPath = `${getAPI(PRODUCT_UPDATE_API)}${productId}/`;
        return _Get(apiPath);
      });
    }

    if (GetPaginatedData) {
      detailAttempts.push(() =>
        GetPaginatedData(PRODUCT_UPDATE_API, {
          page: undefined,
          queryParams: { id: productId },
        }),
      );

      detailAttempts.push(() =>
        GetPaginatedData("getProduct", {
          page: undefined,
          queryParams: { id: productId },
        }),
      );
    }

    for (const attempt of detailAttempts) {
      try {
        const response = await attempt();
        const objectData = normalizeApiObjectResponse(response);
        if (objectData && (objectData.id || objectData.title || objectData.results)) {
          return objectData;
        }
      } catch (error) {
        console.warn("Product detail API attempt failed:", error);
      }
    }

    return null;
  };

  const fetchProductPricingByCandidateApis = async (productId, product) => {
    if (!productId || !GetPaginatedData) return [];

    const barcode = product?.barcode || "";
    const inventoryId = product?.inventory_id || "";

    const attempts = [
      ["productSegmentData", { product_id: productId }],
      ["productSegmentData", { product: productId }],
      ["productSegment", { product_id: productId }],
      ["productSegment", { product: productId }],
      ["getProductSegment", { product_id: productId }],
      ["getProductSegment", { product: productId }],
      ["segmentData", { product_id: productId }],
      ["segmentData", { product: productId }],
      ["inventorySegmentData", { product_id: productId }],
      ["inventoryData", { product_id: productId }],
      ["inventoryData", { product: productId }],
      ["inventoryData", { inventory_id: inventoryId }],
      ["productInventory", { product_id: productId }],
      ["productInventory", { barcode }],
    ];

    const allSegments = [];

    for (const [apiName, queryParams] of attempts) {
      try {
        const cleanedQueryParams = Object.fromEntries(
          Object.entries(queryParams).filter(([, value]) => !isBlankValue(value)),
        );

        if (Object.keys(cleanedQueryParams).length === 0) {
          continue;
        }

        const response = await GetPaginatedData(apiName, {
          page: undefined,
          queryParams: cleanedQueryParams,
        });

        const rows = normalizeApiListResponse(response);
        if (rows.length > 0) {
          allSegments.push(...rows.map((row) => ({ ...row, source: apiName })));
        }
      } catch (error) {
        /**
         * These are candidate APIs. Some may not exist in your api-manager,
         * so failures are expected and should not block the modal.
         */
        console.warn("Pricing/segment API attempt failed:", apiName, error);
      }
    }

    return uniqueByIdOrValue(allSegments);
  };

  const handleViewProduct = async (productOrId) => {
    const productId = getProductId(productOrId);
    const product = getProductObject(productOrId) || productOrId;

    const initialProduct =
      product && typeof product === "object" ? product : { id: productId };

    const initialMergedProduct = mergeProductDetailWithPricing({
      baseProduct: initialProduct,
      detailProduct: null,
      apiSegments: [],
    });

    setSelectedViewProduct(initialMergedProduct || null);
    setShowProductViewModal(true);

    if (!productId) return;

    try {
      setViewProductLoading(true);

      const [productDetail, pricingSegments] = await Promise.all([
        fetchProductDetailByCandidateApis(productId),
        fetchProductPricingByCandidateApis(productId, initialProduct),
      ]);

      const finalProduct = mergeProductDetailWithPricing({
        baseProduct: initialProduct,
        detailProduct: productDetail,
        apiSegments: pricingSegments,
      });

      setSelectedViewProduct(finalProduct);
    } catch (error) {
      console.warn("Unable to load complete product detail for view modal:", error);
      setSelectedViewProduct(initialMergedProduct);
    } finally {
      setViewProductLoading(false);
    }
  };
  const closeProductViewModal = () => {
    setShowProductViewModal(false);
    setSelectedViewProduct(null);
  };

  const renderInfoItem = (label, value, options = {}) => (
    <div
      style={{
        background: options.highlight ? "#f0fdf4" : "#ffffff",
        border: options.highlight ? "1px solid #bbf7d0" : "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "12px 14px",
        minHeight: "72px",
      }}
    >
      <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </div>
      <div style={{ marginTop: "6px", fontSize: options.large ? "18px" : "14px", fontWeight: 800, color: options.color || "#111827", wordBreak: "break-word" }}>
        {getValue(value)}
      </div>
    </div>
  );

  const renderSectionTitle = (title, subtitle) => (
    <div style={{ marginBottom: "14px" }}>
      <h4 style={{ margin: 0, fontSize: "17px", fontWeight: 900, color: "#111827" }}>{title}</h4>
      {subtitle && <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: "12px" }}>{subtitle}</p>}
    </div>
  );

  const safeCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (value === "-") return "-";
    return formatCurrency ? formatCurrency(value) : value;
  };

  const handleInlineStockSubmit = async (event) => {
    event?.preventDefault?.();
    if (inlineStockSaving) return;

    if (!stockLockedProduct) {
      return stockHandleSubmit(event);
    }

    const productId = getProductId(stockLockedProduct);
    const selectedSegment = stockSelectedSegment || getFirstSegment(stockLockedProduct);
    const segmentId = selectedSegment?.id || selectedSegment?.segment || selectedSegment?.segment_id || "";
    const uomId = selectedSegment?.uom || selectedSegment?.uom_id || stockUom || "";
    const totalPcValue =
      selectedSegment?.total_pc_in_uom ||
      selectedSegment?.segment_total_pc_in_uom ||
      stockTotalPcInUom ||
      1;

    if (!productId) {
      toast.error("Product is missing. Please select product again.");
      return;
    }

    if (!segmentId) {
      toast.error("Please select UOM/segment before adding stock.");
      return;
    }

    if (isInvalidNumberValue(stockTotalStockArrived)) {
      toast.error("Please enter Addition In Stock.");
      return;
    }

    const purchase = stockPurchasePrice || selectedSegment?.purchase_price || selectedSegment?.purchasePrice || 0;
    const selling = stockSellingPrice || selectedSegment?.selling_price || selectedSegment?.sellingPrice || 0;

    if (parseFloat(selling) < parseFloat(purchase)) {
      toast.error("Selling price cannot be less than purchase price.");
      return;
    }

    const inventoryData = {
      store: storeId,
      product: productId,
      date: new Date().toISOString(),
      category: stockCategory || invMergeGetCategories(stockLockedProduct)[0] || "",
      uom: uomId,
      total_pc_in_uom: totalPcValue,
      purchase_price: parseFloat(purchase || 0).toFixed(2),
      selling_price: parseFloat(selling || 0).toFixed(2),
      total_stock_arrived: stockTotalStockArrived || 0,
      vendor: stockSelectedVendorId || "",
      vendor_name: stockSelectedVendorName || "",
      segment: segmentId,
      purchase_invoice_no: stockPurchaseInvoiceNo.trim(),
      purchase_payment_method: stockPurchasePaymentMethod,
      purchase_invoice_remark: stockPurchaseInvoiceRemark.trim(),
    };

    try {
      setInlineStockSaving(true);
      await Post("productInventoryData", inventoryData);
      toast.success("Stock added successfully!");
      closeStockModal();
      stockHandleDataUpdate?.();
      handleDataUpdate?.();
      setProductInventoryLotsRefreshKey((current) => current + 1);
    } catch (error) {
      console.error("Inline stock add failed:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Error saving stock details.",
      );
    } finally {
      setInlineStockSaving(false);
    }
  };

  const renderAddStockModal = () => (
    <ReusableModal
      title={stockIsNewProduct ? "Add Product with Opening Stock" : "Add Stock"}
      show={showStockModal}
      handleClose={closeStockModal}
      width="650px"
      height="auto"
    >
      <div className="invmerge-stock-modal">
        <div className="invmerge-stock-scan">
          <img src={scanner} alt="Barcode" />
          <div className="invmerge-stock-field">
            <input
              type="text"
              className="form-control-sales"
              name="barcode"
              value={stockBarcode || ""}
              onChange={stockHandleBarcodeChange}
              onBlurCapture={stockLockedProduct ? undefined : stockScanBarcode}
              placeholder=""
              readOnly={Boolean(stockLockedProduct)}
            />
            <label className="floating-label">Barcode</label>
          </div>
        </div>

        {stockShowConfirmation && (
          <div className="invmerge-stock-alert">
            <strong>Product not found.</strong>
            <span>Do you want to add it as a new product with opening stock?</span>
            <input
              type="checkbox"
              checked={Boolean(stockIsNewProduct)}
              onChange={() => {
                if (!stockIsNewProduct) {
                  handleCloseStockModal();
                  navigate("/product", {
                    state: { openAddModal: true, barcode: stockBarcode },
                  });
                } else {
                  stockHandleCancelNewProduct();
                }
              }}
            />
          </div>
        )}

        {!stockIsNewProduct && (
          <div className="invmerge-stock-section">
            <div className="invmerge-stock-product-row">
              {stockLockedProduct ? (
                <div className="invmerge-locked-product invmerge-stock-locked-product">
                  <span className={`invmerge-product-img ${buildMediaUrl(stockLockedProduct?.product_image) ? "has-image" : ""}`}>
                    {buildMediaUrl(stockLockedProduct?.product_image) && (
                      <img src={buildMediaUrl(stockLockedProduct.product_image)} alt={stockLockedProduct.title} />
                    )}
                    <Package className="invmerge-placeholder-icon" size={21} />
                  </span>
                  <div>
                    <strong>{stockLockedProduct.title || stockLockedProduct.product_title}</strong>
                    <p>
                      Product is locked for this stock entry
                      {stockLockedProduct.barcode ? ` • Barcode: ${stockLockedProduct.barcode}` : ""}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="invmerge-stock-control invmerge-full-product-select">
                  <FormControl fullWidth variant="outlined" size="small">
                    <Autocomplete
                      disablePortal
                      value={
                        stockSelectedProduct
                          ? typeof stockSelectedProduct === "object"
                            ? stockSelectedProduct
                            : { id: stockSelectedProduct, title: stockProductTitle }
                          : null
                      }
                      onChange={(event, newValue) => {
                        const normalized = normalizeProductForAction(newValue) || newValue;
                        stockSetSelectedProduct(normalized);
                        stockSetProductTitle(normalized?.title || normalized?.product_title || "");
                        if (normalized?.barcode && typeof stockHandleBarcodeChange === "function") {
                          stockHandleBarcodeChange({ target: { value: normalized.barcode } });
                        }
                      }}
                      onInputChange={(event, newInputValue) => {
                        stockSetSearchQuery(newInputValue);
                      }}
                      filterOptions={(options, state) => {
                        const query = invMergeNormalizeText(state.inputValue);
                        if (!query) return options;
                        return options.filter((option) =>
                          invMergeNormalizeText(`${invMergeGetTitle(option)} ${invMergeGetBarcode(option)} ${option?.code_sku || ""}`).includes(query),
                        );
                      }}
                      loading={loading}
                      options={Array.isArray(stockProductList) ? stockProductList : []}
                      getOptionLabel={getStockProductOptionLabel}
                      isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return false;
                        const optionId = getProductId(option);
                        const valueId = getProductId(value);
                        return optionId === valueId;
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search / Select Product by name or barcode"
                          variant="outlined"
                          className="advance-input invmerge-product-search-input"
                        />
                      )}
                      renderOption={(props, option) => {
                        const title = invMergeGetTitle(option);
                        const barcodeValue = invMergeGetBarcode(option);
                        return (
                          <MenuItem {...props} className="invmerge-product-option">
                            <div>
                              <strong>{title}</strong>
                              {barcodeValue && <span>Barcode: {barcodeValue}</span>}
                            </div>
                          </MenuItem>
                        );
                      }}
                      ListboxProps={{
                        sx: { maxHeight: 260, overflow: "auto" },
                      }}
                    />
                  </FormControl>
                </div>
              )}
            </div>

            <div className="invmerge-stock-row">
              <div className="invmerge-stock-field">
                <input
                  type="number"
                  className="form-control-sales"
                  name="total_stock_arrived"
                  value={stockTotalStockArrived || ""}
                  onChange={(event) => stockSetTotalStockArrived(event.target.value)}
                  placeholder=""
                />
                <label className="floating-label">Addition In Stock</label>
              </div>

              <div className="invmerge-stock-field invmerge-stock-helper-box">
                <input
                  type="text"
                  className="form-control-sales"
                  value={stockSelectedSegment ? `${stockSelectedSegment.uom_title || stockSelectedSegment.segment_uom || stockSelectedSegment.uom || "Selected UOM"} × ${stockSelectedSegment.total_pc_in_uom || stockSelectedSegment.segment_total_pc_in_uom || 1} pc` : "Select UOM to calculate pieces"}
                  disabled
                  placeholder=""
                />
                <label className="floating-label">UOM Conversion</label>
              </div>
            </div>

            <div className="invmerge-stock-row">
              <div className="invmerge-stock-field">
                <input
                  type="number"
                  className="form-control-sales"
                  value={stockPurchasePrice || ""}
                  onChange={stockHandlePurchasePriceChange}
                  onBlur={stockHandlePurchasePriceBlur}
                  placeholder=""
                />
                <label className="floating-label">Purchase Price</label>
                {stockPurchasePriceError && (
                  <span className="error-text">{stockPurchasePriceError}</span>
                )}
              </div>

              <div className="invmerge-stock-field">
                <input
                  type="number"
                  className="form-control-sales"
                  value={stockSellingPrice || ""}
                  onChange={stockHandleSellingPriceChange}
                  onBlur={stockHandleSellingPriceBlur}
                  placeholder=""
                />
                <label className="floating-label">Selling Price</label>
                {stockSellingPriceError && (
                  <span className="error-text">{stockSellingPriceError}</span>
                )}
              </div>
            </div>

            <div className="invmerge-stock-row">
              <div className="invmerge-stock-field">
                <select
                  className="form-control-borderless"
                  value={
                    stockSelectedSegment
                      ? `${stockSelectedSegment.uom_title || stockSelectedSegment.segment_uom || stockSelectedSegment.uom || ""}_${stockSelectedSegment.total_pc_in_uom || stockSelectedSegment.segment_total_pc_in_uom || 1}`
                      : ""
                  }
                  onChange={stockHandleUomChange}
                >
                  <option value="">Select UOM</option>
                  {Array.isArray(stockSegments) && stockSegments.length > 0 ? (
                    stockSegments.map((segment) => (
                      <option
                        key={segment.id}
                        value={`${segment.uom_title}_${segment.total_pc_in_uom}`}
                      >
                        {segment.uom_title} ({segment.total_pc_in_uom} pc)
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      No UOMs available
                    </option>
                  )}
                </select>
              </div>

              <div className="invmerge-stock-field">
                <input
                  type="number"
                  className="form-control-sales"
                  value={stockCurrentStock || ""}
                  disabled
                  placeholder=""
                />
                <label className="floating-label">Current Stock</label>
              </div>
            </div>

            <div className="invmerge-stock-row">
              <div className="invmerge-stock-field">
                <select
                  className="form-control-borderless product"
                  value={stockSelectedVendorId || ""}
                  onChange={stockHandleVendorChange}
                >
                  <option value="">Select Vendor</option>
                  <option value="add-vendor">Add Vendor</option>
                  {Array.isArray(stockVendors) &&
                    stockVendors
                      .filter((vendor) => vendor.type === "purchase" && !vendor.is_deleted)
                      .map((vendor) => (
                        <option key={vendor.id} value={vendor.id}>
                          {vendor.name}
                        </option>
                      ))}
                </select>
              </div>

              <div className="invmerge-stock-field">
                <input
                  type="number"
                  className="form-control-sales"
                  value={
                    stockTotalStockArrived *
                    (stockSelectedSegment?.total_pc_in_uom || stockSelectedSegment?.segment_total_pc_in_uom || 1)
                  }
                  disabled
                  placeholder=""
                />
                <label className="floating-label">Total Piece Added</label>
              </div>
            </div>

            <div className="invmerge-stock-row">
              <div className="invmerge-stock-field">
                <input
                  type="date"
                  className="form-control-sales"
                  value={stockExpiryDate || ""}
                  onChange={(event) => stockSetExpiryDate(event.target.value)}
                  placeholder=""
                />
                <label className="floating-label">Expiry Date (optional)</label>
              </div>
            </div>
          </div>
        )}

        {stockIsNewProduct && (
          <div className="invmerge-stock-section">
            <div className="invmerge-stock-row">
              <div className="invmerge-stock-field">
                <input
                  type="text"
                  className="form-control-sales"
                  value={stockProductTitle || ""}
                  onChange={stockHandleProductTitleChange}
                  placeholder=""
                />
                <label className="floating-label">Product Title</label>
              </div>

              <PaginatedMultiSelect
                api="productCategory"
                GetPaginatedData={GetPaginatedData}
                getOptionLabel={(opt) => opt.name}
                value={stockSelectedCategory}
                onSelect={stockSetSelectedCategory}
                placeholder="Select Category"
              />
            </div>

            <div className="invmerge-stock-row">
              <PaginatedSearchSelect
                api="uomData"
                GetPaginatedData={GetPaginatedData}
                getOptionLabel={(opt) => opt.uom_title}
                onSelect={(opt) => stockSetSelectedUom(opt)}
                placeholder="Select UOM"
                value={stockSelectedUom}
              />

              <PaginatedSearchSelect
                api="vendorDepartmentData"
                GetPaginatedData={GetPaginatedData}
                getOptionLabel={(opt) => opt.name}
                onSelect={(opt) => {
                  if (opt?.id === "add-vendor") {
                    stockHandleVendorChange({ target: { value: "add-vendor" } });
                  } else {
                    stockHandleVendorChange({ target: { value: opt?.id || "" } });
                    stockSetSelectedVendorName(opt?.name || "");
                  }
                }}
                placeholder="Select Vendor"
                value={
                  stockSelectedVendorId
                    ? { id: stockSelectedVendorId, name: stockSelectedVendorName }
                    : null
                }
              />
            </div>

            <div className="invmerge-stock-row">
              <div className="invmerge-stock-field">
                <input
                  type="number"
                  className="form-control-sales"
                  value={stockPurchasePrice || ""}
                  onChange={stockHandlePurchasePriceChange}
                  onBlur={stockHandlePurchasePriceBlur}
                  placeholder=""
                />
                <label className="floating-label">Purchase Price</label>
              </div>

              <div className="invmerge-stock-field">
                <input
                  type="number"
                  className="form-control-sales"
                  value={stockSellingPrice || ""}
                  onChange={stockHandleSellingPriceChange}
                  onBlur={stockHandleSellingPriceBlur}
                  placeholder=""
                />
                <label className="floating-label">Selling Price</label>
              </div>
            </div>

            <div className="invmerge-stock-row">
              <div className="invmerge-stock-field">
                <input
                  type="text"
                  className="form-control-sales"
                  value={stockOpeningInventory || ""}
                  onChange={stockHandleOpeningInventoryChange}
                  onFocus={stockHandleFocusOpeningInventory}
                  onBlur={stockHandleBlurOpeningInventory}
                  placeholder=""
                />
                <label className="floating-label">Opening Inventory</label>
              </div>

              <div className="invmerge-stock-field">
                <input
                  type="number"
                  className="form-control-sales"
                  value={stockTotalPcInUom || ""}
                  onChange={stockHandleTotalPcInUomChange}
                  placeholder=""
                />
                <label className="floating-label">Total PC in UOM</label>
              </div>
            </div>

            <div className="invmerge-stock-row">
              <div className="invmerge-stock-field">
                <input
                  type="date"
                  className="form-control-sales"
                  value={stockExpiryDate || ""}
                  onChange={(event) => stockSetExpiryDate(event.target.value)}
                  placeholder=""
                />
                <label className="floating-label">Expiry Date (optional)</label>
              </div>
            </div>

            <label className="invmerge-checkbox-row">
              <input
                type="checkbox"
                checked={Boolean(stockAutoGenerateBarcode)}
                onChange={(event) => stockSetAutoGenerateBarcode(event.target.checked)}
              />
              Auto Generate Barcode
            </label>
          </div>
        )}

        {!stockIsNewProduct && stockSelectedVendorId && (
          <div className="invmerge-stock-section" style={{ border: "1px solid #b7e4cf", background: "#f3fbf7" }}>
            <div style={{ marginBottom: "14px" }}>
              <strong style={{ display: "block", color: "#176b4d", fontSize: "15px" }}>
                A paid purchase invoice will be created with this stock entry
              </strong>
              <span style={{ color: "#527267", fontSize: "12px" }}>
                The invoice amount will be calculated as purchase price × stock UOM quantity.
              </span>
            </div>

            <div className="invmerge-stock-row">
              <div className="invmerge-stock-field">
                <input
                  type="text"
                  className="form-control-sales"
                  value={stockPurchaseInvoiceNo}
                  onChange={(event) => setStockPurchaseInvoiceNo(event.target.value)}
                  placeholder=""
                  maxLength={100}
                />
                <label className="floating-label">Purchase Invoice No. (optional)</label>
                <span style={{ color: "#6b7280", fontSize: "11px" }}>A STOCK invoice number is generated when left blank.</span>
              </div>

              <div className="invmerge-stock-field">
                <select
                  className="form-control-borderless"
                  value={stockPurchasePaymentMethod}
                  onChange={(event) => setStockPurchasePaymentMethod(event.target.value)}
                >
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="bank">Bank / Card</option>
                </select>
                <span style={{ color: "#6b7280", fontSize: "11px" }}>Payment type</span>
              </div>
            </div>

            <div className="invmerge-stock-field">
              <textarea
                className="form-control-sales"
                value={stockPurchaseInvoiceRemark}
                onChange={(event) => setStockPurchaseInvoiceRemark(event.target.value)}
                placeholder=""
                rows={3}
                maxLength={160}
                style={{ height: "auto", paddingTop: "18px", resize: "vertical" }}
              />
              <label className="floating-label">Comment / Remark (optional)</label>
            </div>
          </div>
        )}

        <div className="invmerge-modal-actions">
          <RegularButton
            text="Clear"
            onClick={() => {
              stockResetForm?.();
              setStockPurchaseInvoiceNo("");
              setStockPurchasePaymentMethod("cash");
              setStockPurchaseInvoiceRemark("");
            }}
            backgroundColor="#ffffff"
            color="#282E26"
          />
          <RegularButton
            text={(inlineStockSaving || stockIsSubmitting) ? "Saving..." : stockIsNewProduct ? "Save Product" : "Save Stock"}
            onClick={handleInlineStockSubmit}
            backgroundColor="#282e26"
            color="#ffffff"
          />
        </div>
      </div>
    </ReusableModal>
  );

  const renderInlineSegmentModal = () => {
    const lockedProduct = segmentProduct;
    const activeProduct = getInlineSegmentProduct();

    return (
      <ReusableModal
        title={lockedProduct ? "Add Segment for Product" : "Add Product Segment"}
        show={showSegmentModal}
        handleClose={closeInlineSegmentModal}
        width="720px"
      >
        <div className="invmerge-segment-modal">
          {lockedProduct ? (
            <div className="invmerge-locked-product">
              <span className={`invmerge-product-img ${buildMediaUrl(lockedProduct?.product_image) ? "has-image" : ""}`}>
                {buildMediaUrl(lockedProduct?.product_image) && (
                  <img
                    src={buildMediaUrl(lockedProduct.product_image)}
                    alt={lockedProduct.title || "Product"}
                  />
                )}
                <Package className="invmerge-placeholder-icon" size={21} />
              </span>
              <div>
                <strong>{lockedProduct.title || lockedProduct.product_name}</strong>
                <p>Segment will be created for this product. Product selection is locked.</p>
              </div>
            </div>
          ) : (
            <div className="invmerge-form-group invmerge-full-product-select">
              <Autocomplete
                disablePortal
                options={uniqueByIdOrValue([...(Array.isArray(productList) ? productList : []), ...(Array.isArray(visibleProductList) ? visibleProductList : [])])}
                value={segmentSelectedProduct}
                onChange={(event, newValue) => setSegmentSelectedProduct(newValue)}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  const title = option.title || option.product_title || option.product_name || "";
                  const barcodeValue = invMergeGetBarcode(option);
                  return barcodeValue ? `${title} • ${barcodeValue}` : title;
                }}
                isOptionEqualToValue={(option, value) => getProductId(option) === getProductId(value)}
                filterOptions={(options, state) => {
                  const query = invMergeNormalizeText(state.inputValue);
                  if (!query) return options;
                  return options.filter((option) =>
                    invMergeNormalizeText(`${invMergeGetTitle(option)} ${invMergeGetBarcode(option)} ${option?.code_sku || ""}`).includes(query),
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Search / Select Product by name or barcode" variant="outlined" />
                )}
                renderOption={(props, option) => (
                  <MenuItem {...props} className="invmerge-product-option">
                    <div>
                      <strong>{invMergeGetTitle(option)}</strong>
                      {invMergeGetBarcode(option) && <span>Barcode: {invMergeGetBarcode(option)}</span>}
                    </div>
                  </MenuItem>
                )}
                ListboxProps={{ sx: { maxHeight: 260, overflow: "auto" } }}
              />
            </div>
          )}

          <div className="invmerge-segment-grid">
            <div className="invmerge-form-group">
              <label>UOM *</label>
              <Autocomplete
                disablePortal
                loading={segmentLookupLoading}
                options={segmentUomOptions}
                value={segmentSelectedUom}
                onChange={(event, newValue) => setSegmentSelectedUom(newValue)}
                getOptionLabel={(option) => option?.uom_title || option?.name || ""}
                isOptionEqualToValue={(option, value) => getOptionId(option) === getOptionId(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Select UOM" variant="outlined" />
                )}
                ListboxProps={{ sx: { maxHeight: 230, overflow: "auto" } }}
              />
            </div>

            <div className="invmerge-form-group">
              <label>Vendor</label>
              <Autocomplete
                disablePortal
                loading={segmentLookupLoading}
                options={segmentVendorOptions}
                value={segmentSelectedVendor}
                onChange={(event, newValue) => setSegmentSelectedVendor(newValue)}
                getOptionLabel={(option) => option?.name || option?.label || ""}
                isOptionEqualToValue={(option, value) => getOptionId(option) === getOptionId(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Vendor" variant="outlined" />
                )}
                ListboxProps={{ sx: { maxHeight: 230, overflow: "auto" } }}
              />
            </div>

            <div className="invmerge-form-group">
              <label>Purchase Price *</label>
              <input
                type="number"
                value={segmentPurchasePrice}
                onChange={(event) => setSegmentPurchasePrice(event.target.value)}
                placeholder="Purchase price"
              />
            </div>

            <div className="invmerge-form-group">
              <label>Selling Price *</label>
              <input
                type="number"
                value={segmentSellingPrice}
                onChange={(event) => setSegmentSellingPrice(event.target.value)}
                placeholder="Selling price"
              />
            </div>

            <div className="invmerge-form-group">
              <label>Total PC in UOM *</label>
              <input
                type="number"
                value={segmentTotalPcInUom}
                onChange={(event) => setSegmentTotalPcInUom(event.target.value)}
                placeholder="Example: 12"
              />
            </div>

            <div className="invmerge-form-group">
              <label>Segment Barcode</label>
              <input
                type="text"
                value={segmentBarcodeValue}
                onChange={(event) => setSegmentBarcodeValue(event.target.value)}
                placeholder="Optional barcode"
              />
            </div>
          </div>

          {activeProduct && (
            <div className="invmerge-segment-note">
              Adding segment for: <strong>{activeProduct.title || activeProduct.product_name}</strong>
            </div>
          )}

          <div className="invmerge-modal-actions">
            <RegularButton
              text="Cancel"
              onClick={closeInlineSegmentModal}
              backgroundColor="#ffffff"
              color="#282E26"
            />
            <RegularButton
              text={segmentSaving ? "Saving..." : "Save Segment"}
              onClick={handleInlineSegmentSubmit}
              backgroundColor="#282e26"
              color="#ffffff"
            />
          </div>
        </div>
      </ReusableModal>
    );
  };

  const renderProductViewModal = () => {
    const product = selectedViewProduct;
    if (!product) return null;

    const segments = getViewSegments(product);
    const mediaGallery = getProductMediaGallery(product);
    const categoryBreadcrumbs = getCategoryBreadcrumbs(product);
    const primaryImage = buildMediaUrl(product?.product_image);

    return (
      <ReusableModal
        title="Product Details"
        show={showProductViewModal}
        handleClose={closeProductViewModal}
        width="1100px"
      >
        <div style={{ padding: "20px 24px", background: "#f8fafc" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "280px 1fr",
              gap: "20px",
              alignItems: "stretch",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: "18px",
                padding: "14px",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "230px",
                  borderRadius: "14px",
                  background: "#f3f4f6",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #e5e7eb",
                }}
              >
                {primaryImage ? (
                  <img src={primaryImage} alt={product?.title || "Product"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ color: "#9ca3af", fontWeight: 800 }}>No Image</div>
                )}
              </div>

              <div style={{ marginTop: "14px" }}>
                <span
                  style={{
                    display: "inline-flex",
                    padding: "7px 12px",
                    borderRadius: "999px",
                    background: isProductSoftDeletedByMissingPriceInfo(product) ? "#fef2f2" : "#ecfdf3",
                    color: isProductSoftDeletedByMissingPriceInfo(product) ? "#b91c1c" : "#15803d",
                    fontWeight: 900,
                    fontSize: "12px",
                  }}
                >
                  {getProductStatusLabel(product)}
                </span>
              </div>
            </div>

            <div
              style={{
                background: "#ffffff",
                border: "1px solid #e5e7eb",
                borderRadius: "18px",
                padding: "18px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: "12px", color: "#6b7280", fontWeight: 800 }}>SKU: {getValue(product?.code_sku)}</div>
                  <h2 style={{ margin: "6px 0 8px", fontSize: "26px", lineHeight: 1.2, fontWeight: 900, color: "#111827" }}>
                    {getValue(product?.title)}
                  </h2>
                  <div style={{ color: "#4b5563", fontSize: "13px", fontWeight: 600 }}>
                    Barcode: {getValue(product?.barcode)}
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "12px", color: "#6b7280", fontWeight: 800 }}>Next Invoice No</div>
                  <div style={{ fontSize: "24px", fontWeight: 900, color: "#111827" }}>{getValue(product?.next_invoice_no)}</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "12px", marginTop: "18px" }}>
                {renderInfoItem("Current Stock", formatNumber(product?.current_stock), { highlight: true, large: true, color: "#15803d" })}
                {renderInfoItem("Purchase Price", safeCurrency(getProductPurchasePrice(product)), { large: true })}
                {renderInfoItem("Selling Price", safeCurrency(getProductSellingPrice(product)), { large: true, color: "#1d4ed8" })}
                {renderInfoItem("UOM", getProductUomTitle(product), { large: true })}
                {renderInfoItem("Total Current Stock", formatNumber(product?.total_current_stock), { large: true })}
                {renderInfoItem("Stock Arrived", formatNumber(product?.total_stock_arrived), { large: true })}
              </div>
            </div>
          </div>

          <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px", marginBottom: "18px" }}>
            {renderSectionTitle("Product Information", "General product, category, vendor and measurement details.")}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "12px" }}>
              {renderInfoItem("Category", getCategoryNames(product))}
              {renderInfoItem("Vendor", product?.vendor_name || product?.vendor)}
              {renderInfoItem("Weight", product?.weight_value ? `${formatNumber(product.weight_value)} ${product?.weight_unit || ""}` : product?.weight_unit)}
              {renderInfoItem("Inventory Date", formatDateTime(product?.inventory_date))}
              {renderInfoItem("SKU", product?.code_sku)}
              {renderInfoItem("Barcode", product?.barcode)}
              {renderInfoItem("Status", getProductStatusLabel(product))}
              {renderInfoItem("Next Invoice", product?.next_invoice_no)}
            </div>

            {categoryBreadcrumbs.length > 0 && (
              <div style={{ marginTop: "14px" }}>
                <div style={{ fontSize: "12px", fontWeight: 900, color: "#6b7280", marginBottom: "8px" }}>CATEGORY BREADCRUMB</div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {categoryBreadcrumbs.map((item, index) => (
                    <span key={index} style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", padding: "7px 10px", borderRadius: "999px", fontWeight: 800, fontSize: "12px" }}>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px", marginBottom: "18px" }}>
            {renderSectionTitle(
              "Pricing & UOM",
              viewProductLoading
                ? "Loading latest price, UOM and segment information..."
                : product?.__pricing_loaded_from_api
                  ? "Latest pricing was loaded from product/segment APIs."
                  : product?.__pricing_loaded_from_fallback
                    ? "Pricing was filled from table/list data because segment API did not return details."
                    : "Segment-wise UOM, pricing and inventory availability."
            )}
            {viewProductLoading && (
              <div
                style={{
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1d4ed8",
                  padding: "10px 12px",
                  borderRadius: "12px",
                  fontWeight: 800,
                  fontSize: "13px",
                  marginBottom: "12px",
                }}
              >
                Fetching latest product pricing and stock details...
              </div>
            )}
            {segments.length > 0 ? (
              <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: "12px" }}>
                <table className="table" style={{ marginBottom: 0 }}>
                  <thead style={{ background: "#f8f9fa" }}>
                    <tr>
                      <th>UOM</th>
                      <th>Total PC</th>
                      <th>Purchase Price</th>
                      <th>Selling Price</th>
                      <th>Purchase/PC</th>
                      <th>Selling/PC</th>
                      <th>Remaining</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {segments.map((segment) => (
                      <tr key={segment.id || segment.segment_barcode}>
                        <td>{getValue(segment.uom_title || segment.uom)}</td>
                        <td>{formatNumber(segment.total_pc_in_uom)}</td>
                        <td>{safeCurrency(segment.purchase_price)}</td>
                        <td>{safeCurrency(segment.selling_price)}</td>
                        <td>{safeCurrency(segment.purchase_price_per_pc)}</td>
                        <td>{safeCurrency(segment.selling_price_per_pc)}</td>
                        <td>{formatNumber(segment.remaining_inventory)}</td>
                        <td>
                          <span style={{ color: segment.inventory_available ? "#15803d" : "#b91c1c", fontWeight: 900 }}>
                            {segment.inventory_available ? "Available" : "Unavailable"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", padding: "14px", borderRadius: "12px", fontWeight: 700 }}>
                No active segment/pricing data is available for this product. This can happen for deleted or older product records.
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px", marginBottom: "18px" }}>
            <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px" }}>
              {renderSectionTitle("Descriptions")}
              <div style={{ marginBottom: "12px" }}>
                <div style={{ fontSize: "12px", fontWeight: 900, color: "#6b7280", marginBottom: "6px" }}>Short Description</div>
                <div style={{ fontSize: "14px", color: "#111827", whiteSpace: "pre-wrap" }}>{getValue(product?.short_description)}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", fontWeight: 900, color: "#6b7280", marginBottom: "6px" }}>Long Description</div>
                <div style={{ fontSize: "14px", color: "#111827", whiteSpace: "pre-wrap" }}>{getValue(product?.long_description)}</div>
              </div>
            </div>

            <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px" }}>
              {renderSectionTitle("Media Gallery", `${mediaGallery.length} item(s)`)}
              {mediaGallery.length > 0 ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
                  {mediaGallery.map((item, index) => {
                    const mediaUrl = buildMediaUrl(item.image || item.video);
                    const isVideo = Boolean(item.video);
                    return (
                      <div key={item.id || index} style={{ height: "110px", borderRadius: "12px", overflow: "hidden", background: "#f3f4f6", border: "1px solid #e5e7eb" }}>
                        {isVideo ? (
                          <video src={mediaUrl} style={{ width: "100%", height: "100%", objectFit: "cover" }} controls />
                        ) : (
                          <img src={mediaUrl} alt={`Gallery ${index + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: "#6b7280", fontWeight: 700 }}>No gallery media available.</div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            <RegularButton text="Close" onClick={closeProductViewModal} backgroundColor="#ffffff" color="#282E26" />
            <RegularButton
              text={viewProductLoading ? "Loading..." : "Edit Product"}
              onClick={() => {
                closeProductViewModal();
                openQuickEditProduct(product);
              }}
              backgroundColor="#282e26"
              color="#ffffff"
            />
          </div>
        </div>
      </ReusableModal>
    );
  };


  const renderQuickEditProductModal = () => {
    if (!quickEditProduct) return null;

    const existingGallery = getQuickEditExistingGallery(quickEditProduct);

    return (
      <ReusableModal
        title="Edit Product"
        show={Boolean(quickEditProduct)}
        handleClose={closeQuickEditProduct}
        width="860px"
      >
        <div className="invmerge-edit-modal">
          <div className="invmerge-edit-note">
            Only product information can be edited here. Price, UOM and stock changes are controlled from Segment / Stock modules.
          </div>

          <div className="invmerge-edit-grid">
            <div className="invmerge-edit-media-card">
              <label className="invmerge-field-label">Product Image</label>
              <label className="invmerge-primary-upload">
                {quickEditPrimaryPreview ? (
                  <img src={quickEditPrimaryPreview} alt="Product preview" />
                ) : (
                  <span><ImageIcon size={34} /> Upload Image</span>
                )}
                <input type="file" accept="image/*" onChange={handleQuickEditPrimaryImageChange} />
              </label>

              <label className="invmerge-gallery-upload">
                <PlusCircle size={15} /> Add Gallery Images / Videos
                <input type="file" accept="image/*,video/*" multiple onChange={handleQuickEditGalleryUpload} />
              </label>
            </div>

            <div className="invmerge-edit-fields">
              <div className="invmerge-form-group">
                <label>Product Title</label>
                <input
                  type="text"
                  value={quickEditTitle}
                  onChange={(event) => setQuickEditTitle(event.target.value)}
                  placeholder="Enter product title"
                />
              </div>

              <div className="invmerge-form-group">
                <label>Category</label>
                <PaginatedMultiSelect
                  api="productCategory"
                  GetPaginatedData={GetPaginatedData}
                  getOptionLabel={(opt) => opt.name}
                  value={quickEditCategories}
                  onSelect={setQuickEditCategories}
                  placeholder="Select Category"
                />
              </div>

              <div className="invmerge-form-group">
                <label>Short Description</label>
                <textarea
                  value={quickEditShortDescription}
                  onChange={(event) => setQuickEditShortDescription(event.target.value)}
                  placeholder="Short description"
                  rows={3}
                />
              </div>

              <div className="invmerge-form-group">
                <label>Long Description</label>
                <textarea
                  value={quickEditLongDescription}
                  onChange={(event) => setQuickEditLongDescription(event.target.value)}
                  placeholder="Long description"
                  rows={4}
                />
              </div>

              <div className="invmerge-form-group">
                <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer", fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={quickEditHideFromOtherStores}
                    onChange={(event) => setQuickEditHideFromOtherStores(event.target.checked)}
                    style={{ marginTop: "3px" }}
                  />
                  <span>
                    <span style={{ fontWeight: 600 }}>Hide from other stores</span>
                    <br />
                    <span style={{ color: "#6b7280", fontSize: "13px" }}>
                      Other stores in your business won&apos;t see this product in &quot;Find Product in Other
                      Store&quot;, so it can&apos;t be requested via Stock Transfer.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {(existingGallery.length > 0 || quickEditGalleryFiles.length > 0) && (
            <div className="invmerge-gallery-section">
              <div className="invmerge-gallery-title">Gallery</div>
              <div className="invmerge-gallery-grid">
                {existingGallery.map((item) => (
                  <div className="invmerge-gallery-tile" key={item.id}>
                    {item.type === "video" ? (
                      <video src={item.url} controls />
                    ) : (
                      <img src={item.url} alt="Gallery" />
                    )}
                    <button type="button" onClick={() => removeQuickEditExistingGallery(item)}>×</button>
                  </div>
                ))}

                {quickEditGalleryFiles.map((file, index) => {
                  const url = URL.createObjectURL(file);
                  const isVideo = file.type?.startsWith("video");
                  return (
                    <div className="invmerge-gallery-tile" key={`${file.name}-${index}`}>
                      {isVideo ? <video src={url} controls /> : <img src={url} alt={file.name} />}
                      <button type="button" onClick={() => removeQuickEditGalleryFile(index)}>×</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="invmerge-modal-actions">
            <RegularButton text="Cancel" onClick={closeQuickEditProduct} backgroundColor="#ffffff" color="#282E26" />
            <RegularButton
              text={quickEditSaving ? "Saving..." : "Update Product"}
              onClick={handleQuickEditSubmit}
              backgroundColor="#282e26"
              color="#ffffff"
            />
          </div>
        </div>
      </ReusableModal>
    );
  };

  const renderImportResultModal = () => {
    if (!importResult) return null;

    const statusColor =
      importResult.status === "success"
        ? "#15803d"
        : importResult.status === "partial"
          ? "#b45309"
          : "#b91c1c";

    const statusBg =
      importResult.status === "success"
        ? "#ecfdf3"
        : importResult.status === "partial"
          ? "#fffbeb"
          : "#fef2f2";

    const statusText =
      importResult.status === "success"
        ? "Import Successful"
        : importResult.status === "partial"
          ? "Import Partially Completed"
          : "Import Failed";

    return (
      <ReusableModal
        title="CSV Import Result"
        show={showImportResultModal}
        handleClose={closeImportResultModal}
        width="950px"
      >
        <div style={{ padding: "18px 22px" }}>
          <div
            style={{
              background: statusBg,
              border: `1px solid ${statusColor}`,
              borderRadius: "14px",
              padding: "14px 16px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                color: statusColor,
                fontSize: "18px",
                fontWeight: 800,
                marginBottom: "4px",
              }}
            >
              {statusText}
            </div>

            <div style={{ color: "#374151", fontSize: "13px" }}>
              File: <strong>{importResult.fileName || "-"}</strong>
            </div>

            <div
              style={{ color: "#6b7280", fontSize: "13px", marginTop: "4px" }}
            >
              {importResult.message || "Bulk import completed"}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: "12px",
              marginBottom: "18px",
            }}
          >
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
                borderRadius: "14px",
                padding: "14px",
              }}
            >
              <div
                style={{ fontSize: "12px", color: "#6b7280", fontWeight: 700 }}
              >
                Total Rows
              </div>
              <div
                style={{ fontSize: "26px", fontWeight: 900, color: "#111827" }}
              >
                {importResult.totalProcessedRows || 0}
              </div>
            </div>

            <div
              style={{
                background: "#ecfdf3",
                border: "1px solid #bbf7d0",
                borderRadius: "14px",
                padding: "14px",
              }}
            >
              <div
                style={{ fontSize: "12px", color: "#166534", fontWeight: 700 }}
              >
                Created
              </div>
              <div
                style={{ fontSize: "26px", fontWeight: 900, color: "#15803d" }}
              >
                {importResult.createdCount || 0}
              </div>
            </div>

            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "14px",
                padding: "14px",
              }}
            >
              <div
                style={{ fontSize: "12px", color: "#991b1b", fontWeight: 700 }}
              >
                Failed
              </div>
              <div
                style={{ fontSize: "26px", fontWeight: 900, color: "#b91c1c" }}
              >
                {importResult.errorCount || 0}
              </div>
            </div>

            <div
              style={{
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                borderRadius: "14px",
                padding: "14px",
              }}
            >
              <div
                style={{ fontSize: "12px", color: "#9a3412", fontWeight: 700 }}
              >
                Skipped / Unknown
              </div>
              <div
                style={{ fontSize: "26px", fontWeight: 900, color: "#c2410c" }}
              >
                {importResult.skippedOrUnknownCount || 0}
              </div>
            </div>
          </div>

          {importResult.errors?.length > 0 ? (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "10px",
                }}
              >
                <div>
                  <h5 style={{ margin: 0, fontSize: "16px", fontWeight: 800 }}>
                    Failed Rows
                  </h5>
                  <p
                    style={{
                      margin: "3px 0 0",
                      color: "#6b7280",
                      fontSize: "12px",
                    }}
                  >
                    Showing all failed rows returned by the API.
                  </p>
                </div>

                <RegularButton
                  text="Download Error Report"
                  onClick={downloadImportErrorReport}
                  backgroundColor="#282E26"
                  color="#ffffff"
                />
              </div>

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  overflow: "hidden",
                  maxHeight: "420px",
                  overflowY: "auto",
                }}
              >
                <table className="table" style={{ marginBottom: 0 }}>
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      background: "#f8f9fa",
                      zIndex: 1,
                    }}
                  >
                    <tr>
                      <th style={{ width: "90px" }}>Row</th>
                      <th style={{ width: "260px" }}>Product Title</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.errors.map((item, index) => (
                      <tr key={`${item.row || index}-${index}`}>
                        <td>{item.row || "-"}</td>
                        <td>{item.title || "-"}</td>
                        <td style={{ color: "#b91c1c", fontWeight: 600 }}>
                          {item.error || "Unknown error"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div
              style={{
                background: "#ecfdf3",
                border: "1px solid #bbf7d0",
                borderRadius: "12px",
                padding: "14px",
                color: "#166534",
                fontWeight: 700,
              }}
            >
              No row-level errors found.
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "18px",
            }}
          >
            <RegularButton
              text="Close"
              onClick={closeImportResultModal}
              backgroundColor="#ffffff"
              color="#282E26"
            />
          </div>
        </div>
      </ReusableModal>
    );
  };


  const groupedProducts = useMemo(() => {
    const rows = Array.isArray(visibleProductList) ? visibleProductList : [];
    const explicitInventoryLots = Array.isArray(productInventoryLots) ? productInventoryLots : [];
    const controllerInventoryLots = Array.isArray(stockHistoryData) ? stockHistoryData : [];
    const historyRows = invMergeUniqueRowsByKey(
      explicitInventoryLots.length > 0 ? explicitInventoryLots : controllerInventoryLots,
    );
    const productMap = new Map();

    rows.forEach((product) => {
      const title = invMergeGetTitle(product);
      const key = invMergeNormalizeText(title || product?.id);
      const categories = invMergeGetCategories(product);
      const variants = invMergeGetSegments(product);

      if (!productMap.has(key)) {
        productMap.set(key, {
          key,
          title,
          primaryProduct: product,
          categories: new Set(),
          variants: [],
          history: [],
          totalStock: 0,
          stockValue: 0,
          imageUrl: buildMediaUrl(product?.product_image),
          productIds: new Set([product?.id, product?.product].filter(Boolean)),
        });
      }

      const group = productMap.get(key);
      if (product?.id) group.productIds.add(product.id);
      if (product?.product) group.productIds.add(product.product);
      categories.forEach((categoryName) => group.categories.add(categoryName));
      group.variants.push(...variants);
      if (!group.imageUrl && product?.product_image) {
        group.imageUrl = buildMediaUrl(product.product_image);
      }
    });

    historyRows.forEach((entry) => {
      const historyTitle = invMergeGetHistoryTitle(entry);
      const historyKey = invMergeNormalizeText(historyTitle);

      if (!historyKey) return;

      let matchingKey = invMergeFindMatchingProductKey(productMap, entry, historyKey);

      if (!productMap.has(matchingKey)) {
        productMap.set(matchingKey, {
          key: matchingKey,
          title: historyTitle,
          primaryProduct: entry,
          categories: new Set(invMergeGetCategories(entry)),
          variants: [],
          history: [],
          totalStock: 0,
          stockValue: 0,
          imageUrl: buildMediaUrl(entry?.product_image || ""),
          productIds: new Set([typeof entry?.product === "object" ? entry.product?.id : entry?.product].filter(Boolean)),
        });
      }

      const historyGroup = productMap.get(matchingKey);
      const entryProductId = typeof entry?.product === "object" ? entry.product?.id : entry?.product;
      if (entryProductId) historyGroup.productIds.add(entryProductId);
      historyGroup.history.push(entry);
    });

    return Array.from(productMap.values())
      .map((group) => {
        const sortedHistory = [...(group.history || [])].sort((a, b) => {
          const dateA = new Date(a?.date || a?.created_at || 0).getTime();
          const dateB = new Date(b?.date || b?.created_at || 0).getTime();
          return dateB - dateA;
        });
        const historyLots = sortedHistory.flatMap((entry) => invMergeGetStockLotsFromHistory(entry));
        const fallbackLots = invMergeGetStockLotsFromVariants(group.variants || []);
        const stockLots = invMergeUniqueRowsByKey(historyLots.length > 0 ? historyLots : fallbackLots)
          .sort((a, b) => new Date(b?.date || 0).getTime() - new Date(a?.date || 0).getTime());
        const uomSummary = invMergeBuildUomSummary(stockLots);
        const totalStock = stockLots.reduce(
          (sum, lot) => sum + invMergeGetLotCurrentStock(lot),
          0,
        );
        const stockValue = stockLots.reduce(
          (sum, lot) => sum + invMergeGetLotSalesValue(lot),
          0,
        );
        const costValue = stockLots.reduce(
          (sum, lot) => sum + invMergeGetLotCostValue(lot),
          0,
        );
        const profitValue = stockLots.reduce(
          (sum, lot) => sum + invMergeGetLotProfitValue(lot),
          0,
        );
        const profitPercent = invMergeGetProfitPercent(profitValue, costValue);

        return {
          ...group,
          categories: Array.from(group.categories),
          totalStock,
          stockValue,
          costValue,
          profitValue,
          profitPercent,
          history: sortedHistory,
          stockLots,
          uomSummary,
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [visibleProductList, productInventoryLots, stockHistoryData]);

  const filteredGroupedProducts = useMemo(() => {
    const query = invMergeNormalizeText(inventorySearch);
    if (!query) return groupedProducts;

    return groupedProducts.filter((group) => {
      const searchable = [
        group.title,
        invMergeGetBarcode(group.primaryProduct || group.variants?.[0]),
        group.categories.join(" "),
        ...group.variants.map((variant) => `${invMergeGetUom(variant)} ${invMergeGetBarcode(variant)} ${invMergeGetPurchase(variant)} ${invMergeGetSelling(variant)}`),
        ...(group.history || []).map(
          (entry) =>
            `${invMergeGetHistoryVendor(entry)} ${invMergeGetHistoryCategory(entry)} ${invMergeGetHistoryUom(entry)} ${invMergeGetHistoryStockAdded(entry)} ${entry.segment_status || ""}`,
        ),
        ...(group.stockLots || []).map(
          (lot) =>
            `${lot.vendor || ""} ${lot.category || ""} ${lot.uom || ""} ${lot.batch || ""} ${lot.barcode || ""} ${lot.purchasePerPc || ""} ${lot.sellingPerPc || ""} ${invMergeGetLotMarginPerPc(lot)} ${invMergeGetLotProfitValue(lot)}`,
        ),
      ]
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [groupedProducts, inventorySearch]);

  const totalGroupedProducts = groupedProducts.length;
  const totalCategories = new Set(groupedProducts.flatMap((group) => group.categories)).size;
  const totalInventoryUnits = groupedProducts.reduce((sum, group) => sum + group.totalStock, 0);
  const totalInventoryValue = groupedProducts.reduce((sum, group) => sum + group.stockValue, 0);
  const totalInventoryCost = groupedProducts.reduce((sum, group) => sum + (group.costValue || 0), 0);
  const totalPotentialProfit = groupedProducts.reduce((sum, group) => sum + (group.profitValue || 0), 0);
  const totalProfitPercent = invMergeGetProfitPercent(totalPotentialProfit, totalInventoryCost);
  const lowStockCount = groupedProducts.filter((group) => group.totalStock > 0 && group.totalStock <= 20).length;
  const noStockCount = groupedProducts.filter((group) => group.totalStock <= 0).length;
  const totalPages = Math.max(1, Math.ceil((totalCount || groupedProducts.length || 0) / Math.max(resolvedPageSize, 1)));
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  useEffect(() => {
    if (showModal && isEditMode && editingProductData) {
      applyEditFallbackValues(editingProductData);
    }
  }, [showModal, isEditMode, editingProductData]);

  return (
    <>
      <ToastContainer />
      <style>{mergedInventoryProductStyles}</style>
      {renderImportResultModal()}
      {renderQuickEditProductModal()}
      {renderProductViewModal()}
      {renderAddStockModal()}
      {renderInlineSegmentModal()}
      <div className="responsive-box">
        <div className="inventory-header">
          <div className="inventory-title">Inventory & Products</div>
          <div className="btn-position">
            <i
              ref={infoRef}
              className="bi bi-info-circle-fill"
              tabIndex="0"
              style={{ fontSize: "18px", cursor: "pointer", marginTop: "8px" }}
              data-bs-content={`
    <div style="width:260px;padding:12px">
      <h6 style="font-weight:600;margin-bottom:6px">
        Import Instructions
      </h6>

      <p style="font-size:13px;color:#6c757d;margin-bottom:10px">
        Use our standardized CSV format before uploading.
      </p>

      <a
        href="/products.csv"
        download
        style="
          display:block;
          text-align:center;
          width:100%;
          background:#2f80ed;
          color:white;
          border:none;
          padding:8px;
          border-radius:6px;
          margin-bottom:8px;
          text-decoration:none;
        "
      >
        Download CSV Template
      </a>
    </div>
  `}
            />
            <CustomButton
              text="Import CSV"
              icon={ImportIcon}
              onClick={() => fileInputRef.current.click()}
              backgroundColor="#4a5546"
              color="#fff"
            />

            <CustomButton
              text="Export Products CSV"
              icon={ImportIcon}
              onClick={handleExportProductsCsv}
              backgroundColor="#0f766e"
              color="#fff"
            />

            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleImportProducts(file);
                }
              }}
            />
            <CustomButton
              text="Add UOM"
              icon={AddUomIcon}
              onClick={() => setShowUomModal(true)}
              color="#FFFFFF"
              backgroundColor="#4a5546"
              iconPosition="right"
            />
            <CustomButton
              text="Stock History"
              icon={HistoryIcon}
              onClick={handleStockHistoryClick}
              color="#FFFFFF"
              backgroundColor="#4a5546"
              iconPosition="right"
            />
            <CustomButton
              text="Add Stock"
              icon={stockIcon}
              onClick={openGlobalStockModal}
              color="#000000"
              backgroundColor="#FFAF32"
              iconPosition="right"
            />
            <CustomButton
              text="Add Segment"
              icon={segmentIcon}
              onClick={openGlobalSegmentModal}
              color="#FFFFFF"
              backgroundColor="#4a5546"
              iconPosition="right"
            />

            <CustomButton
              text="Add Product"
              icon={productIcon}
              onClick={handleOpenModal}
              color="#000000"
              backgroundColor="#FFAF32"
              iconPosition="right"
            />
          </div>
        </div>
        <script
          src="https://unpkg.com/@dotlottie/player-component@2.7.12/dist/dotlottie-player.mjs"
          type="module"
        ></script>
        <dotlottie-player
          src="https://lottie.host/80848925-1f8b-4855-80ef-c76af1d6c6c4/ZPAlGGMOUV.lottie"
          background="transparent"
          speed="1"
          style={{ width: "300px", height: "300px" }}
          loop
          autoplay
        ></dotlottie-player>
        <ReusableModal
          title={isEditMode ? "Edit Product" : "Add Product"}
          show={showModal}
          handleClose={handleCloseModal}
          width="1120px"
        >
          <form>
            <div style={{ padding: "18px 22px", background: "#f8fafc" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 360px",
                  gap: "20px",
                  alignItems: "start",
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px" }}>
                    {renderSectionTitle("Basic Product Details", "Keep product identity, category and supplier information organized.")}

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
                      <ReusableInput
                        type="text"
                        label="Product Title"
                        value={productTitle}
                        onChange={handleProductTitleChange}
                        onBlur={handleTitleBlur}
                        required
                        error={errors.title}
                        width="100%"
                        autoCapitalize={true}
                      />

                      <PaginatedMultiSelect
                        api="productCategory"
                        GetPaginatedData={GetPaginatedData}
                        getOptionLabel={(opt) => opt.name}
                        value={getEffectiveSelectedCategory()}
                        onSelect={setSelectedCategory}
                        placeholder="Select Category *"
                      />

                      <PaginatedSearchSelect
                        api="uomData"
                        GetPaginatedData={GetPaginatedData}
                        getOptionLabel={(opt) => opt.uom_title}
                        onSelect={(opt) => setSelectedUom(opt)}
                        placeholder="Select UOM *"
                        value={getEffectiveSelectedUom()}
                      />

                      <PaginatedSearchSelect
                        api="vendorDepartmentData"
                        GetPaginatedData={GetPaginatedData}
                        getOptionLabel={(opt) => opt.name}
                        onSelect={(opt) => {
                          if (opt?.id === "add-vendor") {
                            setShowVendorModal(true);
                          } else {
                            setSelectedVendorId(opt?.id || "");
                            setSelectedVendorName(opt?.name || "");
                          }
                        }}
                        placeholder="Select Vendor"
                        value={getEffectiveSelectedVendor()}
                        filterFn={(opt) => opt.type === "purchase" && !opt.is_deleted}
                      />

                      <PaginatedSearchSelect
                        api="getWeight"
                        GetPaginatedData={GetPaginatedData}
                        getOptionLabel={(opt) => opt.weight_title}
                        onSelect={(opt) => setSelectedWeightUnit(opt)}
                        value={getEffectiveSelectedWeightUnit()}
                        placeholder="Select Weight Unit"
                      />

                      <ReusableInput
                        type="number"
                        label="Weight"
                        value={weightValue}
                        onChange={(e) => setWeightValue(e.target.value)}
                        width="100%"
                      />
                    </div>
                  </div>

                  <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px" }}>
                    {renderSectionTitle("Inventory & Price Details", "Price is locked during edit because existing stock keeps its original price.")}

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
                      <ReusableInput
                        type="number"
                        label="Purchase Price"
                        value={purchasePrice}
                        onChange={handlePurchasePriceChange}
                        onBlur={handlePurchasePriceBlur}
                        required
                        error={errors.purchasePrice}
                        width="100%"
                        disabled={isEditMode}
                      />

                      <ReusableInput
                        type="number"
                        value={sellingPrice}
                        label="Selling Price"
                        onChange={handleSellingPriceChange}
                        onBlur={handleSellingPriceBlur}
                        width="100%"
                        required
                        error={errors.sellingPrice || sellingPriceError}
                        disabled={isEditMode}
                      />

                      <ReusableInput
                        type="number"
                        value={openingInventory}
                        label="Opening Inventory"
                        onChange={handleOpeningInventoryChange}
                        onFocus={handleFocusOpeningInventory}
                        onBlur={handleBlurOpeningInventory}
                        width="100%"
                        disabled={isEditMode}
                      />

                      <ReusableInput
                        type="number"
                        label="Total Pc in UOM"
                        value={totalPcInUom}
                        onChange={handleTotalPcInUomChange}
                        onBlur={handleTotalPcBlur}
                        required
                        error={errors.totalPcInUom}
                        width="100%"
                        disabled={isEditMode}
                      />

                      <ReusableInput
                        type="date"
                        label="Expiry Date (optional)"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                        width="100%"
                        disabled={isEditMode}
                      />
                    </div>

                    {isEditMode && (
                      <div
                        style={{
                          background: "#fff7ed",
                          border: "1px solid #fed7aa",
                          color: "#9a3412",
                          borderRadius: "12px",
                          padding: "12px 14px",
                          fontSize: "13px",
                          fontWeight: 700,
                          marginTop: "14px",
                        }}
                      >
                        Purchase Price and Selling Price are locked for current stock. To use a new price, add new stock/batch for this same product with the new purchase and selling price.
                      </div>
                    )}
                  </div>

                  <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px" }}>
                    {renderSectionTitle("Barcode & Descriptions")}

                    <div className="barcode-row" style={{ marginBottom: "12px" }}>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={autoGenerateBarcode}
                          onChange={(e) => setAutoGenerateBarcode(e.target.checked)}
                          disabled={isEditMode}
                        />
                        Auto Generate Barcode
                      </label>
                    </div>

                    {!autoGenerateBarcode && (
                      <div style={{ marginBottom: "18px" }}>
                        <ReusableInput
                          type="text"
                          label="Barcode"
                          value={barcode}
                          onChange={handleBarcodeChange}
                          required
                          error={errors.barcode}
                          width="100%"
                          disabled={isEditMode}
                        />
                      </div>
                    )}

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
                      <ReusableInput
                        type="text"
                        name="shortDescription"
                        label="Short Description"
                        value={shortDescription}
                        onChange={(e) => setShortDescription(e.target.value)}
                        width="100%"
                      />

                      <div>
                        <label style={{ fontSize: "14px", marginBottom: "6px", display: "block", fontWeight: 600 }}>
                          Description
                        </label>
                        <textarea
                          value={longDescription}
                          onChange={(e) => setLongDescription(e.target.value)}
                          rows={5}
                          style={{
                            width: "100%",
                            border: "1px solid #d1d5db",
                            borderRadius: "10px",
                            padding: "10px",
                            fontSize: "14px",
                            resize: "vertical",
                            outline: "none",
                          }}
                          placeholder="Enter full description"
                        />
                      </div>
                    </div>

                    <div style={{ marginTop: "16px" }}>
                      <label style={{ display: "flex", alignItems: "flex-start", gap: "10px", cursor: "pointer", fontSize: "14px" }}>
                        <input
                          type="checkbox"
                          checked={hideFromOtherStores}
                          onChange={(e) => setHideFromOtherStores(e.target.checked)}
                          style={{ marginTop: "3px" }}
                        />
                        <span>
                          <span style={{ fontWeight: 600 }}>Hide from other stores</span>
                          <br />
                          <span style={{ color: "#6b7280" }}>
                            When enabled, other stores in your business won&apos;t see this product in
                            &quot;Find Product in Other Store&quot;, so it can&apos;t be requested via Stock Transfer.
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px", position: "sticky", top: "12px" }}>
                  {renderSectionTitle("Images & Gallery", "Upload primary image and gallery media.")}

                  <div className="image-upload-card">
                    <div style={{ marginTop: "8px", fontSize: "14px", fontWeight: 700 }}>
                      Primary Image
                    </div>
                    <div
                      className="primary-image-box"
                      onClick={() => document.getElementById("primaryImageInput").click()}
                    >
                      {getDisplayImagePreview() ? (
                        <>
                          <img
                            src={getDisplayImagePreview()}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePrimaryImageWithTracking(e);
                            }}
                            style={{
                              position: "absolute",
                              top: "5px",
                              right: "5px",
                              background: "rgba(0,0,0,0.6)",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              width: "22px",
                              height: "22px",
                              cursor: "pointer",
                            }}
                          >
                            ×
                          </button>
                        </>
                      ) : (
                        <span style={{ fontSize: "30px", color: "#9aa0a6" }}>+</span>
                      )}
                    </div>

                    <input
                      id="primaryImageInput"
                      type="file"
                      accept="image/*"
                      onChange={handlePrimaryImageChangeWithTracking}
                      style={{ display: "none" }}
                    />
                  </div>

                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    ref={mediaInputRef}
                    style={{ display: "none" }}
                    onChange={handleGalleryUploadWithTracking}
                  />

                  <div className="gallery-grid" style={{ marginTop: "14px" }}>
                    {getDisplayGalleryPreview().map((item, index) => (
                      <div key={index} className="gallery-item">
                        {item.type === "image" ? (
                          <img src={item.url} className="gallery-media" />
                        ) : (
                          <video src={item.url} className="gallery-media" />
                        )}

                        <button
                          type="button"
                          onClick={() => handleRemoveDisplayGalleryImage(item)}
                          className="gallery-remove-btn"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    <div className="gallery-add-box" onClick={() => mediaInputRef.current.click()}>
                      <span>+</span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="form-actions"
                style={{ marginTop: "22px", gap: "10px", justifyContent: "flex-end" }}
              >
                {!isEditMode && (
                  <RegularButton
                    text="Clear"
                    onClick={resetForm}
                    backgroundColor="#ffffff"
                    color="#282E26"
                  />
                )}
                <RegularButton
                  text={isEditMode ? "Update" : "Save"}
                  onClick={handleValidatedAddProduct}
                  backgroundColor="#282e26"
                />
              </div>
            </div>
          </form>
        </ReusableModal>

        <div className="invmerge-toolbar">
          <div className="invmerge-search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search product, category, UOM, price..."
              value={inventorySearch}
              onChange={(event) => setInventorySearch(event.target.value)}
            />
          </div>

          <div className="invmerge-summary-row">
            <InvMergeMiniMetric icon={Package} label="Products" value={invMergeFormatCount(totalGroupedProducts)} tone="blue" />
            <InvMergeMiniMetric icon={Boxes} label="Total Stock" value={invMergeFormatCount(totalInventoryUnits)} tone="green" />
            <InvMergeMiniMetric icon={Wallet} label="Stock Value" value={`₹${invMergeFormatMoney(totalInventoryValue)}`} tone="purple" />
            <InvMergeMiniMetric icon={TrendingUp} label="Gross Profit" value={`₹${invMergeFormatMoney(totalPotentialProfit)} (${invMergeFormatPercent(totalProfitPercent)})`} tone="profit" />
            <InvMergeMiniMetric icon={AlertTriangle} label="Low / Empty" value={`${invMergeFormatCount(lowStockCount)} / ${invMergeFormatCount(noStockCount)}`} tone="amber" />
          </div>
        </div>

        <div className="invmerge-panel">
          <div className="invmerge-panel-head">
            <div>
              <h3>Product Wise Inventory</h3>
              <p>One row per product. Expand to view UOM, price and stock. Use row actions to view, edit or add segment.</p>
            </div>
            <div className="invmerge-head-badges">
              <span className="invmerge-pill green">{activeProductList.length} Active</span>
              <span className="invmerge-pill red">{softDeletedProductList.length} Soft Deleted</span>
              <span className="invmerge-pill blue">{invMergeFormatCount(totalCategories)} Categories</span>
              <label className="invmerge-toggle-deleted">
                <input
                  type="checkbox"
                  checked={showDeletedProducts}
                  onChange={(event) => setShowDeletedProducts(event.target.checked)}
                />
                Show deleted
              </label>
            </div>
          </div>

          {showDeletedProducts && softDeletedProductList.length > 0 && (
            <div className="invmerge-warning-note">
              Deleted / inactive products are included. They may not have active UOM, purchase price or selling price.
            </div>
          )}

          {loading || stockHistoryLoading || productInventoryLotsLoading ? (
            <div className="invmerge-empty-state">Loading products and stock movement...</div>
          ) : filteredGroupedProducts.length === 0 ? (
            <div className="invmerge-empty-state">No matching products found.</div>
          ) : (
            <div className="invmerge-list">
              {filteredGroupedProducts.map((group) => (
                <InvMergeProductRow
                  key={group.key}
                  group={group}
                  expanded={expandedProductKey === group.key}
                  onToggle={() =>
                    setExpandedProductKey((current) =>
                      current === group.key ? null : group.key,
                    )
                  }
                  onView={() => handleViewProduct(group.primaryProduct)}
                  onEdit={() => openQuickEditProduct(group.primaryProduct)}
                  onAddStock={() => openStockForProduct(group.primaryProduct)}
                  onAddSegment={() => openSegmentForProduct(group.primaryProduct)}
                  onEditExpiry={handleEditLotExpiry}
                />
              ))}
            </div>
          )}

          <div className="invmerge-footer">
            <div>
              Showing {invMergeFormatCount(filteredGroupedProducts.length)} grouped products
              {totalCount ? ` from ${invMergeFormatCount(totalCount)} records` : ""}
            </div>
            <div className="invmerge-pagination">
              <button
                type="button"
                disabled={!canGoPrevious}
                onClick={() => canGoPrevious && handlePageChange(currentPage - 1)}
              >
                Previous
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button
                type="button"
                disabled={!canGoNext}
                onClick={() => canGoNext && handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
        <AddVendorModal
          show={showVendorModal}
          handleClose={handleCloseVendorModal}
          onVendorAdded={handleVendorAdded}
          updateVendors={updateVendors}
        />
        <AddVendorModal
          show={stockShowVendorModal}
          handleClose={stockHandleCloseVendorModal}
          onVendorAdded={stockHandleVendorAdded}
          updateVendors={stockUpdateVendors}
        />
        <UomView
          show={showUomModal}
          handleClose={() => {
            handleCloseUomModal();
            setShowUomModal(false);
          }}
          onUomAdded={""}
        />
      </div>
    </>
  );
};


const mergedInventoryProductStyles = `
  .responsive-box {
    color: #0f172a;
  }

  .inventory-header {
    align-items: center;
    border-bottom: 1px solid #e8edf3;
    padding-bottom: 16px;
    margin-bottom: 18px;
  }

  .inventory-title {
    font-size: 27px;
    font-weight: 950;
    letter-spacing: -0.04em;
    color: #17201a;
  }

  .invmerge-toolbar {
    display: grid;
    grid-template-columns: minmax(280px, 420px) minmax(0, 1fr);
    gap: 14px;
    align-items: stretch;
    margin: 14px 0 18px;
  }

  .invmerge-search-box {
    min-height: 50px;
    border: 1px solid #d7dee8;
    border-radius: 14px;
    display: flex;
    align-items: center;
    gap: 10px;
    background: #ffffff;
    padding: 0 15px;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.045);
  }

  .invmerge-search-box svg {
    color: #64748b;
    flex-shrink: 0;
  }

  .invmerge-search-box input {
    border: none;
    outline: none;
    width: 100%;
    color: #0f172a;
    font-size: 14px;
    font-weight: 750;
    background: transparent;
  }

  .invmerge-summary-row {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
  }

  .invmerge-mini-metric {
    min-height: 50px;
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 11px;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.045);
  }

  .invmerge-mini-icon {
    width: 34px;
    height: 34px;
    border-radius: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .invmerge-mini-metric strong {
    display: block;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
    line-height: 1.1;
  }

  .invmerge-mini-metric p {
    margin: 2px 0 0;
    color: #64748b;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .invmerge-mini-metric.blue .invmerge-mini-icon {
    color: #2563eb;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
  }

  .invmerge-mini-metric.green .invmerge-mini-icon {
    color: #059669;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  .invmerge-mini-metric.purple .invmerge-mini-icon {
    color: #7c3aed;
    background: #f3e8ff;
    border: 1px solid #ddd6fe;
  }

  .invmerge-mini-metric.amber .invmerge-mini-icon {
    color: #d97706;
    background: #fffbeb;
    border: 1px solid #fde68a;
  }

  .invmerge-mini-metric.profit .invmerge-mini-icon {
    color: #047857;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  .invmerge-mini-metric.profit {
    border-color: #bbf7d0;
    background:
      linear-gradient(135deg, rgba(236, 253, 245, 0.92), #ffffff);
  }

  .invmerge-panel {
    border: 1px solid #dbe3ec;
    border-radius: 22px;
    background: #ffffff;
    box-shadow: 0 16px 42px rgba(15, 23, 42, 0.055);
    overflow: hidden;
  }

  .invmerge-panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    padding: 18px 18px 14px;
    border-bottom: 1px solid #edf2f7;
    background: radial-gradient(circle at top left, rgba(13, 83, 47, 0.08), transparent 32%), linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  }

  .invmerge-panel-head h3 {
    margin: 0;
    color: #0f172a;
    font-size: 19px;
    font-weight: 950;
    letter-spacing: -0.035em;
  }

  .invmerge-panel-head p {
    margin: 5px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .invmerge-head-badges {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .invmerge-pill,
  .invmerge-toggle-deleted {
    display: inline-flex;
    align-items: center;
    min-height: 30px;
    border-radius: 999px;
    padding: 0 11px;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .invmerge-pill.green {
    color: #047857;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  .invmerge-pill.red {
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
  }

  .invmerge-pill.blue {
    color: #1d4ed8;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
  }

  .invmerge-toggle-deleted {
    gap: 7px;
    color: #475569;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    cursor: pointer;
  }

  .invmerge-warning-note {
    margin: 14px 18px 0;
    background: #fffbeb;
    border: 1px solid #fde68a;
    color: #92400e;
    border-radius: 12px;
    padding: 10px 12px;
    font-size: 13px;
    font-weight: 750;
  }

  .invmerge-list {
    display: grid;
    gap: 0;
  }

  .invmerge-row {
    border-bottom: 1px solid #edf2f7;
  }

  .invmerge-row:last-child {
    border-bottom: none;
  }

  .invmerge-row-main {
    width: 100%;
    min-height: 78px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    gap: 16px;
    align-items: center;
    padding: 14px 18px;
    transition: background 0.16s ease;
  }

  .invmerge-row-main:hover,
  .invmerge-row.expanded .invmerge-row-main {
    background: #fbfdff;
  }

  .invmerge-row-trigger {
    min-width: 0;
    border: none;
    background: transparent;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 13px;
    text-align: left;
    cursor: pointer;
  }

  .invmerge-product-img-wrap {
    position: relative;
    flex-shrink: 0;
  }

  .invmerge-expiry-alert-chip {
    position: absolute;
    top: -9px;
    right: -10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 6px;
    border-radius: 999px;
    border: 1.5px solid #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
    font-size: 9px;
    font-weight: 800;
    line-height: 1.3;
    white-space: nowrap;
    color: #ffffff;
    z-index: 1;
  }

  .invmerge-expiry-alert-chip.green {
    background: #16a34a;
  }

  .invmerge-expiry-alert-chip.yellow {
    background: #d97706;
  }

  .invmerge-expiry-alert-chip.red {
    background: #dc2626;
  }

  .invmerge-product-img {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: #f8fafc;
    border: 1px solid #dbe3ec;
    color: #0d532f;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
    position: relative;
  }

  .invmerge-product-img img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .invmerge-product-img.has-image .invmerge-placeholder-icon {
    display: none;
  }

  .invmerge-product-info {
    min-width: 0;
  }

  .invmerge-product-info strong {
    display: block;
    color: #0f172a;
    font-size: 14px;
    font-weight: 950;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .invmerge-product-info span {
    display: block;
    margin-top: 4px;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .invmerge-row-stats {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
  }

  .invmerge-row-stats > span:not(.invmerge-stock-badge) {
    display: grid;
    gap: 2px;
    text-align: right;
  }

  .invmerge-row-stats small {
    color: #64748b;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .invmerge-row-stats b {
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
  }

  .invmerge-stock-badge {
    min-width: 76px;
    height: 28px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 950;
  }

  .invmerge-stock-badge.success {
    color: #047857;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  .invmerge-stock-badge.info {
    color: #1d4ed8;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
  }

  .invmerge-stock-badge.warning {
    color: #b45309;
    background: #fffbeb;
    border: 1px solid #fde68a;
  }

  .invmerge-stock-badge.danger {
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
  }

  .invmerge-row-actions {
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .invmerge-action-btn,
  .invmerge-icon-toggle {
    border: 1px solid #dbe3ec;
    background: #ffffff;
    color: #0f172a;
    border-radius: 999px;
    min-height: 32px;
    padding: 0 10px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 950;
    cursor: pointer;
    transition: 0.16s ease;
  }

  .invmerge-action-btn:hover,
  .invmerge-icon-toggle:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgba(15, 23, 42, 0.10);
  }

  .invmerge-action-btn.view {
    color: #1d4ed8;
    background: #eff6ff;
    border-color: #bfdbfe;
  }

  .invmerge-action-btn.edit {
    color: #0d532f;
    background: #ecfdf5;
    border-color: #a7f3d0;
  }

  .invmerge-action-btn.segment {
    color: #b45309;
    background: #fffbeb;
    border-color: #fde68a;
  }

  .invmerge-icon-toggle {
    width: 34px;
    padding: 0;
    justify-content: center;
  }

  .invmerge-row.expanded .invmerge-icon-toggle svg {
    transform: rotate(180deg);
  }

  .invmerge-expanded {
    padding: 0 18px 18px 79px;
    background: #f8fafc;
  }

  .invmerge-expanded-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.3fr) minmax(320px, 0.7fr);
    gap: 12px;
  }

  .invmerge-expanded-card {
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    background: #ffffff;
    padding: 13px;
  }

  .invmerge-history-list {
    display: grid;
    gap: 10px;
  }

  .invmerge-history-item {
    display: grid;
    gap: 10px;
    padding: 12px;
    border-radius: 14px;
    background:
      linear-gradient(135deg, rgba(248, 250, 252, 0.98), #ffffff);
    border: 1px solid #e8eef5;
  }

  .invmerge-history-topline {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .invmerge-history-item strong {
    display: block;
    color: #0f172a;
    font-size: 12px;
    font-weight: 950;
  }

  .invmerge-history-item span {
    display: block;
    margin-top: 4px;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
    line-height: 1.35;
  }

  .invmerge-history-topline b {
    color: #047857;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: 999px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 950;
    white-space: nowrap;
  }

  .invmerge-history-meta-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 7px;
  }

  .invmerge-history-meta {
    min-width: 0;
    border: 1px solid #edf2f7;
    background: #f8fafc;
    border-radius: 10px;
    padding: 7px 8px;
  }

  .invmerge-history-meta small {
    display: block;
    color: #64748b;
    font-size: 9px;
    line-height: 1.1;
    font-weight: 950;
    letter-spacing: 0.045em;
    text-transform: uppercase;
  }

  .invmerge-history-meta strong {
    display: block;
    margin-top: 4px;
    color: #0f172a;
    font-size: 11px;
    line-height: 1.25;
    font-weight: 900;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .invmerge-history-empty {
    min-height: 78px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    border: 1px dashed #cbd5e1;
    border-radius: 13px;
    background: #f8fafc;
    color: #64748b;
    font-size: 12px;
    font-weight: 850;
    padding: 14px;
  }

  .invmerge-card-title {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
    margin-bottom: 10px;
  }

  .invmerge-card-title svg {
    color: #0d532f;
  }

  .invmerge-table-wrap {
    overflow-x: auto;
  }

  .invmerge-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }

  .invmerge-table th {
    padding: 9px 10px;
    color: #64748b;
    background: #f8fafc;
    text-align: left;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }

  .invmerge-table td {
    padding: 10px;
    color: #334155;
    font-weight: 750;
    border-bottom: 1px solid #f1f5f9;
    white-space: nowrap;
  }

  .invmerge-strong-green,
  .invmerge-positive {
    color: #059669 !important;
    font-weight: 950 !important;
  }

  .profit {
    color: #047857 !important;
    font-weight: 950 !important;
  }

  .loss {
    color: #dc2626 !important;
    font-weight: 950 !important;
  }

  .neutral {
    color: #64748b !important;
    font-weight: 900 !important;
  }

  .invmerge-profit-stat.profit b,
  .invmerge-profit-summary.profit strong {
    color: #047857 !important;
  }

  .invmerge-profit-stat.loss b,
  .invmerge-profit-summary.loss strong {
    color: #dc2626 !important;
  }

  .invmerge-negative {
    color: #dc2626 !important;
    font-weight: 950 !important;
  }

  .invmerge-empty-state {
    min-height: 140px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 22px;
    font-size: 13px;
    font-weight: 850;
  }

  .invmerge-footer {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    padding: 13px 18px;
    border-top: 1px solid #edf2f7;
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
    background: #fbfdff;
  }

  .invmerge-pagination {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .invmerge-pagination button {
    border: 1px solid #dbe3ec;
    background: #ffffff;
    color: #0f172a;
    border-radius: 999px;
    min-width: 78px;
    height: 34px;
    padding: 0 12px;
    font-size: 12px;
    font-weight: 900;
    cursor: pointer;
  }

  .invmerge-pagination button:disabled {
    opacity: 0.48;
    cursor: not-allowed;
    background: #f8fafc;
    color: #94a3b8;
  }

  .invmerge-pagination span {
    min-width: 82px;
    text-align: center;
    color: #334155;
    font-weight: 950;
  }

  .invmerge-edit-modal {
    padding: 18px 22px;
    background: #f8fafc;
  }

  .invmerge-edit-note {
    padding: 11px 13px;
    border-radius: 12px;
    margin-bottom: 14px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1d4ed8;
    font-size: 13px;
    font-weight: 800;
  }

  .invmerge-edit-grid {
    display: grid;
    grid-template-columns: 260px minmax(0, 1fr);
    gap: 16px;
  }

  .invmerge-edit-media-card,
  .invmerge-edit-fields,
  .invmerge-gallery-section {
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    background: #ffffff;
    padding: 14px;
  }

  .invmerge-field-label,
  .invmerge-form-group label,
  .invmerge-gallery-title {
    display: block;
    margin-bottom: 7px;
    color: #334155;
    font-size: 12px;
    font-weight: 950;
    letter-spacing: 0.02em;
  }

  .invmerge-primary-upload {
    width: 100%;
    height: 215px;
    border-radius: 14px;
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .invmerge-primary-upload img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .invmerge-primary-upload span {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #64748b;
    font-size: 13px;
    font-weight: 850;
  }

  .invmerge-primary-upload input,
  .invmerge-gallery-upload input {
    display: none;
  }

  .invmerge-gallery-upload {
    margin-top: 12px;
    height: 42px;
    border: 1px solid #dbe3ec;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    cursor: pointer;
    color: #0d532f;
    font-size: 13px;
    font-weight: 950;
    background: #ecfdf5;
  }

  .invmerge-edit-fields {
    display: grid;
    gap: 12px;
  }

  .invmerge-form-group input,
  .invmerge-form-group textarea {
    width: 100%;
    border: 1px solid #cbd5e1;
    border-radius: 12px;
    background: #ffffff;
    color: #0f172a;
    font-size: 14px;
    font-weight: 750;
    padding: 11px 12px;
    outline: none;
  }

  .invmerge-form-group input {
    height: 44px;
  }

  .invmerge-form-group input:focus,
  .invmerge-form-group textarea:focus {
    border-color: #0d532f;
    box-shadow: 0 0 0 4px rgba(13, 83, 47, 0.10);
  }

  .invmerge-gallery-section {
    margin-top: 16px;
  }

  .invmerge-gallery-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 10px;
  }

  .invmerge-gallery-tile {
    position: relative;
    height: 92px;
    border-radius: 12px;
    overflow: hidden;
    background: #f3f4f6;
    border: 1px solid #e2e8f0;
  }

  .invmerge-gallery-tile img,
  .invmerge-gallery-tile video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .invmerge-gallery-tile button {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.74);
    color: #ffffff;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
  }

  .invmerge-modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 16px;
  }

  .invmerge-action-btn.stock {
    color: #92400e;
    background: #fffbeb;
    border: 1px solid #fde68a;
  }

  .invmerge-action-btn.stock:hover {
    background: #fef3c7;
    border-color: #f59e0b;
  }

  .invmerge-stock-modal,
  .invmerge-segment-modal {
    padding: 18px 22px;
    background: #f8fafc;
  }

  .invmerge-stock-scan {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 12px;
    border-radius: 16px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    margin-bottom: 14px;
  }

  .invmerge-stock-scan img {
    width: 34px;
    height: 34px;
    object-fit: contain;
  }

  .invmerge-stock-section {
    display: grid;
    gap: 14px;
  }

  .invmerge-stock-row,
  .invmerge-segment-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
    align-items: end;
  }

  .invmerge-stock-field,
  .invmerge-stock-control {
    position: relative;
    min-width: 0;
  }

  .invmerge-stock-field input,
  .invmerge-stock-field select,
  .invmerge-form-group input,
  .invmerge-form-group textarea {
    width: 100%;
    min-height: 45px;
    border: 1px solid #cbd5e1;
    border-radius: 12px;
    background: #ffffff;
    color: #0f172a;
    padding: 9px 12px;
    font-size: 13px;
    font-weight: 750;
    outline: none;
  }

  .invmerge-stock-field input:focus,
  .invmerge-stock-field select:focus,
  .invmerge-form-group input:focus,
  .invmerge-form-group textarea:focus {
    border-color: #0d532f;
    box-shadow: 0 0 0 4px rgba(13, 83, 47, 0.10);
  }

  .invmerge-stock-alert {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 5px 12px;
    align-items: center;
    padding: 12px;
    margin-bottom: 14px;
    border-radius: 14px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    color: #92400e;
    font-size: 13px;
    font-weight: 750;
  }

  .invmerge-stock-alert strong {
    color: #78350f;
  }

  .invmerge-stock-alert input {
    grid-row: span 2;
    width: 18px;
    height: 18px;
    accent-color: #0d532f;
  }

  .invmerge-checkbox-row {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    color: #334155;
    font-size: 13px;
    font-weight: 850;
    cursor: pointer;
  }

  .invmerge-checkbox-row input {
    width: 16px;
    height: 16px;
    accent-color: #0d532f;
  }

  .invmerge-locked-product {
    display: flex;
    align-items: center;
    gap: 13px;
    padding: 13px;
    border-radius: 16px;
    background: #ffffff;
    border: 1px solid #dbe3ec;
    margin-bottom: 14px;
  }

  .invmerge-locked-product strong {
    display: block;
    color: #0f172a;
    font-size: 15px;
    font-weight: 950;
  }

  .invmerge-locked-product p {
    margin: 3px 0 0;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
  }

  .invmerge-segment-note {
    margin-top: 13px;
    padding: 10px 12px;
    border-radius: 12px;
    color: #0d532f;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    font-size: 13px;
    font-weight: 750;
  }


  .invmerge-expanded-stack.stock-logic {
    display: grid;
    /* A bare 1fr track can't shrink below its content's natural width (the
       same "min-width: auto" default flex items have) - with a wide table
       inside, the whole card just grew past the panel instead of
       overflowing, so the extra columns (like the new Expiry Date one)
       got clipped by an ancestor's overflow:hidden with no way to scroll
       to them. minmax(0, 1fr) lets the track shrink to the panel's actual
       width so .invmerge-table-wrap's own overflow-x:auto can do its job. */
    grid-template-columns: minmax(0, 1fr);
    gap: 14px;
  }

  .invmerge-card-title-between {
    justify-content: space-between;
    gap: 12px;
  }

  .invmerge-card-title-between span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .invmerge-card-title-between em {
    font-style: normal;
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 0 10px;
    border-radius: 999px;
    color: #0d532f;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .invmerge-stock-history-card {
    overflow: hidden;
  }

  .invmerge-stock-history-table th,
  .invmerge-uom-summary-table th {
    background: #f1f5f9;
  }

  .invmerge-stock-history-table tbody tr:nth-child(odd),
  .invmerge-uom-summary-table tbody tr:nth-child(odd) {
    background:
      linear-gradient(90deg, rgba(13, 83, 47, 0.035), rgba(13, 83, 47, 0.010)),
      #ffffff;
  }

  .invmerge-stock-history-table tbody tr:nth-child(even),
  .invmerge-uom-summary-table tbody tr:nth-child(even) {
    background:
      linear-gradient(90deg, rgba(37, 99, 235, 0.030), rgba(37, 99, 235, 0.008)),
      #fbfdff;
  }

  .invmerge-stock-history-table tbody tr:hover,
  .invmerge-stock-history-table tbody tr:focus-within,
  .invmerge-uom-summary-table tbody tr:hover,
  .invmerge-uom-summary-table tbody tr:focus-within {
    background:
      linear-gradient(90deg, rgba(13, 83, 47, 0.095), rgba(13, 83, 47, 0.026)),
      #ffffff;
    box-shadow: inset 3px 0 0 #0d532f;
  }

  .invmerge-stock-history-table td,
  .invmerge-uom-summary-table td {
    vertical-align: top;
  }

  .invmerge-status-chip {
    min-width: 68px;
    height: 26px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 9px;
    font-size: 11px;
    font-weight: 950;
    white-space: nowrap;
  }

  .invmerge-status-chip.success {
    color: #047857;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  .invmerge-status-chip.warning {
    color: #b45309;
    background: #fffbeb;
    border: 1px solid #fde68a;
  }

  .invmerge-status-chip.danger {
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
  }

  .invmerge-expiry-display {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .invmerge-expiry-edit {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .invmerge-expiry-edit-input {
    height: 28px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    padding: 0 6px;
    font-size: 12px;
    max-width: 130px;
  }

  .invmerge-expiry-edit-btn {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    background: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
  }

  .invmerge-expiry-edit-btn:hover {
    background: #f3f4f6;
  }

  .invmerge-expiry-edit-btn.save {
    color: #047857;
    border-color: #a7f3d0;
  }

  .invmerge-expiry-edit-btn.cancel {
    color: #b91c1c;
    border-color: #fecaca;
  }

  .invmerge-expiry-edit-btn.edit {
    color: #6b7280;
    border-color: transparent;
    background: transparent;
    width: 20px;
    height: 20px;
  }

  .invmerge-expiry-edit-btn.edit:hover {
    color: #282e26;
    background: #f3f4f6;
  }

  .invmerge-expiry-edit-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 1200px) {
    .invmerge-expanded-grid {
      grid-template-columns: 1fr;
    }


    .invmerge-toolbar {
      grid-template-columns: 1fr;
    }
    .invmerge-summary-row {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .invmerge-expanded-summary-strip {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .invmerge-row-main {
      grid-template-columns: 1fr;
    }
    .invmerge-row-stats,
    .invmerge-row-actions {
      justify-content: flex-start;
      flex-wrap: wrap;
    }
  }

  @media (max-width: 768px) {
    .inventory-header,
    .invmerge-panel-head,
    .invmerge-footer {
      align-items: flex-start;
      flex-direction: column;
    }
    .invmerge-expanded {
      padding-left: 18px;
    }
    .invmerge-edit-grid,
    .invmerge-stock-row,
    .invmerge-segment-grid {
      grid-template-columns: 1fr;
    }
    .invmerge-gallery-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  @media (max-width: 540px) {
    .invmerge-summary-row {
      grid-template-columns: 1fr;
    }
    .invmerge-row-actions .invmerge-action-btn {
      flex: 1;
      justify-content: center;
    }
  }


  /* ================= Modal + row polish update ================= */

  .invmerge-stock-modal,
  .invmerge-segment-modal {
    background:
      radial-gradient(circle at top left, rgba(13, 83, 47, 0.045), transparent 32%),
      #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 18px;
    padding: 18px !important;
    overflow: visible !important;
  }

  .invmerge-stock-scan {
    min-height: 78px;
    border-radius: 16px;
    background: #ffffff;
    border: 1px solid #dbe3ec;
    display: grid !important;
    grid-template-columns: 42px minmax(0, 1fr);
    align-items: center;
    gap: 12px;
    padding: 13px 16px !important;
    margin-bottom: 18px;
  }

  .invmerge-stock-scan img {
    width: 34px !important;
    height: 34px !important;
    object-fit: contain;
    opacity: 0.86;
  }

  .invmerge-stock-section {
    display: grid;
    gap: 16px !important;
  }

  .invmerge-stock-row {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px !important;
    align-items: start !important;
    margin: 0 !important;
  }

  .invmerge-stock-field,
  .invmerge-stock-control,
  .invmerge-form-group {
    position: relative !important;
    min-width: 0 !important;
  }

  .invmerge-stock-field .floating-label {
    position: absolute !important;
    top: -8px !important;
    left: 12px !important;
    z-index: 2 !important;
    display: inline-flex !important;
    align-items: center !important;
    min-height: 18px !important;
    padding: 0 7px !important;
    margin: 0 !important;
    border-radius: 999px !important;
    background: #f8fafc !important;
    color: #334155 !important;
    font-size: 11px !important;
    line-height: 1 !important;
    font-weight: 900 !important;
    letter-spacing: 0.01em !important;
    transform: none !important;
    pointer-events: none !important;
  }

  .invmerge-stock-field input,
  .invmerge-stock-field select,
  .invmerge-stock-control input,
  .invmerge-segment-modal input,
  .invmerge-segment-modal select,
  .invmerge-segment-modal textarea,
  .invmerge-form-group input,
  .invmerge-form-group select {
    width: 100% !important;
    height: 50px !important;
    min-height: 50px !important;
    border: 1px solid #cbd5e1 !important;
    border-radius: 12px !important;
    background: #ffffff !important;
    color: #0f172a !important;
    font-size: 14px !important;
    font-weight: 750 !important;
    padding: 0 13px !important;
    box-shadow: none !important;
    outline: none !important;
    box-sizing: border-box !important;
  }

  .invmerge-stock-field input:focus,
  .invmerge-stock-field select:focus,
  .invmerge-stock-control input:focus,
  .invmerge-segment-modal input:focus,
  .invmerge-segment-modal select:focus,
  .invmerge-form-group input:focus,
  .invmerge-form-group select:focus {
    border-color: #0d532f !important;
    box-shadow: 0 0 0 4px rgba(13, 83, 47, 0.10) !important;
  }

  .invmerge-stock-field input:disabled,
  .invmerge-stock-field select:disabled {
    background: #f8fafc !important;
    color: #64748b !important;
    cursor: not-allowed !important;
  }

  .invmerge-stock-field .error-text {
    display: block;
    margin-top: 6px;
    color: #dc2626;
    font-size: 11px;
    font-weight: 800;
  }

  .invmerge-stock-control .MuiFormControl-root,
  .invmerge-stock-control .MuiAutocomplete-root,
  .invmerge-segment-modal .MuiFormControl-root,
  .invmerge-segment-modal .MuiAutocomplete-root {
    width: 100% !important;
  }

  .invmerge-stock-control .MuiInputBase-root,
  .invmerge-segment-modal .MuiInputBase-root,
  .invmerge-form-group .MuiInputBase-root {
    height: 50px !important;
    min-height: 50px !important;
    border-radius: 12px !important;
    background: #ffffff !important;
    padding: 0 10px !important;
  }

  .invmerge-stock-control .MuiInputLabel-root,
  .invmerge-segment-modal .MuiInputLabel-root,
  .invmerge-form-group .MuiInputLabel-root {
    color: #334155 !important;
    font-size: 12px !important;
    font-weight: 900 !important;
  }

  .invmerge-segment-modal {
    padding: 20px 22px !important;
  }

  .invmerge-segment-modal .invmerge-form-group {
    display: block !important;
  }

  .invmerge-segment-modal .invmerge-form-group > label,
  .invmerge-segment-modal label {
    display: block !important;
    position: static !important;
    margin: 0 0 7px !important;
    color: #334155 !important;
    background: transparent !important;
    font-size: 12px !important;
    line-height: 1.2 !important;
    font-weight: 900 !important;
    letter-spacing: 0.015em !important;
    transform: none !important;
  }

  .invmerge-locked-product {
    display: grid !important;
    grid-template-columns: 58px minmax(0, 1fr);
    align-items: center !important;
    gap: 13px !important;
    padding: 13px !important;
    border-radius: 16px !important;
    background: #ffffff !important;
    border: 1px solid #dbe3ec !important;
    margin-bottom: 18px !important;
  }

  .invmerge-locked-product .invmerge-product-img {
    width: 58px !important;
    height: 58px !important;
    border-radius: 14px !important;
  }

  .invmerge-locked-product strong {
    color: #0f172a !important;
    font-size: 14px !important;
    font-weight: 950 !important;
    line-height: 1.25 !important;
  }

  .invmerge-locked-product p {
    margin: 4px 0 0 !important;
    color: #64748b !important;
    font-size: 12px !important;
    font-weight: 750 !important;
  }

  .invmerge-segment-modal .invmerge-modal-actions,
  .invmerge-stock-modal .invmerge-modal-actions {
    display: flex !important;
    justify-content: flex-end !important;
    gap: 12px !important;
    margin-top: 18px !important;
    padding-top: 16px !important;
    border-top: 1px solid #e2e8f0 !important;
  }

  .invmerge-segment-modal .invmerge-modal-actions button,
  .invmerge-stock-modal .invmerge-modal-actions button {
    height: 44px !important;
    min-width: 140px !important;
    border-radius: 999px !important;
    font-size: 14px !important;
    font-weight: 950 !important;
  }

  .invmerge-segment-note {
    margin-top: 15px !important;
    padding: 11px 13px !important;
    border-radius: 13px !important;
    color: #0d532f !important;
    background: #ecfdf5 !important;
    border: 1px solid #a7f3d0 !important;
    font-size: 12px !important;
    font-weight: 850 !important;
  }

  .MuiAutocomplete-popper,
  .MuiPopover-root,
  .MuiPopper-root,
  .MuiMenu-root {
    z-index: 2147482500 !important;
  }

  .MuiAutocomplete-paper,
  .MuiPaper-root.MuiMenu-paper {
    border-radius: 13px !important;
    border: 1px solid #dbe3ec !important;
    box-shadow: 0 18px 44px rgba(15, 23, 42, 0.20) !important;
    overflow: hidden !important;
  }

  .invmerge-product-row:nth-child(odd) {
    background:
      linear-gradient(90deg, rgba(13, 83, 47, 0.034) 0%, rgba(13, 83, 47, 0.012) 42%, transparent 100%),
      #ffffff !important;
  }

  .invmerge-product-row:nth-child(even) {
    background:
      linear-gradient(90deg, rgba(37, 99, 235, 0.030) 0%, rgba(37, 99, 235, 0.010) 42%, transparent 100%),
      #fbfdff !important;
  }

  .invmerge-product-row .invmerge-product-main {
    background: transparent !important;
  }

  .invmerge-product-row:hover {
    background:
      linear-gradient(90deg, rgba(13, 83, 47, 0.065) 0%, rgba(13, 83, 47, 0.024) 48%, transparent 100%),
      #ffffff !important;
  }

  .invmerge-product-row.expanded {
    background:
      linear-gradient(90deg, rgba(13, 83, 47, 0.078) 0%, rgba(13, 83, 47, 0.030) 42%, transparent 100%),
      #f8fafc !important;
  }

  .invmerge-product-row + .invmerge-product-row {
    border-top: 1px solid #eef2f7 !important;
  }

  .invmerge-product-main:hover {
    box-shadow: inset 3px 0 0 #0d532f !important;
  }

  .invmerge-expanded {
    background:
      linear-gradient(180deg, rgba(248, 250, 252, 0.96) 0%, #ffffff 100%) !important;
  }

  @media (max-width: 768px) {
    .invmerge-stock-row {
      grid-template-columns: 1fr !important;
      gap: 16px !important;
    }

    .invmerge-stock-scan {
      grid-template-columns: 1fr !important;
    }

    .invmerge-segment-modal .invmerge-modal-actions,
    .invmerge-stock-modal .invmerge-modal-actions {
      flex-direction: column-reverse !important;
    }

    .invmerge-segment-modal .invmerge-modal-actions button,
    .invmerge-stock-modal .invmerge-modal-actions button {
      width: 100% !important;
    }
  }


  /* Functional fixes: product select, barcode visibility, sticky combobox dropdowns */
  .invmerge-stock-product-row {
    display: block;
    width: 100%;
    margin-bottom: 16px;
  }

  .invmerge-full-product-select,
  .invmerge-full-product-select .MuiFormControl-root,
  .invmerge-full-product-select .MuiAutocomplete-root {
    width: 100% !important;
  }

  .invmerge-product-search-input .MuiInputBase-root,
  .invmerge-full-product-select .MuiInputBase-root {
    min-height: 54px !important;
    border-radius: 12px !important;
  }

  .invmerge-product-option {
    min-height: 54px !important;
    display: flex !important;
    align-items: center !important;
    white-space: normal !important;
  }

  .invmerge-product-option strong {
    display: block;
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
    line-height: 1.25;
  }

  .invmerge-product-option span {
    display: block;
    margin-top: 3px;
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
  }

  .invmerge-stock-locked-product {
    margin-bottom: 0 !important;
  }

  .invmerge-stock-helper-box input {
    color: #64748b !important;
    font-weight: 850 !important;
  }

  .invmerge-segment-modal,
  .invmerge-stock-modal,
  .invmerge-form-group,
  .invmerge-stock-control,
  .invmerge-stock-field {
    overflow: visible !important;
  }

  .invmerge-segment-modal .paginated-search-select,
  .invmerge-stock-modal .paginated-search-select,
  .invmerge-segment-modal [class*="select"],
  .invmerge-stock-modal [class*="select"] {
    position: relative;
  }

  .MuiAutocomplete-popper,
  .MuiPopper-root,
  .MuiPopover-root,
  .MuiMenu-root {
    z-index: 2147483500 !important;
  }

  .MuiAutocomplete-popper {
    margin-top: 4px !important;
  }

  .MuiAutocomplete-paper,
  .MuiPaper-root.MuiMenu-paper {
    border-radius: 12px !important;
    border: 1px solid #dbe3ec !important;
    box-shadow: 0 18px 44px rgba(15, 23, 42, 0.20) !important;
    overflow: hidden !important;
  }

  @media (max-width: 768px) {
    .invmerge-stock-product-row {
      margin-bottom: 14px;
    }
  }


  /* Exact old API payload and anchored combo fixes */
  .invmerge-segment-modal,
  .invmerge-stock-modal {
    overflow: visible !important;
  }

  .invmerge-segment-grid {
    overflow: visible !important;
    align-items: start !important;
  }

  .invmerge-full-product-select {
    width: 100% !important;
    grid-column: 1 / -1 !important;
    margin-bottom: 16px !important;
  }

  .invmerge-full-product-select .MuiAutocomplete-root,
  .invmerge-form-group .MuiAutocomplete-root,
  .invmerge-stock-control .MuiAutocomplete-root {
    width: 100% !important;
  }

  .invmerge-form-group .MuiInputBase-root,
  .invmerge-stock-control .MuiInputBase-root,
  .invmerge-full-product-select .MuiInputBase-root {
    min-height: 50px !important;
    border-radius: 12px !important;
    background: #ffffff !important;
  }

  .invmerge-form-group .MuiInputLabel-root,
  .invmerge-stock-control .MuiInputLabel-root,
  .invmerge-full-product-select .MuiInputLabel-root {
    color: #334155 !important;
    font-size: 13px !important;
    font-weight: 850 !important;
  }

  .invmerge-product-option {
    display: flex !important;
    align-items: flex-start !important;
    padding: 10px 12px !important;
  }

  .invmerge-product-option strong {
    display: block !important;
    color: #0f172a !important;
    font-size: 13px !important;
    font-weight: 900 !important;
  }

  .invmerge-product-option span {
    display: block !important;
    margin-top: 3px !important;
    color: #64748b !important;
    font-size: 11px !important;
    font-weight: 750 !important;
  }

  .MuiAutocomplete-popper,
  .MuiPopper-root,
  .MuiPopover-root,
  .MuiMenu-root {
    z-index: 2147482500 !important;
  }

  .MuiAutocomplete-paper,
  .MuiPaper-root.MuiMenu-paper {
    border-radius: 13px !important;
    border: 1px solid #dbe3ec !important;
    box-shadow: 0 18px 44px rgba(15, 23, 42, 0.20) !important;
    overflow: hidden !important;
  }


  /* Stock lot optimisation: use productInventory rows as source of truth */
  .invmerge-row:nth-child(odd) {
    background:
      linear-gradient(90deg, rgba(13, 83, 47, 0.032) 0%, rgba(13, 83, 47, 0.012) 44%, transparent 100%),
      #ffffff;
  }

  .invmerge-row:nth-child(even) {
    background:
      linear-gradient(90deg, rgba(37, 99, 235, 0.030) 0%, rgba(37, 99, 235, 0.010) 44%, transparent 100%),
      #fbfdff;
  }

  .invmerge-row .invmerge-row-main {
    background: transparent;
  }

  .invmerge-row:hover,
  .invmerge-row:focus-within {
    background:
      linear-gradient(90deg, rgba(13, 83, 47, 0.070) 0%, rgba(13, 83, 47, 0.026) 44%, transparent 100%),
      #ffffff;
  }

  .invmerge-row.expanded {
    background:
      linear-gradient(90deg, rgba(13, 83, 47, 0.082) 0%, rgba(13, 83, 47, 0.032) 44%, transparent 100%),
      #f8fafc;
  }

  .invmerge-row:hover .invmerge-row-main,
  .invmerge-row:focus-within .invmerge-row-main {
    box-shadow: inset 4px 0 0 #0d532f;
  }

  .invmerge-expanded-summary-strip {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 12px;
  }

  .invmerge-expanded-summary-strip span {
    border: 1px solid #dbe3ec;
    border-radius: 14px;
    background: #ffffff;
    padding: 10px 12px;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.045);
  }

  .invmerge-expanded-summary-strip small {
    display: block;
    color: #64748b;
    font-size: 9px;
    font-weight: 950;
    letter-spacing: 0.045em;
    text-transform: uppercase;
  }

  .invmerge-expanded-summary-strip strong {
    display: block;
    margin-top: 4px;
    color: #0f172a;
    font-size: 13px;
    font-weight: 950;
  }

  .invmerge-expanded-grid.stock-logic {
    grid-template-columns: minmax(0, 1.15fr) minmax(420px, 0.85fr);
  }

  .invmerge-uom-summary-table tbody tr:nth-child(odd),
  .invmerge-table tbody tr:nth-child(odd) {
    background: rgba(248, 250, 252, 0.82);
  }

  .invmerge-uom-summary-table tbody tr:nth-child(even),
  .invmerge-table tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.96);
  }

  .invmerge-uom-summary-table tbody tr:hover,
  .invmerge-table tbody tr:hover {
    background: rgba(236, 253, 245, 0.82);
  }

  .invmerge-table-subtext {
    display: block;
    margin-top: 3px;
    color: #64748b;
    font-size: 10px;
    font-weight: 750;
  }

  .invmerge-stock-lot-list {
    display: grid;
    gap: 10px;
    max-height: 420px;
    overflow-y: auto;
    padding-right: 4px;
  }

  .invmerge-stock-lot-card {
    display: grid;
    gap: 10px;
    padding: 12px;
    border: 1px solid #e8eef5;
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(248, 250, 252, 0.98), #ffffff);
  }

  .invmerge-stock-lot-card:nth-child(even) {
    background: linear-gradient(135deg, rgba(239, 246, 255, 0.72), #ffffff);
  }

  .invmerge-stock-lot-card:hover,
  .invmerge-stock-lot-card:focus-within {
    border-color: #a7f3d0;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
  }

  .invmerge-stock-lot-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .invmerge-stock-lot-head strong {
    display: block;
    color: #0f172a;
    font-size: 12px;
    font-weight: 950;
  }

  .invmerge-stock-lot-head span {
    display: block;
    margin-top: 4px;
    color: #64748b;
    font-size: 11px;
    font-weight: 750;
    line-height: 1.35;
  }

  .invmerge-stock-lot-head b {
    color: #047857;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: 999px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 950;
    white-space: nowrap;
  }

  .invmerge-stock-lot-meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 7px;
  }

  .invmerge-stock-lot-equation {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
    padding: 8px 10px;
    border-radius: 12px;
    color: #0d532f;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    font-size: 11px;
    font-weight: 850;
  }

  .invmerge-stock-lot-equation span {
    color: #047857;
    font-weight: 950;
  }

  .invmerge-stock-lot-equation strong {
    color: #0f172a;
    font-weight: 950;
    text-align: right;
  }

  @media (max-width: 1250px) {
    .invmerge-expanded-grid.stock-logic {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 760px) {
    .invmerge-expanded-summary-strip,
    .invmerge-stock-lot-meta {
      grid-template-columns: 1fr;
    }
  }


  /* Final stock history table polish: compact, information-rich and readable */
  .invmerge-stock-lot-table-wrap {
    max-height: 460px;
    overflow: auto;
  }

  .invmerge-stock-history-table th,
  .invmerge-uom-summary-table th {
    white-space: nowrap;
  }

  .invmerge-stock-history-table td,
  .invmerge-uom-summary-table td {
    vertical-align: top;
  }

  .invmerge-stock-history-table tbody tr:nth-child(odd),
  .invmerge-uom-summary-table tbody tr:nth-child(odd) {
    background: linear-gradient(90deg, rgba(13, 83, 47, 0.035), rgba(255, 255, 255, 0.96));
  }

  .invmerge-stock-history-table tbody tr:nth-child(even),
  .invmerge-uom-summary-table tbody tr:nth-child(even) {
    background: linear-gradient(90deg, rgba(37, 99, 235, 0.032), rgba(255, 255, 255, 0.98));
  }

  .invmerge-stock-history-table tbody tr:hover,
  .invmerge-uom-summary-table tbody tr:hover,
  .invmerge-stock-history-table tbody tr:focus-within,
  .invmerge-uom-summary-table tbody tr:focus-within {
    background: linear-gradient(90deg, rgba(13, 83, 47, 0.095), rgba(236, 253, 245, 0.85)) !important;
    box-shadow: inset 3px 0 0 #0d532f;
  }

  .invmerge-stock-history-table .invmerge-table-subtext {
    line-height: 1.35;
  }
`;

export default Product;
